import MaturixStrengthUnit from '../../enums/MaturixStrengthUnit'
import MaturixTemperatureUnit from '../../enums/MaturixTemperatureUnit'
import { TagType } from '../../enums/TagType'
import { getBandTitleByTagType } from '../../utils/TagHelper'
import { brandName, brandShortName } from '../brand'
import LanguageTreeType from '../LanguageTreeType'
import countries from './countries'
import timezones from './timezones'

// узгодження числівника з іменником йопт
const ending = (x: number, for1: string, for234: string, forRest: string) => {
  const xMod100 = x % 100
  if (5 <= xMod100 && xMod100 <= 20) {
    return forRest
  }

  const xMod10 = x % 10
  if (xMod10 === 1) {
    return for1
  }

  if (2 <= xMod10 && xMod10 <= 4) {
    return for234
  }

  return forRest
}

// Деякі слова мають різну форму у називному і знахідному відмінках
const deliveriesNominative = (x: number) =>
  `Достав${ending(x, 'ка', 'ки', 'ок')}`
const deliveriesAccusative = (x: number) =>
  `Достав${ending(x, 'ку', 'ки', 'ок')}`
const plans = (x: number) => `План${ending(x, '', 'и', 'ів')}`
const users = (x: number) => `Член${ending(x, 'а', 'ів', 'ів')}`
const activitiesAccusative = (x: number) =>
  `Роб${ending(x, 'оту', 'оти', 'іт')}`
const workflowsAccusative = (x: number) =>
  `робочий процес${ending(x, '', 'и', 'ів')}`
const sensors = (x: number) => `Сенсор${ending(x, '', 'а', 'ів')}`
const struxTagsCount = (x: number) => `StruxTag${ending(x, '', 'и', 'ів')}`
const workflows = (x: number) =>
  `Робоч${ending(x, 'ий', 'і', 'их')} процес${ending(x, '', 'и', 'ів')}`
const equipment = (x: number) => `одиниц${ending(x, 'я', 'і', 'ь')} обладнання`
const materials = (x: number) => `матеріал${ending(x, '', 'и', 'ів')}`

const xHours = (x: number) => `${x} Годи${ending(x, 'на', 'ни', 'н')}`
const xUsers = (x: number) => `${x} користувач${ending(x, '', 'і', 'ів')}`
const xFiles = (x: number) => `${x} файл${ending(x, '', 'а', 'ів')}`
const xDates = (x: number) => `${x} Дат${ending(x, 'а', 'и', '')}`
const xForms = (x: number) => `${x} форм${ending(x, 'у', 'и', '')}`
const xFields = (x: number) => `${x} пол${ending(x, 'е', 'я', 'ів')}`
const xFieldsAdded = (x: number) =>
  `Додано ${x} пол${ending(x, 'е', 'я', 'ів')}`
const xProjects = (x: number) => `${x} проєкт${ending(x, '', 'и', 'ів')}`
const xProperties = (x: number) => `${x} Властивіст${ending(x, 'ь', 'і', 'ей')}`

const easterEggMode = true

const UkrainianTree: LanguageTreeType = {
  _brandName: brandName,
  _brandShortName: brandShortName,

  label: 'Мітка',
  avatar: 'Аватарка',
  ok: 'Добре',
  alert: 'Сповіщення',
  warning: 'Попередження',
  cancel: 'Скасувати',
  bulkAddOptions: 'Масове додавання опцій',
  dropdownOptions: 'Опції списку',
  optionLabel: 'Назва опції',
  selectAllOptions: 'Вибрати всі опції',
  hierarchyOptionsIntro:
    'Створіть список опцій, які можуть вибирати користувачі. Створюйте ієрархічні списки, вкладаючи опції під батьківські.',
  cantChangeNestingChecking:
    'Поки не можна змінити вкладеність — триває перевірка, які опції використовуються записами. Спробуйте ще раз за мить.',
  optionUsedCannotNest:
    'Ця опція використовується наявними записами й не може стати батьківською. Лише опції без записів можуть мати вкладені опції.',
  cantRestoreExceedsDepth:
    'Не вдається відновити — вкладення цих опцій разом перевищить максимальну глибину.',
  restoredDeletedOptions: (labels: string, count: number) =>
    `Відновлено раніше видален${
      count === 1 ? 'у опцію' : 'і опції'
    }: ${labels}`,
  savesAs: 'Зберігається як',
  alreadyUsedBy: (label: string) => `Уже використовується «${label}»`,
  useValue: (value: string) => `Використати ${value}`,
  matchesDeletedOptionWillRestore: (label: string) =>
    `Збігається з видаленою опцією «${label}» — її буде відновлено`,
  restoreAndApply: 'Відновити та застосувати',
  collapseOption: 'Згорнути опцію',
  expandOption: 'Розгорнути опцію',
  pickedUpOption: (label: string) => `Опцію ${label} взято.`,
  optionIsOver: (active: string, over: string) =>
    `Опція ${active} над ${over}.`,
  optionDroppedOver: (active: string, over: string) =>
    `Опцію ${active} перенесено на ${over}.`,
  optionDropped: (label: string) => `Опцію ${label} відпущено.`,
  reorderingOptionCancelled: (label: string) =>
    `Перевпорядкування опції ${label} скасовано.`,
  optionsSelected: (count: number) =>
    `Вибрано ${count} опці${count === 1 ? 'ю' : 'й'}`,
  indent: 'Збільшити відступ',
  outdent: 'Зменшити відступ',
  reorderOption: (label: string) => `Перевпорядкувати ${label}`,
  selectOption: (label: string) => `Вибрати ${label}`,
  bulkAddOptionEnterEachLine: 'Введіть кожну опцію в окремому рядку.',
  bulkAddOptionIndentToNest:
    'Додайте відступ до опції, щоб вкласти її під батьківську.',
  bulkAddOptionCopyFromExcel:
    'Копіюйте та вставляйте цілі набори даних з Excel',
  seeTips: 'переглянути поради',
  addOptions: 'Додати опції',
  copyingAndPastingFromExcel: 'Копіювання та вставлення даних з Excel',
  bulkImportGuideSubtitle:
    'Три способи форматування опцій під час вставлення з електронної таблиці.',
  flatDatasets: 'Плоскі набори даних',
  flatDatasetsHint:
    'Розмістіть кожну опцію в окремому рядку перед копіюванням і вставленням.',
  hierarchicalDatasetMethod1: 'Ієрархічний набір даних (Метод 1)',
  hierarchicalDatasetMethod1Hint:
    'Розмістіть кожен рівень в окремому стовпці, а кожну опцію — в окремому рядку перед копіюванням.',
  hierarchicalDatasetMethod2: 'Ієрархічний набір даних (Метод 2)',
  hierarchicalDatasetMethod2Hint:
    'Як у Методі 1, але батьківські елементи можна за бажанням дублювати в кожному рядку.',
  back: 'Назад',
  addOptionBelow: (label: string) => `Додати опцію нижче ${label}`,
  optionCount: (count: number) => {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return `${count} опція`
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
      return `${count} опції`
    return `${count} опцій`
  },
  optionMatchCount: (matched: number, total: number) => `${matched} з ${total}`,
  tipXOfY: (current: number, total: number) => `Порада ${current} з ${total}`,
  placeHere: 'Розмістити тут',
  failedToSavePlacement: 'Не вдалося зберегти розташування. Спробуйте ще раз.',
  placedAtMapCenterDragToAdjust:
    'Розміщено в центрі карти — перетягніть, щоб скоригувати',
  notPlacedOnMap: 'Не розміщено на карті',
  placeOnMap: 'Розмістити на карті',
  placedOnLocationDragToAdjust: (name: string) =>
    `Розміщено на ${name} — перетягніть, щоб скоригувати`,
  placingMultipleItems: (count: number) =>
    `Розміщення ${count} об'єктів — перетягніть кожен і підтвердіть`,
  addedToPlacementQueue: (remaining: number) =>
    `Додано до черги розміщення — залишилось ${remaining}`,
  open: 'Відкрити',
  openWorkflowForm: 'Відкрити робочий процес',
  minimize: 'Згорнути',
  opened: 'Відкрито',
  close: 'Закрити',
  closed: 'Закритий',
  contacts: 'Контакти',
  loading: 'Завантаження',
  language: 'Мова',
  notSpecified: 'Не вказано',
  default: 'За замовчуванням',
  done: 'Готово',
  save: 'Зберегти',
  apply: 'Застосувати',
  edit_verb: 'Редагувати',
  merge_verb: "Об'єднати",
  link_verb: "Прив'язати",
  re_link_verb: `Переприв'язати`,
  unlink_verb: `Від’єднати`,
  unlinking: `Від’єднання`,
  linkToX: (x: string) => `Прив’язати до ${x}`,
  linkedToX: (x: string) => `Прив’язано до ${x}`,
  savingEllipsis: `Збереження...`,
  details: `Деталі`,
  delete: 'Видалити',
  rename: 'Перейменувати',
  reorder: 'Перевпорядкувати',
  renameMapView: 'Перейменувати карту',
  mapViewName: 'Назва карти',
  deletedField: 'Видалене поле',
  duplicate: 'дублікат',
  duplicatedName: (name: string) => `${name} - Копія`,
  initials: 'Ініціали',
  invite: 'Запросити',
  search: 'Шукати',
  selectAll_people: 'Вибрати всіх',
  selectAll_items: 'Вибрати все',
  selectNone_people: 'Не вибрати нікого',
  selectNone_items: 'Не вибрати нічого',
  clearAll_items: 'Очистити все',
  clear: 'Очистити',
  uploading: 'Завантаження',
  noResults: 'Жодних результатів',
  add: 'Додати',
  update_verb: 'Оновити',
  error: easterEggMode ? 'Халепа' : 'Помилка',
  goBack: 'Повернутися',
  confirm: 'Підтвердити',
  select: 'Вибрати',
  selectAll: 'Вибрати все',
  selected: 'Вибрані',
  start_verb: 'Розпочати',
  finish_verb: 'Завершити',
  followers: 'Підписники',
  view_verb: 'Переглянути',
  download: 'Завантажити з сайту',
  export: 'Експортувати',
  downloadingStatus: {
    starting: 'Запуск',
    fetching: 'Отримання',
    compiling: 'Компіляція',
    generating: 'Генерація',
    downloading: 'Завантажит',
  },
  generatingLink: 'Генерація посилання...',
  searchCompany: 'Шукати компанію',
  thereIsNoCompanies: 'Немає компаній',
  addCompany: 'Додати компанію',
  locationClosure: 'Закриття локації',
  ifDeliveryHasTheFollowingTags: 'Якщо доставка має такі теги',
  ifEntityHasTheFollowingTags: (entityName: string) =>
    `Якщо ${entityName} має такі теги`,
  thenDoAction: 'Тоді виконувати дію',
  toUserTeamRoleOrCompany: 'Для користувача, команди, посади або компанії',
  addRule: 'Додати правило',
  shouldDeleteRule: 'Потрібно видалити правило',
  tag: 'Тег',
  searchForOrSelectTag: 'Шукайте або виберіть тег',
  addTag: 'Додати тег',
  addUserTeamCompany: 'Додати користувача, команду, компанію',
  tagCreation: 'Створення тега',
  deliveryStatus: 'Статус доставки',
  sendNotification: 'Надсилати сповіщення',
  toExternalCalendar: 'До зовнішніх календарів',
  notificationRules: 'Правила сповіщення',
  mention: 'Згадати',
  gotToDirectory: 'Перейти до каталогу',
  goToProjectHome: 'Перейти на головну сторінку проєкту',

  daily: 'Щодня',
  monSat: 'Пон-Суб',
  d_daysShort: 'д',
  xSecondsAgoShort: (x: number) => `${x}с тому`,
  xMinutesAgoShort: (x: number) => `${x} хв тому`,
  xHoursAgoShort: (x: number) => `${x} год тому`,
  xDaysAgoShort: (x: number) => `${x} д тому`,
  yesterday: 'Учора',
  today: 'Сьогодні',
  tomorrow: 'Завтра',

  loggingIn: 'Вхід у систему',
  loggingOut: 'Вихід із системи',
  loginFailed: 'Помилка входу',
  backToLogin: 'Повернутися до входу',
  processingLogin: 'Обробка входу...',
  login: 'Увійти',
  logo: 'Логотип',
  procoreLogin: 'Увійти з Procore',
  loginWithMicrosoft: 'Увійти з Microsoft',
  loginWithGoogle: 'Увійти з Google',
  tryAgain: 'Спробувати знову',
  contactAdminForAccess:
    'Якщо ви вважаєте, що повинні мати доступ, зверніться до свого адміністратора.',
  ssoSessionExpired:
    'Ваша сесія входу закінчилася. Будь ласка, спробуйте ще раз.',
  ssoProviderError:
    'Не вдалося завершити автентифікацію з провайдером. Будь ласка, спробуйте ще раз.',
  ssoInvalidUser:
    'Не вдалося отримати інформацію про ваш обліковий запис. Будь ласка, спробуйте ще раз.',
  ssoUserNotFound: 'Цей обліковий запис не додано до StruxHub.',
  ssoCodeExpired:
    'Ваше посилання для входу закінчилося. Будь ласка, увійдіть знову.',
  ssoInternalError:
    'Сталася неочікувана помилка. Будь ласка, спробуйте пізніше.',
  ssoProviderNotAllowed: 'Цей метод входу недоступний для вашої організації.',
  ssoUseAllowedMethod:
    'Ваша організація вимагає один з наступних методів входу:',
  ssoPersonalAccountsNotAllowed:
    'Особисті облікові записи не можна використовувати для входу. Скористайтеся робочим обліковим записом.',
  ssoInitiationFailed:
    'Не вдалося розпочати вхід через цього постачальника. Спробуйте ще раз або скористайтеся іншим способом входу.',
  ssoDomainRequiresMethods: (domain: string) =>
    `Користувачі з @${domain} мають використовувати один з наступних методів входу:`,
  passwordLoginNotAvailableForDomain: (providers: string) =>
    `Вхід за електронною поштою та паролем недоступний для вашого домену. Скористайтеся: ${providers}`,
  emailAndPassword: 'Електронна пошта та пароль',
  noColumnsDefined: 'Жодних колонок не визначено',
  changePassword: 'Змінити пароль',
  setPassword: 'Встановити пароль',
  passwordWillBeUsedForAllSites:
    'Цей пароль буде використовуватися для всіх сайтів StruxHub',
  setPasswordToSignBackIn:
    'Ваше посилання-запрошення тимчасове. Пароль дозволяє увійти знову. Ви також можете встановити його пізніше у своєму профілі.',
  forgotPassword: 'Забули пароль?',
  reset: 'Скинути',
  resetPassword: 'Скинути пароль',
  logOut: 'Вийти',

  hideAll: 'Приховати всі',
  hideGroup: 'Приховати групу',
  hideObject: 'Приховати об’єкт',

  showAll: 'Показати всі',
  showGroup: 'Показати групу',
  showObject: 'Показати об’єкт',

  contactHelpAtMail: (email: string) => 'Звернутися по допомогу ' + email,
  contactHelp: 'Звернення по допомогу',

  userNotFound: 'Користувача не знайдено',
  noUserMatchingYourSearch: 'Жоден користувач не відповідає вашому пошуку',
  allUsersAlreadyAssigned: 'Всі користувачі вже призначені',
  noTagMatchingYourSearch: 'Жоден тег не відповідає вашому пошуку',

  enterYourEmailBelowAndStruxHubWillSendAResetLink: `Уведіть Вашу електронну адресу нижче, і ${brandName} надішле Вам посилання, за яким Ви зможете скинути пароль`,
  pleaseEnterYourPasswordForTheExistingAccountToLinkDifferentProviders:
    'Будь ласка, введіть свій пароль для наявного облікового запису, щоб зв’язати різних провайдерів:',
  pleaseOpenTheLinkFromTheLetterAgain:
    'Будь ласка відкрийте посилання з листа знову',
  passwordResetLinkWillBeSentIfAccountExists:
    'Якщо обліковий запис із цією електронною адресою існує, ви отримаєте посилання для скидання пароля',
  checkYourEmail: 'Перевірте свою пошту',
  passwordResetLinkSentIfAccountExists: (email: string) =>
    `Якщо обліковий запис для ${email} існує, ми надіслали посилання для скидання пароля. Це може зайняти кілька хвилин.`,
  didNotGetItTryAnotherEmail:
    'Не отримали? Спробуйте іншу адресу електронної пошти',
  passwordResetLinkInvalid:
    'Це посилання для скидання пароля недійсне або його термін дії минув. Запросіть нове.',
  invalidEmailError: 'Електронна адреса недійсна.',
  userNotFoundError:
    'Користувача з такою електроною адресой не знайдено. Можливо, користувач видалений.',
  enterANewPasswordBelow: 'Уведіть новий пароль нижче',
  setYourPassword: 'Задайте Ваш пароль',
  tooManyUnsuccessfulLoginAttempts: 'Забагато невдалих спроб входу.',
  incorrectEmailOrPassword:
    'Неправильна електронна пошта або пароль. Скористайтеся «Забули пароль?», щоб скинути його.',
  passwordErrors: {
    weakPassword:
      'Пароль повинен містити щонайменше 8 символів, включаючи ' +
      'латинські малі та великі літери, цифри та принаймі 1 спеціальний символ !,@#$%&*?_~-',
    doNotMatch: 'Паролі не співпадають',
    empty: 'Пароль не може бути порожнім',
    invalid: 'Пароль є недійсним або користувач не має паролю',
  },
  authFieldErrors: {
    enterYourEmail: 'Уведіть адресу',
    enterValidEmail: 'Уведіть дійсну адресу',
    enterYourPassword: 'Уведіть пароль',
    enterNewPassword: 'Уведіть новий пароль',
    enterNewPasswordAgain: 'Повторіть пароль',
    enterYourPhoneNumber: 'Уведіть номер телефону',
    enterValidPhoneNumber: 'Уведіть дійсний номер',
    enterYourFirstName: 'Уведіть ім’я',
    enterYourLastName: 'Уведіть прізвище',
    enterAtLeastXCharacters: (x: number) => `Уведіть від ${x} символів`,
  },

  chat: 'Чат',
  activities: 'Роботи',
  deliveries: 'Доставки',
  closures: 'Закриття',
  noActiveClosures: 'Немає активних закриттів',
  activeClosuresHeading: 'Активні закриття:',
  logistics: "Логістичний об'єкт",
  logisticObject: 'Логістика',
  site: 'Сайт',
  siteNews: 'Новини будмайданчика',
  home: 'Домашня сторінка',
  hub: 'Хаб',
  siteLogistics: 'Логістика сайту',
  materials: 'Матеріали',
  materialsLegacy: 'Матеріали (застаріле)',
  photos: 'Фото',
  analytics: 'Аналітика',
  analyticsSetting: 'Налаштування аналітика',
  showInReports: 'Показувати у репортi',
  dashboards: 'Приладові панелі',
  widgetDescriptions: {
    workflowsCount:
      'Shows the total number of active workflows in the selected time period. This includes all workflows regardless of their status.',
    companiesCount:
      'Displays the count of unique companies involved in workflows during the selected period. Each company is counted once regardless of how many workflows they participate in.',
    usersCount:
      'Shows the number of unique users associated with workflows in the selected time range.',
    averageDuration:
      'Calculates the average number of days from workflow start date to completion date. Only includes workflows that have both start and end dates.',
    advancedPlanning:
      'Measures the average number of days between when a workflow was requested and when it actually started. Higher values indicate better advance planning.',
    equipmentBookingsWorkflows:
      'Counts how many workflows have equipment bookings attached to them during the selected period.',
    equipmentBookingsHours:
      'Shows the total hours of equipment bookings across all workflows in the selected time range.',
    locationsWorkflows:
      'Shows the distribution of workflows across different locations. Helps identify which locations have the most activity.',
    locationsByType:
      'Displays the breakdown of workflows by location type. Shows the percentage distribution across different workflow types.',
    workflowsByStatus:
      'Visualizes the distribution of workflows across different statuses (Requested, Submitted, Active, Changed, Ended). Helps understand the workflow pipeline.',
    activeVsCompleted:
      'Compares in-progress workflows (Requested, Submitted, Changed, Accepted, Reviewed, Active, OnSite, ToInspect) versus closed workflows (Done, Ended, Passed, Failed, Denied) in the selected period. Draft and deleted workflows are excluded.',
    workflowsByCategory:
      'Shows how workflows are distributed across different categories. Helps identify which workflow categories are most common.',
    workflowsTimeline:
      'Shows the number of workflows started over time. Useful for identifying trends and patterns in workflow creation.',
    recentWorkflows:
      'Table listing the most recent workflows with their status and dates. Provides quick access to the latest workflow activity.',
  },
  dashboard: {
    settings: 'Налаштування панелі',
    settingsDescription:
      'Увімкніть або вимкніть віджети. Віджети організовані за категоріями для кращого керування панеллю.',
    resetLayout: 'Скинути макет',
    widgetInfo: 'Інформація про віджет',
    backToWidget: 'Повернутися до віджета',
    downloadChart: 'Завантажити графік',
    downloadAsPng: 'Завантажити як PNG',
    downloadAsJpeg: 'Завантажити як JPEG',
    noChange: 'Без змін',
    chartType: 'Тип діаграми',
    chartTypes: {
      pie: 'Кругова діаграма',
      bar: 'Стовпчикова діаграма',
      donut: 'Кільцева діаграма',
      line: 'Лінійна діаграма',
    },
    chartTypeNotSupported: 'Тип графіка не підтримується',
    noWorkflowsFound: 'Робочі процеси не знайдено за цей період',
    workflowsCreated: 'Створено робочих процесів',
    retry: 'Повторити',
    refresh: 'Оновити',
    lastUpdated: 'Останнє оновлення',
    justNow: 'щойно',
    minutesAgo: (m: number) => `${m}хв тому`,
    hoursAgo: (h: number) => `${h}год тому`,
    widgetTitles: {
      totalWorkflows: 'ВСЬОГО РОБОЧИХ ПРОЦЕСІВ',
      companies: 'КОМПАНІЇ',
      usersEditors: 'КОРИСТУВАЧІ',
      averageDuration: 'СЕРЕДНЯ ТРИВАЛІСТЬ',
      advancedPlanning: 'ПОПЕРЕДНЄ ПЛАНУВАННЯ',
      workflowsTimeline: 'ХРОНОЛОГІЯ РОБОЧИХ ПРОЦЕСІВ',
      workflowsByStatus: 'РОБОЧІ ПРОЦЕСИ ЗА СТАТУСОМ',
      activeVsCompleted: 'АКТИВНІ VS ЗАВЕРШЕНІ',
      workflowsByCategory: 'РОБОЧІ ПРОЦЕСИ ЗА КАТЕГОРІЄЮ',
      recentWorkflows: 'ОСТАННІ РОБОЧІ ПРОЦЕСИ',
      locationsWorkflows: 'ЛОКАЦІЇ (РОБОЧІ ПРОЦЕСИ)',
      locationsByType: 'ЛОКАЦІЇ (ЗА ТИПОМ)',
      equipmentBookings: 'БРОНЮВАННЯ ОБЛАДНАННЯ',
      equipmentHours: 'ГОДИНИ ОБЛАДНАННЯ',
    },
    widgetLabels: {
      activeWorkflowsInPeriod: 'Активні робочі процеси за вибраний період',
      uniqueCompaniesInvolved: 'Унікальні залучені компанії',
      uniqueUsersEditing: 'Унікальні користувачі в робочих процесах',
      daysFromStartToCompletion: 'Днів від початку до завершення',
      timeRequestedBeforeStart: 'Час запиту до дати початку',
      workflowsWithEquipment: 'Робочі процеси з обладнанням',
      totalEquipmentBookingHours:
        'Загальна кількість годин бронювання обладнання',
    },
    sections: {
      keyMetrics: {
        title: 'Ключові метрики',
        description: 'Основні показники ефективності',
      },
      planningEfficiency: {
        title: 'Ефективність планування',
        description: 'Метрики планування роботи',
      },
      workflowStatus: {
        title: 'Статус робочих процесів',
        description: 'Поточний стан та прогрес',
      },
      workflowAnalytics: {
        title: 'Аналітика робочих процесів',
        description: 'Розподіл та тенденції',
      },
      recentActivity: {
        title: 'Остання активність',
        description: 'Останні оновлення робочих процесів',
      },
      locationAnalytics: {
        title: 'Аналітика локацій',
        description: 'Де виконується робота',
      },
      equipmentResources: {
        title: 'Обладнання та ресурси',
        description: 'Використання ресурсів',
      },
    },
  },
  reports: 'Звіти',
  filedToGenerateReport: 'Не вдалося створити звіт',
  objectProperties: "Властивості об'єктів",
  formProperties: 'Властивості форми',
  createNewForm: 'Створити нову форму',
  formNameLabel: 'Назва форми',
  untitled: 'Без назви',
  newFormButton: 'Нова форма',
  selectEquipmentForm: 'Виберіть форму обладнання',
  selectMaterialForm: 'Виберіть форму матеріалу',
  selectLocationForm: 'Виберіть форму локації',
  noFormsAvailable: 'Немає доступних форм',
  askAdminToCreateForm:
    'Попросіть адміністратора створити форму перед додаванням записів.',
  propertiesPlural: 'властивості',
  createNewEquipment: 'Створити нове обладнання',
  createNewMaterial: 'Створити новий матеріал',
  createNewLocation: 'Створити нове місце',
  createNewStruxTag: 'Створити новий StruxTag',
  equipmentForm: 'Форма обладнання',
  materialForm: 'Форма матеріалу',
  equipmentCreationForm: 'Форма створення обладнання',
  materialCreationForm: 'Форма створення матеріалу',
  anyForm: 'Будь-яка форма',
  equipmentPropertiesLabel: 'Властивості обладнання',
  materialPropertiesLabel: 'Властивості матеріалу',
  notifications: 'Сповіщення',
  userSettings: 'Налаштування користувача',
  projectSettings: 'Налаштування проєкта',
  projectSettingsOnDesktop: 'Налаштування проєкта у десктопній версії',
  adminSettings: 'Адміністрування Налаштування',
  settings: 'Налаштування',
  pinSettingsSidebar: 'Закріпити бічну панель на всіх сторінках',
  unpinSettingsSidebar: 'Відкріпити бічну панель',
  messages: 'Повідомлення',
  themeDark: 'Темний режим',
  themeLight: 'Світлий режим',
  signIn: 'Увійти',
  signInWithSms: 'Увійти через SMS',
  signUp: 'Зареєструватися',
  signedInAs: 'Ви увійшли як',
  account: 'Обліковий запис',
  profile: 'Профіль',
  editor: 'Редактор',
  editProfile: 'Редагувати профіль',
  myPortal: 'Мій портал',
  portalSidebar: {
    projects: 'Проєкти',
    navigation: 'Навігація порталу',
    accountMenu: 'Меню облікового запису',
    editProfile: 'Редагувати профіль',
    openNavigation: 'Відкрити навігацію',
    closeNavigation: 'Закрити навігацію',
    expandSidebar: 'Розгорнути бічну панель',
    collapseSidebar: 'Згорнути бічну панель',
    signOut: 'Вийти',
    reportABug: 'Повідомити про помилку',
  },
  portalProjects: {
    view: 'Подання проєкту',
    gridView: 'Подання сіткою',
    mapView: 'Подання на карті',
    welcomeBack: (name: string) => `З поверненням, ${name} 👋`,
    chooseProject: 'Оберіть проєкт, щоб розпочати',
    openingProject: (name: string) => `Відкриття ${name}…`,
    couldNotOpenProject: 'Не вдалося відкрити проєкт',
    loadFailedFor: (tenants: string) =>
      `Не вдалося завантажити проєкти з ${tenants}.`,
    someProjectsMissing:
      'Деякі ваші проєкти можуть бути відсутні у цьому списку.',
    loadingProblem: 'Це проблема завантаження, а не порожній обліковий запис.',
    loadingProjects: 'Завантаження проєктів…',
    noProjectsMatchSearch: 'Жоден проєкт не відповідає вашому пошуку.',
    noProjectsMatchFilters: 'Жоден проєкт не відповідає вашим фільтрам.',
    noProjectsLoaded: 'Не вдалося завантажити жодного проєкту.',
    noProjectsYet: 'У вас ще немає проєктів.',
    loadingMap: 'Завантаження карти…',
    mapUnavailable:
      'Карта недоступна — немає конфігурації Mapbox на цьому хості.',
    mapDescription:
      'Кожна шпилька розташована на реальному місці Проєкту та несе його логотип — натисніть, щоб відкрити.',
    lastOpened: (when: string) => `Востаннє відкрито ${when}`,
    daysAgo: (n: number) => `${n} днів тому`,
    oneWeekAgo: '1 тиждень тому',
    weeksAgo: (n: number) => `${n} тижнів тому`,
    loadingPortal: 'Завантаження порталу…',
    lastOpenedLabel: 'Останній доступ',
    loadFailed:
      'Не вдалося завантажити ваші проєкти. Перевірте підключення та спробуйте ще раз.',
  },
  portalProfile: {
    profileSettings: 'Налаштування профілю',
    personalData: 'Особисті дані',
    accountData: 'Дані облікового запису',
    profilePicture: 'Фото профілю',
    upload: 'Завантажити',
    uploading: 'Завантаження\u2026',
    removing: 'Видалення…',
    firstName: "Ім'я",
    lastName: 'Прізвище',
    jobTitle: 'Посада',
    phoneNumber: 'Номер телефону',
    email: 'Електронна пошта',
    password: 'Пароль',
    currentPassword: 'Поточний пароль',
    newPassword: 'Новий пароль',
    confirmPassword: 'Підтвердження пароля',
    enterCurrentPassword: 'Введіть поточний пароль',
    minimumCharacters: 'Мінімум 8 символів',
    reEnterPassword: 'Повторіть пароль',
    passwordsMismatch: 'Паролі не збігаються.',
    change: 'Змінити',
    updatePassword: 'Оновити пароль',
    saveChanges: 'Зберегти зміни',
    saving: 'Збереження\u2026',
    backToPortal: 'Повернутися до Порталу',
    tenant: 'Орендар',
    errorLoadProfile: 'Не вдалося завантажити ваш профіль. Спробуйте ще раз.',
    errorSaveProfile: 'Не вдалося зберегти зміни. Спробуйте ще раз.',
    errorUploadAvatar: 'Не вдалося завантажити фото. Спробуйте ще раз.',
    errorRemoveAvatar: 'Не вдалося видалити фото. Спробуйте ще раз.',
    errorChangePassword: 'Не вдалося змінити пароль.',
    currentPasswordIncorrect: 'Поточний пароль неправильний.',
    tooManyPasswordAttempts:
      'Забагато спроб. Зачекайте кілька хвилин і спробуйте знову.',
    phoneOnlySignInMethod:
      'Ваш номер телефону — єдиний спосіб входу, тому його не можна видалити.',
    phoneInUse: 'Цей номер телефону вже використовує інший обліковий запис.',
    invalidPhone: 'Введіть дійсний номер телефону.',
    invalidProfile:
      'Деякі дані недійсні. Перевірте введене та спробуйте ще раз.',
    firstNameRequired: 'Імʼя обовʼязкове.',
    setPassword: 'Встановити пароль',
    projectCoverAlt: (name: string) => `Обкладинка ${name}`,
    struxHubVersion: (v: string) => `StruxHub версія: ${v}`,
    fileTooLarge: (maxMB: number) =>
      `Файл занадто великий. Максимальний розмір — ${maxMB} МБ.`,
    phoneNotVerifiedYet: 'Цей номер телефону ще не підтверджено',
    verify: 'Підтвердити',
    remove: 'Видалити',
    addPhoneNumber: 'Додати номер телефону',
    changePhoneNumber: 'Змінити номер телефону',
    newNumber: 'Новий номер',
    codeSmsNotice:
      'Ми надішлемо 6-значний код на цей номер у SMS. Може стягуватися плата за повідомлення та передачу даних.',
    sendCode: 'Надіслати код',
    sendingCode: 'Надсилання\u2026',
    enterTheCode: 'Введіть код',
    verificationCode: 'Код підтвердження',
    codeSentTo: (phone: string) => `Надіслано на ${phone}.`,
    wrongNumber: 'Неправильний номер?',
    edit: 'Змінити',
    resendCodeIn: (time: string) => `Надіслати код повторно через ${time}`,
    resendCode: 'Надіслати код повторно',
    sendNewCode: 'Надіслати новий код',
    verifyingCode: 'Перевірка\u2026',
    phoneNumberVerified: 'Номер телефону підтверджено.',
    phoneAlreadyVerified: 'Цей номер уже підтверджено.',
    removePhoneTitle: 'Видалити номер телефону',
    removePhoneQuestion: (phone: string) =>
      `Видалити ${phone} з вашого профілю?`,
    invalidCode: (triesLeft: number) =>
      `Неправильний код. ${ending(triesLeft, 'Залишилася', 'Залишилося', 'Залишилося')} ${triesLeft} ${ending(triesLeft, 'спроба', 'спроби', 'спроб')}.`,
    codeExpired: 'Термін дії коду минув. Надішліть новий.',
    verificationPhoneInUse:
      'Цей номер уже використовується іншим обліковим записом. Якщо він ваш, зверніться до підтримки.',
    invalidMobilePhone: 'Введіть правильний номер мобільного телефону.',
    tooManyCodesRequested: (minutes: number) =>
      `Забагато запитів коду. Спробуйте ще раз через ${minutes} ${ending(minutes, 'хвилину', 'хвилини', 'хвилин')}.`,
    tooManyCodesRequestedHours: (hours: number) =>
      `Забагато запитів коду. Спробуйте ще раз через ${hours} ${ending(hours, 'годину', 'години', 'годин')}.`,
    smsUnavailable: 'Зараз не вдалося надіслати SMS. Спробуйте пізніше.',
    unsupportedDestination: 'SMS у цю країну поки що не підтримуються.',
    errorPhoneVerification:
      'Не вдалося підтвердити номер телефону. Спробуйте ще раз.',
    qrCode: 'QR-код',
    qrCodeDescription:
      'Використовуйте цей код на сканерах у будь-якому з ваших проєктів StruxHub.',
  },
  portalProjectRequest: {
    title: 'Запит на новий проєкт',
    description:
      'Щоб створити новий проєкт, надішліть запит за допомогою форми нижче',
    firstName: 'Ім’я',
    lastName: 'Прізвище',
    jobTitle: 'Посада',
    workEmail: 'Робоча пошта',
    phoneNumber: 'Номер телефону',
    projectName: 'Назва проєкту',
    additionalDetails: 'Додаткові відомості',
    submit: 'Надіслати',
    submitting: 'Надсилання…',
    fieldRequired: 'Це поле обов’язкове.',
    invalidEmail: 'Введіть дійсну адресу електронної пошти.',
    successTitle: 'Запит надіслано',
    successDescription:
      'Ваш запит на проєкт успішно надіслано. Ми розглянемо його та незабаром зв’яжемося з вами.',
    errorSubmit: 'Не вдалося надіслати запит. Спробуйте ще раз.',
    errorInvalid: 'Деякі дані недійсні. Перевірте форму.',
    errorTooManyRequests:
      'Забагато запитів. Зачекайте трохи й спробуйте знову.',
  },
  preferences: 'Налаштування',
  administration: 'Адміністрування',
  projectDetails: 'Деталі проєкта',
  integrations: 'Інтеграції',
  userDirectory: 'Каталог користувачів',
  team: 'Команда',
  crew: 'Команда',
  teams: 'Команди',
  defaultTeams: 'Команди за замовчуванням',
  presentations: 'Презентації',
  tracker: 'Трекер',
  uploadSchedule: 'Завантаження розкладу',
  filterBy: 'Відфільтрувати за',
  filterByMe: "Відфільтрувати за 'Мої'",
  filterByMyCompany: "Відфільтрувати за 'Моя компанія'",
  filter: 'Фільтрувати',
  filters: 'Фільтри',
  createNewFilter: 'Створити новий фільтр',
  addScheduleData: 'Дотати дані розкладу',
  editScheduleData: 'Редагувати дані розкладу',
  struxhubObject: `${brandName} об'єкт`,
  selectObject: "Оберіть об'єкт",
  selectWorkForm: 'Виберіть форму для роботи',
  hierarchies: 'Ієрархії',
  dataToSchedule: 'Дані до розкладу',
  searchUserOrGroup: 'Пошук Користувача або Групи',
  tool: 'Інструмент',
  workflow: 'Робочий процес',
  workflowDetails: 'Деталі робочого процесу',
  workflows: 'Робочі процеси',
  inactiveWorkflowTypeTooltip:
    'Цей тип робочого процесу недоступний для створення.',
  workflowConfigurator: 'Конфігураторі робочих процесів',
  manageItIn: 'Керуйте ним у',
  manageInWorkflowConfiguratorOnDesktop:
    'Керуйте ним у Конфігураторі робочих процесів у десктопній версії',
  fields: 'Поля',
  submissions: 'Подання',
  fieldsSelected: 'поля вибрано',
  struxHubFields: 'Поля StruxHub',
  primaryWorkflowFields: 'Основні',
  secondaryWorkflowFields: 'Додаткові',
  structureFields: 'Структура',
  contentFields: 'Вміст',
  primaryWorkflowFieldsDescription:
    'Первинні поля робочого процесу визначають основну інформацію, що характеризує запис і використовується в усьому StruxHub для відображення, керування та контролю кожного робочого процесу. Це включає основні деталі, такі як Дата і Час, Місцезнаходження, Компанія, Обладнання та Матеріал, а також користувачів робочого процесу на основі ролей, таких як Відповідальний користувач (основний власник робочого процесу), Запитувач та Додаткові контакти. Кожне Первинне поле робочого процесу можна додати лише один раз',
  secondaryWorkflowFieldsDescription:
    "Додає допоміжний контекст та зв'язки, що використовуються в усьому StruxHub для уточнення, організації та доповнення основних деталей робочого процесу",
  struxHubFieldsDescription:
    'Поля, що є рідними для StruxHub і відображаються на всій платформі',
  structureFieldsDescription:
    'Елементи макета, що використовуються для організації робочого процесу',
  contentFieldsDescription:
    'Інструктивний текст, що показується користувачам. Не збирає дані',
  customFieldsDescription:
    'Загальні поля введення для збору даних, не охоплених полями StruxHub',
  goToStep: 'Перейти до кроку',
  goToWorkflow: 'Перейти до робочого процесу',
  sitemapSetup: 'Налаштування мапи сайту',
  templateUpdating: 'Оновлення шаблону',
  selectExistingWorkflow: 'Вибрати існуючий робочий процес',
  createNewWorkflow: 'Створити новий робочий процес',
  enterWorkflowType: 'Введіть тип робочого процесу',
  basedOnWorkflow: 'На основі',

  publishTo: 'Опублікувати до',
  geoReferencing: "Географічно з'єднано",
  tags: 'Теги',
  permittedCompanies: 'Дозволені компанії',
  notPermitted: 'Не дозволено',
  hoursOfOperation: 'Часи роботи',
  inheritedHoursDescription:
    'Успадковано з робочих годин проєкту. Керуйте цим у розділі',
  inheritedClosuresDescription:
    'Успадковано із закриттів проєкту. Керуйте цим у розділі',
  outsideOperatingHours: 'Поза робочими годинами',
  dateClosures: 'Дати закриття',
  showInMap: 'Показувати на картах',
  showInMapTooltip:
    'Показувати на картах:\nУвімкнено: об’єкт ресурсу можна показувати на видах карти та перемикати там його видимість.\nВимкнено: об’єкт ресурсу недоступний на картах.',
  resourceShownInMaps:
    'Об’єкт ресурсу можна показувати на видах карти та перемикати там його видимість.',
  resourceHiddenFromMaps: 'Об’єкт ресурсу недоступний на картах.',
  workflowTypes: 'Типи робочих процесів',
  workflowResource: 'WR',
  workflowResourceTooltip:
    'WR (Workflow Resource):\nУвімкнено: об’єкт ресурсу можна забронювати та призначити робочому процесу.\nВимкнено: об’єкт ресурсу недоступний для бронювання.',
  workflowResourceBulk: 'Ресурс робочого процесу',
  resourceNotAvailableForBooking: 'Об’єкт ресурсу недоступний для бронювання.',
  resourceAvailableForBooking:
    'Об’єкт ресурсу можна забронювати та призначити робочому процесу.',
  maxBookingDuration: 'Максимальна тривалість бронювання',
  maxOverlapping: 'Максимальне перекриття',
  maxFormBookingDurationDays: (duration: number, unit: string) =>
    `Max Booking Duration: Максимальна тривалість бронювання для цього Workflow становить ${duration} ${unit}`,
  objects: "Об'єкти",
  image: 'Зображення',
  groupBy: 'Згрупувати за',
  basemap: easterEggMode ? 'Базована мапа' : 'Базова мапа',
  newView: 'Створити нову мапу',
  addView: 'Додати перегляд',
  savedViews: 'Збережені перегляди',
  createNewView: 'Створити нову мапу',
  createNewWhiteboard: 'Створити нову дошку',
  mapFileUpload: {
    uploadMapFile: 'Завантажити файл мапи',
    uploadWhiteboard: 'Завантажити дошку',
    replaceThisPlan: 'Замінити цей план',
    withThisOne: '... на цей.',
    dragMapHere: 'Перетягніть мапу сюди',
    dragMapFileHere: 'Перетягніть файл мапи сюди',
    acceptedExtensions: (extensions: string) => `(підтримуються ${extensions})`,
    selectPage: 'Вибрати сторінку',
    planName: 'Назва плану',
    whiteboardName: 'Назва дошки',
    planUploaded: 'План завантажено',
    mapFileUploaded: 'Файл мапи завантажено',
    basemapUploaded: 'Базову мапу завантажено',
    uploadingPlan: 'Завантаження плану',
    uploadingBasemap: 'Завантаження базової мапи',
    tilesetLimits: (maxNumber: number, maxSize: string) =>
      `Ви можете завантажити до ${maxNumber} наборів тайлів розміром до ${maxSize} кожен.`,
    tooManyTilesets: 'Ви завантажили забагато наборів тайлів',
    fileTooLarge: 'Ви завантажили завеликий файл',
    tilesetNameTooLong: (maxLength: number) =>
      `Назва набору тайлів задовга. Максимум ${maxLength} символів`,
    invalidFileName:
      'Назва файлу може містити лише літери, цифри та символи «.», «-» і «_»',
  },
  mapExport: {
    exportMap: 'Експортувати мапу',
    output: 'Результат',
    map: 'Мапа',
    frameTag: 'Область експорту · перетягніть мапу, щоб змінити',
    backToSavedView: 'Відновити збережений ракурс',
    restoreViewportReason: 'Доступно після переміщення або масштабування мапи.',
    framing: 'Кадрування',
    previewReduced: 'Показано дрібніше за реальний розмір',
    background: 'Фон',
    blank: 'Порожній',
    basemapLabels: 'Підписи базової мапи',
    notOnBlank: 'Не для порожнього фону',
    buildings3d: '3D-будівлі',
    streetOnly: 'Лише «Вулиця»',
    angle: 'Кут',
    topDown: 'Згори',
    tilted: 'З нахилом',
    toScale: 'У масштабі',
    notToScale: 'Без масштабу',
    topDownBody: (deg: number) =>
      `Вирівняно зі збереженого нахилу ${deg}°, тож у масштабі.`,
    tiltedBody: 'Зображення, а не план: без масштабної лінійки й покажчика.',
    whatYouLose: 'Що втрачається',
    tiltTradeOffsTitle: 'Експорт із нахилом — це зображення, а не план',
    tiltTradeOffs: {
      noScale: 'Без масштабної лінійки. Не вимірюйте по ньому.',
      mapLabelsOnly: 'Власні підписи мапи, без покажчика.',
      farSofter: 'Дальній край менш чіткий і дрібніший.',
      northCentreSlower:
        'Стрілка півночі точна лише в центрі, а експорт триває довше.',
      buildingsHide: '3D-будівлі можуть закривати піни.',
      buildingsHideIcons: '3D-будівлі можуть закривати значки.',
    },
    format: 'Формат',
    png: 'PNG',
    pdf: 'PDF',
    useFor: 'Призначення',
    useForChoices: {
      print: 'Друк',
      screen: 'Екран',
    },
    paperSize: 'Розмір паперу',
    paperOption: (paper: string, size: string) => `${paper} · ${size}`,
    screenShape: 'Пропорції',
    screenShapes: {
      '16x9': '16:9 Широкоекранний',
      '16x10': '16:10',
      '4x3': '4:3',
    },
    closestToMap: 'Найближче до вашої мапи',
    tooLargeForDevice: 'Завеликий для цього пристрою',
    fileSize: (mb: number) => `Розмір файлу ~ ${mb} МБ`,
    fileSizeRange: (low: number, high: number) =>
      `Розмір файлу ~ ${low}–${high} МБ`,
    pdfSizeHint: (mb: string) => `PDF буде значно меншим: ~ ${mb} МБ.`,
    exportAs: (format: string) => `Експортувати ${format}`,
    exporting: 'Експортування…',
    standardQuality: 'Стандартна',
    highQuality: 'Висока',
    screenStandardHint: (width: number, height: number) =>
      `${width} × ${height} px: для пошти, чатів і презентацій.`,
    screenHighHint: (width: number, height: number) =>
      `${width} × ${height} px: лишається чітким при збільшенні.`,
    screenHighExceedsDevice: 'Висока якість завелика для цього пристрою.',
    paperStandardHint: (dpi: number) => `${dpi} dpi: для офісних принтерів.`,
    paperHighHint: (dpi: number) => `${dpi} dpi: чіткий друк великих форматів.`,
    highQualityExceedsDevice:
      'Висока якість завелика для пристрою на цьому форматі.',
    screenImageryPointer: 'Супутник чіткіший на великому аркуші.',
    useSharpestPaper: (useFor: string, paper: string, quality: string) =>
      `Перейти на ${useFor} · ${paper} · ${quality}`,
    letter: 'Letter',
    a4: 'A4',
    tabloid: 'Tabloid',
    a3: 'A3',
    a2: 'A2',
    a1: 'A1',
    archD: 'ARCH D',
    a0: 'A0',
    archA: 'ARCH A',
    archB: 'ARCH B',
    ansiC: 'ANSI C',
    archC: 'ARCH C',
    ansiD: 'ANSI D',
    archE1: 'ARCH E1',
    ansiE: 'ANSI E',
    archE: 'ARCH E',
    paperGroups: {
      imperial: 'ANSI і ARCH',
      metric: 'ISO',
    },
    moreSizes: (family: string) => `Інші розміри (${family})`,
    withFamiliarName: (standard: string, name: string) =>
      `${standard} (${name})`,
    orientation: 'Орієнтація',
    landscape: 'Альбомна',
    portrait: 'Книжкова',
    millimetres: (width: number, height: number) => `${width} × ${height} мм`,
    inches: (width: number, height: number) =>
      `${width.toLocaleString('uk')} × ${height.toLocaleString('uk')} дюйм.`,
    pageElements: 'Назва й легенда',
    pageElementsHint: 'Назва, дата, легенда, масштаб і стрілка півночі.',
    pageElementsHintTilted:
      'Назва, дата й стрілка півночі, позначка «Без масштабу».',
    northLetter: 'Пн',
    clusterLegend: 'Робочі процеси',
    clusterLegendKeyed: 'Робочі процеси · див. покажчик',
    keyTagLegend: 'Див. покажчик',
    truePositionLegend: 'Точне розташування',
    metres: (length: number) => `${length.toLocaleString('uk')} м`,
    kilometres: (length: number) => `${length.toLocaleString('uk')} км`,
    miles: (length: number) => `${length.toLocaleString('uk')} миль`,
    mapFiles: 'Файли мапи',
    mapFileList: 'Список файлів мапи',
    subLayersOf: (layer: string) => `Підшари: ${layer}`,
    mapFilesOutsideFrame: (count: number) =>
      `Ще ${count} файл${ending(count, '', 'и', 'ів')} мапи в цьому вигляді поза кадром`,
    shapes: 'Фігури',
    markers: 'Значки',
    layerLabels: 'Підписи',
    pins: 'Піни',
    markups: 'Розмітка',
    needsPins: 'Потрібні піни',
    needsMarkups: 'Потрібна розмітка',
    readability: 'Читабельність',
    sizing: 'Розміри',
    optimized: 'Оптимально',
    asOnMap: 'Як на мапі',
    readabilityHints: {
      optimized: 'Розмір під папір і відстань, без накладань.',
      asOnMap: 'Піни, значки й підписи мають розміри як на мапі.',
      asOnMapWithoutPins: 'Значки й підписи мають розміри як на мапі.',
      tilted: '«Оптимально» доступне лише згори, тож розміри як на мапі.',
    },
    overallSize: 'Загальний розмір',
    easyToReadFrom: 'Легко читати з',
    readZones: {
      inHand: 'У руках або на столі',
      upClose: 'Зблизька',
      acrossRoom: 'Здалеку',
    },
    standardFor: (name: string) => `Стандарт: ${name}`,
    standardAt: (name: string, distance: string) =>
      `Стандарт ${name}: ${distance}`,
    aboutDistance: (distance: string) => `бл. ${distance}`,
    readFromValueText: (distance: string, zone: string) =>
      `${distance}, ${zone}`,
    adjustByType: 'Налаштувати за типом',
    adjustByTypeNote: (baseline: string): string => `Основа: ${baseline}`,
    adjustByTypeInfo: (baseline: string, isPrint: boolean): string =>
      isPrint
        ? `Кожен тип іде за «${baseline}», доки ви не задасте його розмір. Заданий розмір лишається, доки ви його не скинете.`
        : `Кожен тип — це відсоток від «${baseline}» і зростає разом із ним.`,
    percent: (value: number) => `${value}%`,
    centimetres: (length: number) => `${length.toLocaleString('uk')} см`,
    feet: (length: number) => `${length.toLocaleString('uk')} фут.`,
    customized: 'Змінено',
    textKinds: {
      workflowPin: 'Підписи робочих процесів',
      location: 'Підписи розташувань',
      markup: 'Підписи розмітки',
      markupText: 'Текст розмітки',
      key: 'Покажчик і номери',
    },
    points: (pt: number) => `${pt.toLocaleString('uk')} пт`,
    autoSize: (size: string) => `Авто · ${size}`,
    resetRowToAuto: (row: string) => `Повернути «${row}» до «Авто»`,
    resetRowToDefault: (row: string) => `Повернути «${row}» до 100%`,
    markupTextInfo:
      'Масштабується разом із мапою як її частина, тому не залежить від відстані читання.',
    atFloorHint: 'Це вже найменший розмір, що читається з цієї відстані.',
    atFloorScreenHint: 'Це вже найменший розбірливий розмір.',
    atFitLimitHint: 'Найбільший розмір, що вміщується на цьому папері.',
    atFitLimitScreenHint: 'Найбільший розмір, що вміщується в цей експорт.',
    needsLabelKey: 'Потрібен «Покажчик»',
    symbolKinds: {
      workflowPin: 'Піни робочих процесів',
      location: 'Значки розташувань',
    },
    printedMillimetres: (length: number) => `${length.toLocaleString('uk')} мм`,
    printedInches: (length: number) => `${length.toLocaleString('uk')} дюйм.`,
    key: 'Покажчик',
    keyExplainer:
      'Підписи, що не вміщуються, отримують номер або літеру й наводяться в покажчику.',
    keyAllFit: 'Усі підписи вміщуються на мапі, покажчик не потрібен.',
    keyOffHint: (count: number) =>
      `Увімкніть, щоб перелічити приховані підписи у файлі: ${count}.`,
    keyOnHint: {
      besideMap: () => 'Поруч із мапою у файлі',
      besidePage: (paper: string) =>
        `Поруч зі сторінкою, тож PNG ширший за ${paper}. У PDF він на сторінці 2.`,
      pageTwo: () => 'На сторінці 2 PDF',
    },
    keyViewList: 'Переглянути список',
    keySummaryTitle: (labels: number) => `Куди потрапляють підписи: ${labels}`,
    labelFates: {
      onMap: 'На мапі',
      inKey: 'У покажчику',
      notListed: 'Не вмістилися в покажчик',
      asCounts: 'Лише кількість, у чорних кружках',
      notShown: 'Пропущено',
    },
    markupLeftOff: 'Пропущені підписи розмітки',
    densityWarning: (isPaper: boolean) =>
      isPaper
        ? 'Надто щільно: наблизьте мапу, зменште текст або виберіть більший папір.'
        : 'Надто щільно: наблизьте мапу або зменште текст.',
    keyGroupHeader: (count: number) => `Робочих процесів: ${count}`,
    keyGroupHeaderTrimmed: (count: number, listed: number) =>
      `Робочих процесів: ${count}, наведено ${listed}`,
    keyOverflowRow: (count: number) => `+${count} ще, не наведено`,
    previewKeyBadge: {
      besideMap: (labels: number) =>
        `Покажчик поруч із мапою · підписів: ${labels}`,
      besidePage: (labels: number) =>
        `Покажчик поруч зі сторінкою · підписів: ${labels}`,
      pageTwo: (labels: number) =>
        `Покажчик на сторінці 2 · підписів: ${labels}`,
    },
    exportCancelled: 'Експорт скасовано.',
    shareMap: 'Поділитися мапою',
    createImage: 'Створити зображення',
    createPdf: 'Створити PDF',
    mapReady: (details: string) => `Мапа готова · ${details}`,
    keepScreenOpen: 'Не закривайте цей екран, доки файл не буде готовий.',
    shareFailed: 'Не вдалося поділитися файлом. Спробуйте ще раз.',
    desktopExportHint: (tool: string) =>
      `Налаштуйте папір, шари й мітки в «${tool}» на комп'ютері.`,
    phases: {
      preparing: 'Підготовка…',
      loadingTiles: 'Завантаження тайлів…',
      placingLabels: 'Розміщення підписів…',
      rendering: 'Рендеринг…',
      encoding: 'Збереження файлу…',
    },
    errors: {
      tilesTimeout: 'Карта завантажувалася надто довго. Спробуйте ще раз.',
      bufferClamped:
        'Цей пристрій не зміг відрендерити повний розмір. Виберіть менший розмір.',
      zoomTooDeep: 'Зменште масштаб, щоб експортувати в цьому розмірі.',
      exceedsDevice:
        'Завеликий розмір для цього пристрою. Виберіть менший розмір або стандартну якість.',
      exceedsDeviceScreen:
        'Завеликий розмір для цього пристрою. Виберіть стандартну якість.',
      interrupted: 'Розмір вікна змінився під час експорту. Спробуйте ще раз.',
      pageReloaded:
        'Сторінка перезавантажилася під час експорту. Спробуйте ще раз.',
      failed: 'Не вдалося експортувати. Спробуйте ще раз.',
    },
    phoneErrors: {
      bufferClamped:
        'Телефону забракло пам’яті для мапи. Закрийте інші застосунки й спробуйте ще раз.',
      exceedsDevice: 'Завеликий розмір для цього телефона.',
      exceedsDeviceScreen: 'Завеликий розмір для цього телефона.',
      interrupted:
        'Екран повернувся або змінив розмір під час експорту. Спробуйте ще раз.',
      pageReloaded:
        'Телефону забракло пам’яті, і він перезавантажив сторінку. Закрийте інші застосунки й спробуйте ще раз.',
    },
    shareMapFailed: 'Не вдалося відкрити «Поділитися мапою». Спробуйте ще раз.',
    actualSize: 'Реальний розмір',
    fitPage: 'Вмістити сторінку',
    actualSizeHint: 'Перетягуйте, щоб роздивитися',
    previewViewMode: 'Розмір попереднього перегляду',
    actualSizePanLabel:
      'Реальний розмір · перетягуйте або натискайте стрілки, щоб роздивитися, Shift — більші кроки',
    actualSizeTooltip:
      'Перевірте, чи читаються підписи в реальному розмірі. Область експорту не зміщується.',
    actualSizeTopDownOnly: 'Реальний розмір · лише згори',
    fitPageTooltip: 'Знову показує всю сторінку з попереднім кадруванням.',
    keepTabVisible: 'Не перемикайте вкладку, доки експорт не завершиться.',
  },
  newMapView: 'Нова мапа',
  notPublished: 'Не опубліковано',
  applications: 'Додатки',
  savedViewsDialog: {
    searchHint: 'Пошук переглядів',
    columns: {
      viewName: 'Переглянути назву',
      filterCount: 'Фільтри',
      access: 'Надано доступ проєкта',
      creator: 'Створено',
    },
  },
  createNewTag: 'Створити новий тег',
  viewBasemap: 'Показати базову мапу',
  viewBookings: 'Показати бронювання',
  thereIsNoInstanceInTheSelectedCategory:
    'У вибраній категорії немає жодного екземпляра',
  directory: 'Каталог',
  name: "Ім'я",
  firstName: "Ім'я",
  lastName: 'Прізвище',
  phoneNumber: 'Телефон',
  globalRole: 'Назва посади',
  employeeIDNumber: 'Ідентифікаційний номер працівника',
  company: 'Компанія',
  companyAliasLabel: 'Компанія, як повідомив користувач',
  companyOther: 'Інша',
  addXAsNewCompany: (name: string) => `Додати «${name}» як нову Компанію`,
  selectOrAddCompany: 'Оберіть або додайте Компанію',
  companyDirectory: 'Каталог компаній',
  common: 'Звичайні',
  myCompany: 'Моя компанія',
  myCo: 'Моя Кo.',
  companyType: 'Тип компанії',
  classification: 'Класифікація',
  companyName: 'Назва компанії',
  user: 'Користувач',
  users: 'Користувачі',
  userTags: 'Теги',
  allUsers: 'Всі Користувачі',
  role: 'Посада',
  projectRole: 'Посада у проєкті',
  projectRoles: 'Посади у проєкті',
  projectTrades: 'Спеціальності у проєкті',
  address: 'Aдреса',
  email_noun: 'Електронна адреса',
  phone: 'Телефон',
  phoneVerification: 'Перевірка телефону',
  call_verb: 'Зателефонувати',
  email_verb: 'Написати листа',
  sendDirectMessageInStruxHub: `Відправити особисте повідомлення у ${brandName}`,
  password: 'Пароль',
  currentPassword: 'Поточний пароль',
  newPassword: 'Новий пароль',
  confirmNewPassword: 'Підтвердіть новий пароль',
  confirmPassword: 'Підтвердіть пароль',
  group: 'Група',
  reportDetails: 'Деталі репорту',
  workflowReportTitle: 'Звіт по робочих процесах',
  deliveryReportTitle: 'Репорт по доставкам',
  scanStationReportTitle: 'Звіт по скан станціям',
  resourceDetails: 'Деталі ресурсу',
  createReport: 'Створити репорт',
  deliveryReport: 'Репорт по доставкам',
  formReport: 'Звіт по робочих процесах', // REVIEW: родовий відмінок 'робочих процесів'
  materialReport: 'Звіт по матеріалам',
  aReportWillBeGeneratedWithTheAboveFilters:
    'Буде створено звіт із зазначеними вище фільтрами',
  aReportWillBeSentToYourEmailShortly:
    'Звіт буде надіслано на вашу електронну адресу найближчим часом',
  reportsTo: 'Звітує',
  reportOf: (type: string) => `${type} эвітує`,
  officeField: easterEggMode ? 'Маєток/Поле' : 'Офіс/Будмайданчик',
  accountType: 'Тип облікового запису',
  status: 'Статус',
  manufacturer: 'Виробник',
  fuel_type: 'Тип палива',
  bid_package: 'Тендерний пакет',
  assembly: 'Збірка',
  isDockMaster: 'Чи є начальником доку',
  isInspector: 'Чи є інспектором',
  invalidUser: 'Невалідний користувач',
  unknownUser: 'Невідомий користувач',
  unknownDeletedObject: "Невідомий (видалений) Об'єкт",
  ruleProblems: {
    incomplete: 'Правило неповне, його не можна зберегти.',
    unknownCondition:
      'Цей тег посилається на те, чого більше не існує. Видаліть його або виберіть знову.',
  },
  inviteStatus: 'Статус запрошення',
  inviteStatusValues: {
    Not_Invited: 'Не запрошений',
    Pending: 'В очікуванні',
    Accepted: 'Підтверджений',
    // service
    Not_Added: 'Не доданий',
    Added: 'Доданий',
    Not_Requested: 'Не запитано',
    Requested: 'Запрошений',
  },
  accountPosition: 'Позиція облікового запису',
  accountPositions: {
    office: easterEggMode ? 'Маєток' : 'Офіс',
    field: easterEggMode ? 'Поле' : 'Будмайданчик',
    management: easterEggMode ? 'Канцелярія' : 'Адміністрація',
  },
  fieldMapping: 'Відображення полів',
  projectAccessTypeValues: {
    admin: easterEggMode ? 'Управитель' : 'Адміністратор',
    member: easterEggMode ? 'Кріпак' : 'Учасник',
    owner: easterEggMode ? 'Пан' : 'Власник',
  },

  modalInfoSections: {
    personalInformation: 'Персональні дані',
    companyInformation: 'Корпоративні відомості',
    projectInformation: 'Проєктні відомості',
    additionalInformation: 'Додаткові відомості',
    workflowPermissions: 'Дозволи',
    companyContactInfo: 'Корпоративні відомості',
    legalInfo: 'Юридична інформація',
    companyAddress: 'Aдреса компанії',
    users: 'Користувачі',
    qrCodeAccessAndBadges: 'Доступ до QR-коду та значки',
    globalInfo: `Загальна інформація (${brandShortName} рівень)`,
  },
  projectWorkflows: {
    deliveryBooking: 'Бронювання доставки',
    deliveryInspection: 'Перевірка доставки',
    scheduleComment: 'Керування розкладом',
    formsMaster: 'Головний по формам',
    scanMaster: 'Головний зі сканування',
    viewSchedule: 'Переглянути розклад',
    scheduleUpdating: 'Оновити розклад',
    scheduleUploading: 'Завантажити розклад',
    scheduleNotes: 'Примітки до розкладу',
  },
  permissions: {
    none: 'Без доступу',
    readOnly: 'Читання',
    standard: 'Стандарт',
    master: 'Майстер',
  },
  userProfileRoleNames: {
    admin: easterEggMode ? 'Управитель' : 'Адміністратор',
    owner: easterEggMode ? 'Пан' : 'Власник',
    docMaster: 'Начальник доку',
    inspector: 'Інспектор',
    presentationUser: 'Майстер презентації',
    formsMaster: 'Головний по формам',
    scheduleViewing: 'Переглянути розклад',
    scheduleUpdating: 'Оновити розклад',
    scheduleUploading: 'Завантажити розклад',
    scheduleNotes: 'Примітки до розкладу',
  },
  companyTypes: {
    GeneralContractorPrimary: 'Генеральний підрядник (Первинний)',
    GeneralContractorSecondary: 'Генеральний підрядник (Вторинний)',
    Owner: 'Власник',
    SubContractor: 'Субпідрядник',
    TieredSubContractor: 'Багаторівневий субпідрядник',
  },
  saveProfile: 'Зберегти профіль',
  notificationType: 'Тип сповіщення',
  notificationSettings: 'Налаштування сповіщень',
  deliveryNotifications: 'Сповіщення про доставки',
  scheduleNotifications: 'Сповіщення про розкладу',
  trackerNotifications: 'Сповіщення Трекера',
  logisticsNotifications: 'Сповіщення логістики',
  formNotifications: 'Сповіщення робочого процесу', // REVIEW: можливо plural 'процесів'
  scannerNotifications: 'Сповіщення сканера',
  iWouldLikeReceiveMy: 'Я хочу отримувати мої',
  inApp: 'В додатку',
  sms: 'СМС',
  bySms: 'За допомогою СМС',
  byEmail: 'Електронною поштою',
  successfullyUpdated: 'Успішно оновлено',
  wrongPhoneNumberFormat: 'Некоректний формат номера телефону',
  userProfileDescriptions: {
    name: `Ваше ім'я буде використовуватися коли Ви спілкуєтесь з іншими учасниками команди у ${brandName}.`,
    projectRoles:
      'Ваші ролі використовуються для забезпечення відповідності сповіщень та робочих процесів, в яких ви берете участь. ' +
      'Подальша кастомізація можлива у розділі Уподобання в Налаштуваннях.',
    notifications:
      `Додатки ${brandName} надсилатимуть вам сповіщення про роботу, учасником якої ви є. ` +
      'Ці сповіщення з’являються в додатку поруч із піктограмою дзвінка у верхньому правому куті сторінки, ' +
      'якщо ви ввели свій номер телефону у своєму профілі за допомогою SMS-повідомлень, надісланих на ваш телефон, ' +
      'а деякі зведення сповіщень можуть бути надіслані на вашу електронну адресу. ' +
      'Ви можете вимкнути будь-яку з цих опцій нижче, щоб більше не отримувати сповіщення цим методом.',
  },
  whitelistedDomains: 'Домени в білому списку',

  projectAdminSummary: 'Резюме адміністратора проєкта',
  lastUpdated: 'Востаннє оновлено',
  dateByName: (date: string, name: string) => date + ', ' + name,
  uploadProjectSchedule: 'Завантажте розклад проєкта',
  fileUploaded: 'Завантажено файл: ',
  totalActivities: 'Усього робіт',
  totalUsers: 'Усього користувачів',
  uploadProcurementPlan: 'Завантажте план закупівель',
  bpAssignedToCompanies: (amount: number, totalAmount: number) =>
    amount + ' з ' + totalAmount + ' бід пакетів доручено компаніям.',
  downloadCurrentValuesAsCsv: (toLink: (aText: string) => any) => [
    toLink('Завантажити'),
    ' поточні значення як csv файл.',
  ],
  uploadTeamList: 'Завантажте список членів команди',
  currentTeamMembers: (amount: number) =>
    'Членів команди на даний момент: ' + amount,
  emptyTagTypes: (tags: TagType[]) =>
    `Деякі з необхідних тегів типу (${tags
      .map(tag => getBandTitleByTagType(tag))
      .toString()}) порожні для деяких компаній. Для них будуть встановлені значення за замовчуванням.`,
  adminSummaryDescriptions: {
    projectDetails:
      'Встановіть назву та місцезнаходження вашого проєкта, так що Ви його зможете легко знайти у селекторі проєктів',
    projectSchedule: `Завантажуйте ваш оновлений розклад із P6 так часто як забажаєте. Не хвилюйтеся — Ви не втратите жодних данних у ${brandName}.`,
    procurementPlan:
      'Завантажуйте ваш оновлений план закупівель у міру того, як збільшується кількість субпідрядників у проєкті ' +
      'та уточнюйте очікувані значення для кожного бід пакета.',
    teamList:
      'Завантажуйте список членів команди у міру того, як нові члени приєднуються до проєкта. ' +
      "Включення необов'язкової інформації, такої як внутрішня приналежність до відділу / команди, " +
      'полегшує призначення ресурсів на роботи.',
  },
  integrationsSetupDescriptions: {
    invalidToken: 'Не валідний Токен',
  },
  procoreDescription: 'Програмне забезпечення для управління будівництвом',
  loginToProcore: 'Увійти в Procore',
  procoreLoginDescription:
    'Для налаштування цієї інтеграції потрібно увійти в обліковий запис Procore.',
  procoreNoCompaniesAccess:
    'У вас немає доступу до жодної компанії Procore. Будь ласка, зверніться до адміністратора вашої компанії.',
  procoreNoAccessibleProjects:
    'У вас немає доступних проєктів у цій компанії. Будь ласка, зверніться до адміністратора вашої компанії.',
  procoreConnectionFailed:
    'Не вдалося підключитися до Procore. Спробуйте ще раз або зверніться до адміністратора вашої компанії.',
  procoreUsersWithoutContact: (names: string[]) =>
    `Не вдалося додати ${names.join(', ')}: ${names.length === 1 ? 'у користувача' : 'у користувачів'} немає електронної адреси чи номера телефону. Зверніться до адміністратора проєкту в Procore, щоб отримати доступ до списку користувачів.`,
  integrationEnabled: 'Інтеграція увімкнена',
  integrationDisabled: 'Інтеграція вимкнена',
  deliveriesFieldMapping: 'Мапування полів доставок',
  workflowsFieldMapping: 'Мапування полів робочого процесу',
  selectWorkflowTypeFirst: 'Будь ласка, спочатку оберіть тип робочого процесу',
  deleteWorkflowMapping: 'Видалити мапування робочого процесу',
  synchronizationEnabled: 'Синхронізація увімкнена',
  synchronizationDisabled: 'Синхронізація вимкнена',
  connecting: 'Підключення...',
  loggedIn: 'Увійшли',
  loginToEnableIntegration: 'Увійдіть в Procore, щоб увімкнути інтеграцію',
  loginToDisableIntegration: 'Увійдіть в Procore, щоб вимкнути інтеграцію',
  procoreLoginSuccess: 'Успішно увійшли в обліковий запис Procore',
  procoreIntegrationEnabled: 'Інтеграція з Procore увімкнена',

  procoreFieldMappings: {
    struxhubDeliveryLog: 'Журнал доставок Struxhub',
    procoreDeliveryLog: 'Журнал доставок Procore',
    startTimeOfBookingDate: 'Час початку дати бронювання',
    time: 'Час',
    requesterCompany: 'Компанія замовника',
    deliveryFrom: 'Доставка від',
    bookingId: 'ID бронювання',
    trackingNumber: 'Номер відстеження',
    vendorVendorEmailsMaterialsDeliveryNote:
      'Постачальник, електронні адреси постачальника, матеріали, накладна',
    contents: 'Вміст',
    notesFromDeliveryLog: 'Примітки з журналу доставок',
    comments: 'Коментарі',
    attachedFilesFromDeliveryLog: 'Прикріплені файли з журналу доставок',
    attachments: 'Вкладення',
  },

  project: 'Проєкт',
  xProjects,
  projectSingularPlural: 'Проєкт(и)',
  notificationsRulesSetupDescriptions: {
    helperText1: (entityType: string) =>
      'Правило надсилатиме сповіщення списку користувачів про всі майбутні ' +
      entityType +
      ' з набором визначених тегів',
    helperText2:
      'Теги можна використовувати окремо або комбінувати з булевими операторами. Наприклад:',
  },

  selectProject: 'Оберіть проєкт',
  selectProjectToSync: 'Оберіть проєкт(и) для синхронізації',
  selectBookingTime: 'Виберіть час бронювання',
  newProject: 'Новий проєкт',
  projectName: 'Назва проєкту',
  projectCode: 'Код проєкту',
  projectLocation: 'Місцезнаходження проєкту',
  projectLogo: 'Лого проєкту',
  projectHeaderImage: 'Заголовок проєкту',
  streetAddress: 'Адреса вулиці',
  city: 'Місто',
  state_region: 'Штат',
  zipCode: 'Індекс',
  country: 'Країна',
  projectTimeSettings: 'Налаштування часу проєкту і календар',
  QRCodes: 'QR коди',
  xQRCode: (name: string) => `QR код ${name}`,
  scanStations: 'Станції сканування',
  projectAdmins: 'Адміни проєкту',
  setTheTimeZoneForThisProject: 'Вкажіть часовий пояс для цього проєкту',
  dateFormat: 'Формат часу',
  maturixProjectName: 'Назва проєкту Maturix',
  maturixTemperatureUnit: 'Одиниці виміру температури Maturix',
  maturixStrengthUnit: 'Одиниці виміру сили Maturix',
  maturixStations: 'Станції Maturix',
  undefinedSection: 'Невизначена секція',
  setTheDateTimeFormatForThisProject:
    'Вкажіть формат дати і часу для цього проєкту',
  localTimezone: 'Місцевий часовий пояс',
  us: 'США',
  international: 'Міжнародний',
  customProjectClosures: 'Спеціальні неробочі дні для проєкту',
  addCustomProjectClosure: 'Додати спеціальні неробочі дні для проєкту',
  forTheFuture: easterEggMode ? 'Як Бог дасть' : 'На майбутнє',
  projectType: 'Тип проєкту',
  apps: 'Додатки',
  admins: 'Адміни',
  theListOfProjectsAdministrators: 'Список адміністраторів проєкта',
  addNew: 'Додати новий',
  addNewValue: 'Додати',
  addNewAdmin: 'Додати нового адміна',
  addNewContact: 'Додати новий контакт',
  defaultWorkingHours: 'Робочі години за замовчуванням',
  timeZone: 'Часовий пояс',
  everyday: 'Щодня',
  holidays: 'Свята',

  struxHubHome: `Головна сторінка ${brandName}`,
  struxHubForms: `Форми у ${brandName}`,
  app: 'Додаток',
  manageApps: 'Керування додатками',
  defaultPage: 'Сторінка за замовчуванням',
  appsVisibilityInfo: 'Показати або приховати додатки в навігації.',
  defaultPageInfo:
    'Виберіть, який додаток слугуватиме стартовою сторінкою, коли користувачі повертаються до продукту.',
  projectDetailsDescriptions: {
    projectNameError:
      'Назва не може бути пустою і має містити лише літери, чиста, пробіли, _ , -',
    projectCodeError:
      'Код проекту не може бути порожнім і не повинен містити жодних спеціальних символів. Максимальна довжина коду проекту становить 5 символів',

    changeProjectLocationWarning:
      'Часовий пояс проєкта відрізняється від часового поясу розташування проєкта',
  },
  countryValues: countries,
  timezoneValues: timezones,
  timezoneWithOffsets: (name: string, offsets: string[]) =>
    `${name} (GMT ${offsets.join('/')})`,

  addCustomProjectClosures: 'Додати спеціальні неробочі дні для проєкта',
  closureName: 'Назва інтервала',
  closureStartDate: 'Дата початку закриття',
  closureEndDate: 'Дата закінчення закриття',

  loadingTeamMembers: 'Завантажуються члени команди',
  loadingMonitoringData: 'Завантажуються данні моніторингу',
  xTeamMembersSelected: (x: number) =>
    `Вибрано ${x} член${ending(x, 'а', 'ів', 'ів')} команди`,
  xCompaniesSelected: (x: number) =>
    `Вибрано ${x} компан${ending(x, 'а', 'ів', 'ів')}`,
  importExportTeamMembers: 'Імпортувати/Експортувати членів команди',
  teamMembersDropdownStates: {
    downloadTeamMembers: 'Завантажити користувачів (.csv)',
    uploadTeamMembers: 'Додати користувачів (.csv)',
    viewCompanyDirectory: 'Переглянути адресну книгу компанії',
    connectToProcore: 'Підключитися до Procore',
  },
  selectNotInvited: 'Вибрати не запрошених',
  selectStatusRequested: 'Виберіть СТАТУС Запитано',
  setNotInvited: 'Відкликати запрошення',
  addNewProjectMember: 'Додати нового члена команди',
  uploadYourUpdatedProjectMemberListCsvFile:
    'Оновіть список членів команди із csv файла.',
  orDragYourFileHere: 'Або перетягніть Ваш файл сюди.',
  uploadYourScheduleFileInP6XerOrExcelXlsxXlsmFormat:
    'Завантажте Ваш розклад у P6 (.xer) або Excel (.xlsx, .xlsm) форматі',
  sendInviteLinkToUser: 'Надіслати посилання із запрошенням користувачу',
  addUser: 'Додати користувача',
  byUsers: 'За користувачами',
  byWorkflowField: 'За полем процесу',
  byCompany: 'За компанією',
  bicUsers: 'Користувачі BIC',
  editUsers: (x: number) => `Редагувати користувач${x === 1 ? 'а' : 'ів'}`,
  userValidationErrors: {
    firstNameRequired: "Поле ім'я є обов'язковим",
    lastNameRequired: "Поле прізвище є обов'язковим",
    emailRequired: "Поле електронна адреса є обов'язковим",
    invalidEmail: 'Електронна адреса недійсна',
    invalidPhone: 'Номер телефону не дійсний',
    emailIsTaken: 'Інший член команди має таку електронну адресу',
    phoneIsTaken: 'Інший член команди має такий самий номер телефону',
    memberWithSuchEmailIsAlreadyAdded:
      'Користувач з такою електронною адресою вже є у проєкті',
    memberWithSuchPhoneNumberIsAlreadyAdded:
      'Користувач з таким номером телефону вже є у проєкті',
    companyRequired: "Поле компанія є обов'язковим",
  },

  bulkSaveErrors: {
    invalidEmail: 'Недійсна електронна адреса',
    invalidPhone: 'Недійсний номер телефону',
    duplicateRow: 'Дублює попередній рядок у цьому пакеті',
    missingName: "Ім'я та прізвище є обов'язковими",
    identifierConflict:
      'Електронна адреса та телефон належать різним наявним користувачам',
    authDenied: 'Реєстрацію відхилено службою автентифікації',
    authUnavailable: 'Служба автентифікації недоступна — спробуйте пізніше',
    rowProcessingFailed:
      'Не вдалося обробити рядок — зверніться до підтримки, якщо це повториться',
    rowError: (row: number | string, message: string) =>
      `Рядок ${row}: ${message}`,
    namedRowError: (name: string, message: string) => `${name}: ${message}`,
    andXMoreRows: (x: number) =>
      `…і ще ${x} ${ending(x, 'рядок', 'рядки', 'рядків')}.`,
    xRowsFailedToSave: (x: number) =>
      `Не вдалося зберегти ${x} ${ending(x, 'рядок', 'рядки', 'рядків')}`,
    unknownError: 'Невідома помилка',
    xMembersImported: (x: number) => `Імпортовано учасників: ${x}`,
  },

  companyValidationErrors: {
    nameRequired: "Поле з ім'ям компанії є обов'язковим",
    emailRequired: "Електронна адреса компанії є обов'язковим",
    invalidEmail: 'Електронна адреса компанії недійсна',
    invalidPhone: 'Номер телефону не дійсний',
    emailIsTaken: 'Інша компанія має таку електронну адресу',
    somethingFilledIncorrectly: 'Щось заповнено неправильно',
    companyTypeIsRequired: "Тип компанії є обов'язковим",
    codeRequired: "Код компанії є обов'язковим",
    codeIncorrectLength: 'Довжина коду має бути між 3 і 5 символами',
    codeIsNotUnique:
      'Компанія з таким кодом вже існує, поміняйте код, щоб зробити його унікальним',
  },
  companyLinkingErrors: {
    anErrorOccurredWhileLinking: 'Під час зв’язування сталася помилка',
    anErrorOccurredWhileUnlinking: 'Під час від’єднання сталася помилка',
    anErrorOccurredWhileFetchingGlobalStorage:
      'Під час отримання даних з глобального сховища сталася помилка',
  },
  companyMerging: {
    confirmation: (sourceCompaniesText: string, targetCompanyText: string) =>
      `Ви підтверджуєте злиття ${sourceCompaniesText} із ${targetCompanyText}?`,
  },

  somethingWentWrongDuringAPIInteraction:
    'Щось пішло не так під час взаємодії з API',

  signUpLinkInvalid:
    'Це посилання для реєстрації неповне. Будь ласка, скористайтеся QR-кодом із запрошення до проєкту.',

  confirmDeactivationOfTheFollowingXCompanies: (x: number) =>
    `Підтвердити деактивацію наступн${ending(x, 'ої', 'их', 'их')} ` +
    `${x} компан${ending(x, 'ії', 'ій', 'ій')}?`,
  confirmActivationOfTheFollowingXCompanies: (x: number) =>
    `Підтвердити активацію наступн${ending(x, 'ої', 'их', 'их')} ` +
    `${x} компан${ending(x, 'ії', 'ій', 'ій')}?`,
  confirmDeletionOfTheFollowingXProjectMembers: (x: number) =>
    `Підтвердити видалення наступн${ending(x, 'ого', 'их', 'их')} ` +
    `${x} член${ending(x, 'у', 'ів', 'ів')} команди?`,
  confirmInvitationOfTheFollowingXProjectMembers: (x: number) =>
    `Підтвердити запрошення для наступн${ending(x, 'ого', 'их', 'их')} ` +
    `${x} член${ending(x, 'у', 'ів', 'ів')} команди?`,
  confirmSettingAsNotInvitedOfTheFollowingXProjectMembers: (x: number) =>
    `Підтвердити відкликання запрошення для ` +
    `наступн${ending(x, 'ого', 'их', 'их')} ` +
    `${x} член${ending(x, 'у', 'ів', 'ів')} команди?`,
  confirmInvitation: 'Підтвердити запрошення',
  confirmSettingUsersAsNotInvited: 'Підтвердити відкликання запрошення',
  confirmDeletion: 'Підтвердити видалення',
  confirmActivation: 'Підтвердити активацію',
  confirmDeactivation: 'Підтвердити деактивацію',
  confirmDeletionOfTheFollowingXPlannedManpower: (x: number) =>
    `Підтвердити видалення наступн${ending(x, 'ої', 'их', 'их')} ` +
    `${x} одиниц${ending(x, 'і', 'ь', 'ь')} робочої сили?`,
  confirmDeletionOfTheFollowingXBidPackages: (x: number) =>
    `Підтвердити видалення наступн${ending(x, 'ого', 'их', 'их')} ` +
    `${x} бід пакет${ending(x, 'у', 'ів', 'ів')}?`,
  confirmDeletionOfActivities: (x: number) =>
    `Підтвердити видалення ${x} обран${ending(x, 'ої', 'их', 'их')} роб${ending(
      x,
      'оти',
      'іт',
      'іт',
    )}?`,
  confirmDeletionOfEquipment: (x: number) =>
    `Ви впевнені, що хочете видалити вибране обладнання?\n\n${x} ${equipment(
      x,
    )} буд${ending(x, 'е', 'уть', 'уть')} видален${ending(x, 'о', 'о', 'о')}.`,
  confirmDeletionOfSensors: (x: number) =>
    `Ви впевнені, що хочете видалити вибрані датчики?\n\n${x} датчик${ending(
      x,
      '',
      'ів',
      'и',
    )} буд${ending(x, 'е', 'уть', 'уть')} видален${ending(x, 'о', 'о', 'о')}.`,
  confirmDeletionOfLocations: (x: number) =>
    `Ви впевнені, що хочете видалити вибрані локації?\n\n${x} локац${ending(
      x,
      'ія',
      'ії',
      'ій',
    )} буд${ending(x, 'е', 'уть', 'уть')} видален${ending(x, 'о', 'о', 'о')}.`,
  confirmDeletionOfStruxTags: (x: number) =>
    `Ви впевнені, що хочете видалити вибрані StruxTags?\n\n${x} ${struxTagsCount(
      x,
    )} буд${ending(x, 'е', 'уть', 'уть')} видален${ending(x, 'о', 'о', 'о')}.`,
  confirmDeletionOfMaterials: (x: number) =>
    `Ви впевнені, що хочете видалити вибрані матеріали?\n\n${x} ${materials(
      x,
    )} буд${ending(x, 'е', 'уть', 'уть')} видален${ending(x, 'о', 'о', 'о')}.`,
  confirmDeletionOfResources: (x: number, countLabel: string) =>
    `Ви впевнені, що хочете видалити вибрані ${countLabel}?\n\n${x} ${countLabel} буд${ending(
      x,
      'е',
      'уть',
      'уть',
    )} видален${ending(x, 'о', 'о', 'о')}.`,
  resourceCountLabel: (resourceTypeId: string, x: number) => {
    switch (resourceTypeId) {
      case 'offloading_equipment':
        return equipment(x)
      case 'sensor':
        return sensors(x)
      case 'material':
        return materials(x)
      default:
        return `елемент${ending(x, '', 'и', 'ів')}`
    }
  },
  confirmApprovalOfTheFollowingXProjectMembers: (x: number) =>
    `Підтвердити затвердження наступних ${x} запитів у ${brandName}?`,
  confirmApproval: 'Підтвердити затвердження',
  selectProcoreProject: 'Вибрати проєкт з Procore',
  selectProcoreCompany: 'Вибрати компанію з Procore',
  noUnit: 'Жодної одиниці виміру',
  noCompanies: 'Жодної компанії',
  noDirectoryAccess: 'Без доступу до списка користувачів',
  xUsers,
  xUsersShowing: (x: number) =>
    `${x} користувач${ending(x, '', 'і', 'ів')} показано`,
  xUsersCount: (x: number) => `${x} Користувач${ending(x, '', 'і', 'ів')}`,
  xTeams: (x: number) => `${x} команд${ending(x, 'а', 'и', '')}`,
  addXUsers: (x: number) => {
    if (!x) {
      return 'Додати'
    }
    // 'Add 1 user' or 'Add x users'
    return `Додати ${x} користувач${ending(x, 'а', 'ів', 'ів')}`
  },
  xFiles,
  xFields,
  xFieldsAdded,

  files: 'Файли',
  multiLevelScreen: 'Мапи всіх поверхів',
  levelsViewsWithZoneSummary: 'Мапа поверха із резюме зони',
  levelsViewsWithCompanySummary: 'Мапа поверха із резюме компанії',
  gantt: 'Гантт',
  presentationMode: 'Режим презентації',
  play: 'Відтворити',
  stop: 'Зупинити',
  turnOn: 'Увімкнути',
  presentationScreens: 'Сторінки презентації',
  xMinutes: (x: number) => `${x} хвилин${ending(x, 'у', 'и', '')}`,
  order_noun: 'Порядковий номер',
  order_number: (x: number) => `${x}ий`,
  display: 'Показувати',
  presentationModeDescriptions: {
    multiLevelScreen:
      'Показує хід виконання робіт та продуктивність робочої сили порівнюючи ' +
      'фактичні дані із запланованими для всіх поверхів будівлі. ' +
      'Прокручує сторінку, щоб показати всі поверхи.',
    levelsViewsWithZoneSummary:
      'Проходить через кожен поверх будівлі, ' +
      'показуючи хід виконання робіт та продуктивність робочої сили для кожної зони. ' +
      'Прокручує сторінку, щоб показати дані для кожної зони.',
    levelsViewsWithCompanySummary:
      'Проходить через кожен поверх будівлі, ' +
      'показуючи сукупну продуктивність компанії. ' +
      'Прокручує сторінку, щоб показати дані для кожної компанії.',
    gantt:
      'Показує гантт діаграму для поточного періоду. ' +
      'Прокручує сторінку, щоб показати всі роботи.',
    deliveries: 'Показує тижневий календар доставок для поточного тижня.',
    logistics: 'Показує мапу логістики',
    confirmText:
      'Режим презентації вийде із системи та встановить цей пристрій в режим відтворення. ' +
      'Щоб вийти з режиму презентації, перейдіть у налаштування. ' +
      'Вам знадобиться увійти в систему повторно після того, як завершиться режим презентації.',
    header:
      'Увімкнення режиму презентації здійснить вихід із системи, ' +
      'і запустить цей пристрій у режимі тільки для огляду',
    periodOfInactivity:
      'Презентація запуститься автоматично після періоду бездіяльності в',
    stop:
      'Припинення режиму презентації здійснить вихід ' +
      'до сторінки входу, де Ви зможете увійти у Ваші проєкти.',
  },

  include: 'Включати',
  saveConfiguration: 'Зберігти конфігурацію',
  preTaskChecklist: 'Контрольний список дій передуючих завданню',
  requireDailyInspections: 'Потребувати щоденної перевірки',
  inspectionFrequency: 'Періодичність перевірок',
  deadLineForRecordingAnInspection: 'Кінцевий термін запису інспекції',
  checklist: 'Контрольний список',
  instructions: 'Інструкції',
  xOptions: (x: number) => `${x} опці${ending(x, 'я', 'ії', 'ій')}`,
  NA: 'Не застосовується',
  remove: 'Видалити',
  typeOfList: 'Тип списку',
  addAdditionalItem: 'Додати елемент',
  enterInstruction: 'Введіть інструкцію',
  enterTheXDigitCodeSentTo: () => `Введіть цифровий код, на який надіслано на`,
  areYouSureWantToDeleteInstructionItem:
    'Ви впевнені, що хочете видалити елемент інструкції?',
  instructionListTypes: {
    enumerated: 'Нумерований',
    bulleted: 'Маркований',
    none: 'Ні той, ні інший',
  },
  fieldStates: {
    mandatory: 'Обов’язкове',
    optional: 'Необов’язкове',
    hidden: 'Приховане',
  },

  revertingProjectData: 'Повернення до попередніх даних проєкта',
  loadingUpdatingProject: 'Завантажується/Оновлюється проєкт',
  downloadProjectDataCsv: 'Завантажити дані проєкта у .csv',
  exploreYourScheduleInStruxHub: `Переглянути Ваш розклад у ${brandName}`,
  scheduleIsNotUploaded: 'Розклад не завантажено',
  gettingXerProjects: 'Отримання проєктів',
  gettingExcelSheets: 'Отримання аркушів excel',
  parsingTheScheduleFileStarted: 'Обробку файла розкладу розпочато',
  readingScheduleFile: 'Читаємо файл розкладу',
  extractingScheduleData: 'Витягуємо дані розкладу',
  xEntriesInTheFile: (x: number) =>
    `${x} запис${ending(x, '', 'и', 'ів')} у файлі`,
  savingScheduleDataCollections: 'Збереження колекцій даних розкладу',
  xOfYEntriesSaved: (x: number, y: number) =>
    `${x} з ${y} запис${ending(y, 'а', 'ів', 'ів')} збережено`,
  recalculatingStatusUpdates: 'Перераховуємо статуси робіт',
  savingSchedule: 'Збереження розкладу',
  uploadingScheduleFailedAt:
    'Завантаження розкладу завершилося невдало на кроці',
  uploadingScheduleDone: 'Завантаження розкладу завершено',
  seeGanttInStruxHub: `Дивитися Гантт у ${brandName}`,
  noActivitiesFound: 'Жодних робіт не знайдено',
  noPropertiesFoundForForm: 'Не знайдено властивостей для цієї форми',
  noPropertiesMatchSearch: 'Немає властивостей, що відповідають пошуку',
  continueToNextStep: 'Продовжити до наступного кроку',
  thereAreNoProjectsInProvidedFile: 'У наданому файлі немає проєктів',
  thereAreNoInstancesToDisplay: (instancesName: string = 'Екземплярів') =>
    `Немає ${instancesName.toLowerCase()} для відображення`,
  scheduleUpdateSummary: 'Резюме оновлення розкладу',
  current: 'На даний момент',
  upload_noun: 'Завантаження',
  fileName: 'Назва файла',
  dateUploaded: 'Дата завантаження',
  uploadScheduleDescriptions: {
    warning: (delta: number) =>
      `${delta} Роб${ending(delta, 'ота', 'оти', 'іт')}, ` +
      `як${ending(delta, 'а', 'і', 'і')} ` +
      `відсутн${ending(delta, 'а', 'і', 'і')} ` +
      `в оновленому графіку, буде видалено. Усе, що пов'язано ` +
      `із ц${ending(delta, 'ією', 'ими', 'ими')} ${delta} ` +
      `Роб${ending(delta, 'отою', 'отами', 'отами')}, ` +
      `буде також видалено із ${brandName}.`,

    chooseProject:
      'У цьому P6 xer файлі знаходяться дані з декількох проєктів. ' +
      'Будь ласка оберіть проєкт для імпортування. Дані цього проєкта мають ' +
      'пасувати до тих даних, що містяться в інших джерелах ' +
      '(напр. План закупівель, Список членів команди).',

    chooseSheet:
      'Вибраний файл містить декілька листів. ' +
      'Будь ласка оберіть лист для імпорту.',

    mergeData: 'Оновити поточний розклад.',

    mapFields:
      'Будь ласка виберіть, які поля будуть використовуватися для кожного стовпчика',
  },
  activityId: 'ID роботи',
  activityUniqueId: 'Унікальний ID роботи',
  activityName: 'Назва роботи',
  activityCode: 'Код роботи',
  activityStatus: 'Статус роботи',
  activityPercentComplete: 'Відсоток виконання роботи',
  plannedActivityStart: 'Запланований початок роботи',
  plannedActivityFinish: 'Запланований кінець роботи',
  actualActivityStart: 'Актуальний початок роботи',
  actualActivityFinish: 'Актуальний кінець роботи',
  resourceId: 'ID ресурса',
  resourceName: 'Назва ресурса',
  trade: 'Торгівля',
  assignee: 'Призначена особа',
  primarySafetyHazard: 'Первинна загроза безпеки',
  secondarySafetyHazard: 'Вторинна загроза безпеки',
  categoryOfVariance: 'Категорія варіації',
  categoryOfVarianceOpened: 'Категорію варіації відкрито',
  activityCodeTypes: 'Типи кодів робіт',
  resources: 'Ресурси',
  uploadAnother: 'Завантажити інший',
  next: 'Далі',
  saveAndViewUpdatedGantt: 'Зберегти та переглянути оновлений Гантт',
  lastUpload: 'Востаннє завантажено',
  companies: 'Компанії',
  targetCompany: 'Цільова компанія',
  activitiesWithTheFollowingActivityCodesAreExcluded:
    'Роботи з наступними кодами робіт виключені',
  activityCodesSelected: 'Вибрано коди робіт',
  scheduleRestoringConfirmation: 'Підтверження відновлення розкладу',
  doYouConfirmRestoringFileFromDate: (file: string, wrappedDate) => [
    `Чи Ви підтверджуєте повернення ${file} від `,
    wrappedDate,
    '?',
  ],
  errorSendingOneTimeCode:
    'Помилка відправлення SMS. Будь ласка, спробуйте ще раз. Якщо проблема залишається, зверніться до адміністратора.',
  noNonsenseInOneTimeCodeResponse:
    'Номер телефону не знайдено. Будь ласка, введіть свій номер телефону з кодом міста.',
  redirectingYouTo: (x: string) => `Перенаправлення на ${x}`,
  doYouWantToSaveChanges: 'Ви хочете зберегти зміни?',
  doYouAlreadyHaveAnAccount: 'У вас вже є обліковий запис?',
  uploadHistory: 'Історія завантажень',
  restore: 'Відновити',
  chooseTenant: 'Оберіть орендаря',
  chooseAnotherTenant: 'Оберіть іншого орендаря',
  unableToReachTenant: 'Не вдається підключитися до орендаря',
  couldNotOpenTenant: (tenant: string) =>
    `Не вдалося відкрити ${tenant}. Можливо, у вас немає доступу або час очікування запиту минув. Оберіть іншого орендаря або вийдіть і спробуйте ще раз.`,
  theSelectedTenant: 'обраного орендаря',
  chooseProject: 'Оберіть проєкт',
  chooseSheet: 'Оберіть лист',
  mapFields: 'Поставити відповідність полів',
  xActivities: (x: number) => `${x} Роб${ending(x, 'ота', 'оти', 'іт')}`,
  xHours,
  locationsWithDeletedParent: (x: number): string =>
    `${x} локац${ending(x, 'ія', 'ії', 'ій')} без батьківського елемента LBS`,
  locationsWithDeletedParentDescription: (x: number): string =>
    x === 1
      ? 'Її батьківський елемент LBS видалено до жовтня 2026 р., тому вона не відображається на бічній панелі карт. Перегляньте її, щоб видалити або призначити новий батьківський елемент.'
      : 'Їхній батьківський елемент LBS видалено до жовтня 2026 р., тому вони не відображаються на бічній панелі карт. Перегляньте їх, щоб видалити або призначити новий батьківський елемент кожній.',
  reviewLocations: (x: number): string =>
    `Переглянути ${x} локац${ending(x, 'ію', 'ії', 'ій')}`,
  shownInTableBelow: 'Показано в таблиці нижче',
  xLocations: (x: number) => `${x} Локац${ending(x, 'ія', 'ії', 'ій')}`,
  xObjectsHere: (x: number) => `${x} об'єкт${ending(x, '', 'и', 'ів')} тут`,
  xObjectsAtThisSpot: (x: number, names: string[]) =>
    `${x} об'єкт${ending(x, '', 'и', 'ів')} у цьому місці: ${names.join(', ')}`,
  workflowsHereX: (x: number) => `Робочі процеси тут (${x})`,
  xAttachments: (x: number) => `${x} Додат${ending(x, 'ок', 'ки', 'ів')}`,
  xColumns: (x: number) => `${x} стовпчик${ending(x, '', 'и', 'ів')}`,
  cancelUpload: 'Відмінити завантаження',
  selectProjectMembersLinkedToX: (x: string, isPlural: boolean) =>
    `Виберіть член${isPlural ? 'ів' : 'а'} команди, ` +
    `як${isPlural ? 'і' : 'ий'} належ${
      isPlural ? 'а' : 'и'
    }ть до категорії ${x}`,
  selectActivityCodesLinkedToX: (x: string, isPlural: boolean) =>
    `Виберіть код${isPlural ? 'и' : ''} роб${isPlural ? 'іт' : 'оти'}, ` +
    `як${isPlural ? 'і' : 'ий'} належ${
      isPlural ? 'а' : 'и'
    }ть до категорії ${x}`,
  selectionOf1ProjectMemberIsPermitted: `Дозволяється вибрати 1 члена команди.`,
  selectionOf1ActivityCodeIsPermitted: `Дозволяється вибрати 1 код роботи.`,
  multipleSelectionsArePermitted: `Дозволяється множинний вибір.`,
  excludeSectionsOfTheScheduleOptional: `Виключити розділи розкладу (необов'язково)`,
  activityFilterDialogDescriptions: {
    hiddenCodes:
      'Якщо Ви бажаєте приховати деякі розділи розкладу, ' +
      'відмітьте коди робіт, які відповідають роботам, які ви бажаєте приховати.',
  },
  only: 'Тільки це',
  noCompany: 'Без компанії',
  noMessage: 'Без повідомлення',
  submit_verb: 'Затвердити',
  showAllXActivities: (x: number) =>
    `Показати всі ${x} роб${ending(x, 'оту', 'оти', 'іт')}`,
  showXOfYActivities: (x: number, y: number) =>
    `Показати ${x} з ${y} роб${ending(y, 'оти', 'іт', 'іт')}`,
  verification: 'Перевірка',
  xActivitiesAdded: (x: number, wrappedX) => [
    wrappedX,
    ` роб${ending(x, 'ота', 'оти', 'іт')} додано.`,
  ],
  seeDetails: 'Переглянути деталі',
  xTypeActivityCodesAdded: (x: number, wrappedX, type: string) => [
    wrappedX,
    ` код${ending(x, '', 'и', 'ів')} ` +
      `роб${ending(x, 'оти', 'іт', 'іт')} типу ${type} додано.`,
  ],

  activityFilters: 'Фільтри робіт',
  scheduleFilters: 'Фільтри розкладу',
  customFilter: 'Фільтр користувача',
  filterNameCannotBeEmpty: 'Назва фільтра не може бути порожньою',
  suchFilterNameIsAlreadyTaken: 'Фільтр з такою назвою вже є',
  struxHubData: `Дані зі ${brandName}`,
  scheduleData: 'Дані з розкладу',
  members: 'Члени',
  projectMembers: 'Члени проєкта',
  activityCodes: 'Коди робіт',
  projectMembersSelected: 'Вибрано членів проєкта',
  renameFilterOptional: "Переіменувати фільтр (необов'язково)",
  filterName: 'Назва фільтра',
  xCompaniesIdentified: (x: number) =>
    `${x} Компані${ending(x, 'ю', 'ї', 'й')} ідентифіковано`,
  xCompaniesShowing: (x: number) =>
    `${x} Компані${ending(x, 'ю', 'ї', 'й')} показано`,
  xCompaniesCount: (x: number) => `${x} Компані${ending(x, 'я', 'ї', 'й')}`,
  xCompanies: (x: number) => `${x} компані${ending(x, 'ю', 'ї', 'й')}`,
  activityCodeSelected: 'Вибрано код роботи',
  responsibleUser: 'Відповідальна Особа',
  responsibilityCode: 'Код відповідальності',
  responsibilityDescription: 'Опис відповідальності',
  contractN: 'Контракт #',
  plannedValueS: 'Запланована вартість ($)',
  uploadAnUpdatedXlsxFile: 'Завантажити оновлений файл xlsx',
  downloadCurrentListAsXlsxFile: 'Отримати поточний список у файлі xlsx',
  addNewEntry: 'Додати новий запис',
  addNewUser: 'Додати нового користувача',
  newUserCreatedViaSignUp: 'Реєстрація нового користувача',
  newUserAwaitingApproval: 'Очікує затвердження',
  previous: 'Попередньо',
  activityFiltersDescriptions: {
    confirmDeletionOfX: (x: string) =>
      `Ви упевнені, що бажаєте видалити фільтр ${x}?`,
    title:
      `${brandName} трансформує Ваші коди робіт та дані P6 у зручні ` +
      'фільтри для Ваших команд, що дає змогу легко знаходити свої роботи ' +
      'у веб та мобільному сайтах. Ви можете налаштувати ці фільтри нижче ' +
      'та оновлювати їх будь-якої миті.',
    selectProjectMembers:
      'Виберіть члена(ів) команди, якого(их) Ви бажаєте ' +
      'додати в цей фільтр. Дозволяється множинний вибір.',
    selectActivityCodes:
      'Виберіть код(и) роботи(іт), якого(их) Ви бажаєте ' +
      'додати в цей фільтр. Дозволяється множинний вибір.',
    company:
      'Цей фільтр дозволяє користувачам вибирати ' +
      'компанії, які відповідають за роботи у Вашому проєкті.',
    level:
      'Цей фільтр дозволяє користувачам вибирати ' + 'поверхи Вашої будівлі.',
    zone:
      'Цей фільтр дозволяє користувачам вибирати ' +
      'різноманітні зони кожного поверха будівлі.',
    activityCode:
      'Цей фільтр дозволяє користувачам вибирати ' +
      'додаткові коди робіт, які ви бажаєте відслідковувати.',
    projectMember:
      'Цей фільтр дозволяє користувачам вибирати ' +
      'членів команди, яких ви бажаєте відслідковувати.',
    associateCompanies:
      'Будь ласка, вкажіть, як саме Ви асоціюєте компанії ' +
      'з Вашими роботами у розкладі.',
    resourceCompanies:
      'Назви компаній вказані у полі Ресурс у .xer файлі. ' +
      'Зауважте: це є значенням за замовчуванням, якщо Ваш розклад містить Ресурси.',
    bpCompanies:
      'Назви компаній вказані в окремому файлі xlsx та ' +
      'асоціюються з роботами за допомогою кодів робіт.',
    bpStep1:
      'Виберіть код роботи у Вашому файлі .xer, ' +
      'за яким слід асоціювати відповідальні компанії.',
    bpStep2:
      'Завантажте файл excel, який містить щонайменше 2 колонки ' +
      'Код відповідальності та Компанія.',
  },

  trackingHierarchy: 'Ієрархія у трекері',
  defaultHierarchies: 'Ієрархії за замовчуванням',
  defineBandsEnPermissionsForYourTrackingHierarchy:
    'Визначте ланки та дозволи для Вашої ієрархії у трекері',
  setupDefaultHierarchiesForDifferentViews:
    'Налаштувати ієрархії за замовчуванням для різних виглядів',
  selectBands: 'Вибрати ланки',
  selectControlBand: 'Вибрати управляючу ланку',
  band: 'Ланка',
  addBand: 'Додати ланку',
  activityUpdatesOn: 'Оновлення робіт увімкнено',
  activityUpdatesOff: 'Оновлення робіт вимкнено',
  view_noun: 'Вигляд',
  wbs: 'ІСР', // ієрархічна структура робіт
  webGantt: 'Гантт на вебі',
  webSitemap: 'Мапа на вебі',
  mobile: 'Мобільний',
  table: 'Таблиця',
  tablet: 'Планшет',
  hierarchyPreview: 'Попередній перегляд ієрархії',
  unassigned: 'Непризначене',
  photo: 'Фото',
  completed: 'Завершено',
  startEarly: 'Розпочати раніше',
  PComplete: '% Виконання',
  unassignedActivity: 'Непризначена робота',
  trackingHierarchyDescriptions: {
    title:
      'Налаштуйте Вашу ієрархію трекера нижче, щоб розпочати відслідковувати Ваш розклад ' +
      'Вашою командою з будмайданчика, Вашими субпідрядниками або і тими, і тими.',
  },
  editCompany: 'Редагувати компанію',
  addDataToSchedule: 'Додати дані до розкладу',
  plannedCompanyManpower: 'Запланована робоча сила компанії',
  dataToScheduleDescriptions: {
    title:
      `За допомогою ${brandName} Ви Маєте можливість швидко додавати дані ` +
      'до Вашого розкладу, імпортуючи їх з файлів excel',
  },

  formField: 'Поле форми',
  showField_question: 'Показати поле?',
  mandatory_question: "Обов'язкове?",
  fieldShown: 'Поле показано',
  mandatory: "Обов'язкове",
  addNewListElement: 'Додати новий елемент до списка',
  workflowPreview: 'Попередній перегляд робочого процесу',

  setThresholdForDDays: 'Задати поріг для ∆ Днів',
  setThresholdForDManpower: 'Задати поріг для ∆ Робочої сили',
  onTrack: 'За планом',
  watch: 'Увага',
  behind: 'Позаду',
  dDays: '∆ Днів',
  days: 'Дні',
  percent: 'Відсоток',
  number: 'Число',
  daysBehind: 'Днів позаду',
  daysAhead: 'Днів попереду',
  XBehind: '% позаду',
  XAhead: '% попереду',
  under: 'Нижче',
  over: 'Вище',
  XUnder: '% Нижче',
  XOver: '% Вище',
  dManpower: '∆ Робочої сили',
  sitemapsDescriptions: {
    title:
      `За допомогою ${brandName} Ви маєте можливість легко з'єднати ` +
      'Ваш розклад з Вашими мапами. ' +
      'Нижче Ви можете налаштувати порогові значення для метрик ∆ Днів та ' +
      '∆ Робочої сили, які використовуються для візуалізації прогресу у розкладі ' +
      'та поточній робочій силі відповідно.',
    configure: 'Налаштовуйте порогові значення для Розкладу і робочої сили',
  },

  inbox: 'Вхідні',
  unread_2form: 'Непрочитане',
  read_2form: 'Прочитане',
  readStatus: 'Статус сповіщення',
  archived: 'Архівоване',
  xArchived: (x: number) => `${x} архівовано`,
  xMarkedAsRead: (x: number) => `${x} позначено як прочитане`,
  xMarkedAsUnread: (x: number) => `${x} позначено як непрочитане`,
  xUnarchived: (x: number) => `${x} розархівовано`,
  requested: 'Запитане',
  changed: 'Змінене',
  scheduled: 'Заплановане',
  onSite: 'На місці',
  denied: 'Відхилено',
  passedInspection: 'Перевірку пройдено',
  failedInspection: 'Перевірку не пройдено',
  done_plural: 'Виконані',
  flags: 'Прапори',
  workflowSubmitted: 'Робочий процес подано',
  workflowApproved: 'Робочий процес затверджено',
  workflowDone: 'Робочий процес завершено',
  workflowChanged: 'Робочий процес змінено',
  workflowDenied: 'Робочий процес відхилено',
  workflowReadyToInspect: 'Робочий процес готовий до перевірки',
  workflowActivated: 'Робочий процес активовано',
  workflowReviewed: 'Робочий процес переглянуто',
  workflowOnSite: 'Робочий процес на місці',
  workflowFailed: 'Робочий процес провалено',
  workflowPassed: 'Робочий процес пройшов',
  workflowDeleted: 'Робочий процес видалено',
  workflowEnded: 'Робочий процес завершено',
  workflowFollowed: 'Робочий процес відслідковується',
  rfis: 'Запити інформації',
  scheduleComments: 'Коментарі щодо розкладу',
  onTime: 'За розкладом',
  late: 'Із запізненням',
  performance: 'Продуктивність',
  parameters: 'Параметри',
  onTimeWindowHr: 'Вікно за розкладом (год)',
  showLess: 'Показати менше',
  showMore: 'Показати більше',
  all_items: 'Все',
  my_documents: 'Мої документи',
  my_company: 'Моя компанія',
  maps: 'Мапи',
  none: 'Нічого',
  all: 'Усі',
  important: 'Важливі',
  importantTabTooltip:
    'Сповіщення, що потребують вашої уваги або дії — включає ball-in-court, комунікацію на майданчику та @-згадки.',
  updates: 'Оновлення',
  updatesTabTooltip:
    'Оновлення робочого процесу, не позначені як Важливі — лише для інформації.',
  batteryLevel: 'Рівень заряду',
  struxTagState: 'Стан StruxTag',
  sensorStateInUseMoving: 'Рухається',
  sensorStateInUseIdle: 'Без руху',
  sensorStateNoSignal: 'Немає сигналу',
  noStruxTagLinked: 'Немає пов’язаного StruxTag',
  noActivitySessions: 'Немає сеансів активності',
  activitySessionMoving: 'Рухається',
  activitySessionIdle: 'Без руху',
  activitySessionOpen: 'Відкрито',
  activitySessionClosed: 'Закрито',
  unArchive: 'Розархівувати',
  archive: 'Заархівувати',
  leaveConversation: 'Покинути розмову',
  archiveConversation: 'Заархівувати розмову',
  unarchiveConversation: 'Розархівувати розмову',
  conversationIsArchived: 'Цю розмову заархівовано',
  photoMessage: '📷 Фото',
  thisWorkflowIsInProject: (projectName: string) =>
    `Цей робочий процес знаходиться в проєкті ${projectName}`,
  leaveConversationConfirmation:
    'Ви впевнені, що хочете покинути цю розмову? Ви втратите до неї доступ.',
  archiveConversationConfirmation:
    'Ви впевнені, що хочете заархівувати цю розмову? Її буде приховано для всіх учасників і зроблено доступною лише для читання.',
  conversation: 'Розмова',
  reconnecting: 'Повторне підключення…',
  noMessagesYet: 'Повідомлень поки немає',
  noMessagesYetSayHello: 'Повідомлень поки немає — привітайтеся…',
  couldNotLoadYourInbox: 'Не вдалося завантажити вхідні',
  couldNotLoadMessages: 'Не вдалося завантажити повідомлення',
  couldNotOpenConversation: "Не вдалося відкрити розмову — перевірте з'єднання",
  noArchivedConversations: 'Немає заархівованих розмов',
  noConversationsFound: 'Розмов не знайдено',
  inboxIsEmpty: 'Вхідні порожні',
  xMembersYOnline: (members: number, online: number) =>
    `${members} учасників, ${online} онлайн`,
  xMembers: (x: number) => `${x} учасник${ending(x, '', 'и', 'ів')}`,
  failedToLeave: 'Не вдалося покинути',
  leave_verb: 'Покинути',
  message_noun: 'Повідомлення',
  youCanAttachUpToXPhotos: (x: number) =>
    `До повідомлення можна прикріпити до ${x} фото.`,
  xFilesCouldNotBeAttached: (x: number) =>
    x > 1
      ? `Не вдалося прикріпити файлів: ${x} — лише зображення до 15 МБ.`
      : `Не вдалося прикріпити 1 файл — лише зображення до 15 МБ.`,
  notSentTapToRetry: 'Не надіслано — торкніться, щоб повторити',
  newChat: 'Новий чат',
  searchUser: 'Пошук користувача',
  newGroup: 'Нова група',
  noPeopleFound: 'Нікого не знайдено',
  addMembers: 'Додати учасників',
  groupDetails: 'Деталі групи',
  userDetails: 'Деталі користувача',
  groupName: 'Назва групи',
  createdByX: (name: string) => `Створено: ${name}`,
  you: 'Ви',
  couldNotCreateGroup: 'Не вдалося створити групу — спробуйте ще раз',
  couldNotUpdateGroup: 'Не вдалося оновити групу — спробуйте ще раз',
  read: 'Прочитати',
  unread: 'Позначити непрочитаним',
  wwpColoring: 'Забарвлення ТРБ', // тижневого робочого плану
  useCSIMaterialCategories: 'Використовувати категорії матеріалів від ІБС', // Інститут будівельних специфікацій
  noNotificationsPassedFilters: 'Жодних сповіщень за вказаними фільтрами',
  replyToActivityFlag: 'Відповісти на прапор',
  activityFlag: 'Робочий прапор',
  replyToActivityRfi: 'Відповісти на Запит інформації',
  activityRfi: 'Робочий запит інформації',
  replyToScheduleComment: 'Відповісти на коментар щодо розкладу',
  replyToCategoryOfVariance: 'Відповісти на Категорію варіації',
  replyToSafetyHazard: 'Відповісти на Загрозу безпеки',
  scheduleComment: 'Коментар щодо розкладу',
  activityUpdate: 'Оновлення роботи',
  scheduleUpdate: 'Оновлення розкладу',
  safetyHazard: 'Загроза безпеки',
  safetyHazardOpened: 'Загрозу безпеки відкрито',
  bulk: 'Масово',
  bulkEdit: 'Масове редагування',
  bulkUpdate: 'Масове оновлення',
  bulkEditConfirmQuestion: (fieldCount: number, recordCount: number) =>
    `Оновити ${xFields(fieldCount)} для ${recordCount} запис${ending(
      recordCount,
      '',
      'и',
      'ів',
    )}?`,
  replyToActivityUpdate: 'Відповісти на оновлення роботи',
  replyToScheduleUpdate: 'Відповісти на коментар щодо розкладу',
  replyToBulkUpdate: 'Відповісти на масове оновлення',
  activityFollowed: 'Робота відслідковується',
  deliveryFollowed: 'Доставка відслідковується',
  deliveryRequested: 'Додано запит на доставку',
  deliveryRequestedByLocationClosure: (id: string) =>
    `На доставку (${id}) вплинуло закриття локації, тому її потрібно перенести`,
  deliveryScheduled: 'Доставка додана до розкладу',
  deliveryOnSite: 'Доставка на місці',
  deliveryPassedInspection: 'Доставка пройшла перевірку',
  deliveryFailedInspection: 'Доставка не пройшла перевірку',
  deliveryInspected: 'Доставка перевірена',
  deliveryDone: 'Доставка виконана',
  deliveryIncompletelyDone: 'Доставка неповністю виконана',
  deliveryUpdated: 'Доставка оновлена',
  deliveryDeleted: 'Доставка видалена',
  deliveryDeletedBy: (id: string, user: string, company: string) =>
    `Доставка (${id}) видалена by ${user} at ${company}`,
  deliveryDenied: 'Доставка відхилена',
  deliveryCanceled: 'Доставка скасована',
  deliveryOnHold: 'Доставка відкладена',
  deliveryPaused: 'Доставку призупинено',
  updateMaterials: 'Оновити матеріали',
  materialsLoading: 'Завантажуються матеріали',
  xMaterials: (count: number) =>
    `${count} Матеріал${ending(count, '', 'и', 'ів')}`,
  xFilesShowing: (count: number) =>
    `${count} Файл${ending(count, '', 'и', 'ів')} показано`,
  planned: 'Заплановане',
  actual: 'Фактичне',
  scannerDeactivated: 'Сканер деактивовано',
  scheduleUpdated: 'Розклад оновлено',
  passed: 'Пройдено',
  failed: 'Не пройдено',
  scheduling: 'Планування',
  schedule: 'Запланувати',
  schedule_noun: 'Розклад',
  pass_verb: 'Пройти перевірку',
  change_verb: 'Змінити',
  deny_verb: 'Відмінити',
  accept: 'Прийняти',
  fail: 'Відмовити',
  incompletelyDone: 'Неповністю виконано',
  deleted: 'Видалено',
  deactivated: 'Деактивовано',
  activate: 'Активувати',
  deactivate: 'Деактивувати',
  canceled: 'Скасовано',
  siteNotReady: 'Місце не готове',
  materialProblem: 'Проблема з матеріалом',
  laborIssue: 'Питання праці',
  qualityIssue: 'Питання якості',
  clarificationNeeded: 'Потрібні пояснення',
  incorrectSpecification: 'Неточна специфікація',
  materialQuestion: 'Питання матерілу',
  superintendentNeeded: 'потрібен наглядач',
  plusXMore: (x: number) => `+ ${x} ще`,
  submittalInReview: 'Розглядається',
  submittalComplete: 'Розглянуто',
  inFabrication: 'Виготовляється',
  fabricationComplete: 'Виготовлено',
  inStorage: 'На зберіганні',
  installed: 'Встановлено',
  delivery: 'Доставка',
  newRequest: 'Новий запит',
  deleteDelivery: 'Видалити доставку',
  yesDeleteDelivery: 'Так, видалити доставку',
  cancelDelivery: 'Скасувати доставку',
  yesCancelDelivery: 'Так, скасувати доставку',
  changeDate: 'Змінити дату',
  yesMoveTheDate: 'Так, посунути дату',
  requestIsNotValid: 'Запит на доставку не дійсний',
  datePassedDeadline: 'Дата вийшла за кінцевий термін',
  requestAnyway: 'Зробити запит все одно',
  requestAccessTo: 'Запросити доступ до',
  thisRequestHasPassedTheDeadline: 'Цей запит вийшов за кінцевий термін',
  noDeadline: 'Немає кінцевого терміну',
  twentyFourHours: '24 години',
  fortyEightHours: '48 годин',
  seventyTwoHours: '72 години',
  fourDays: '4 дні',
  fiveDays: '5 днів',
  oneWeek: '1 тиждень',
  twoWeeks: '2 тижні',
  oneMonth: '1 місяць',
  twoMonths: '2 місяці',
  threeMonths: '3 місяці',
  sixMonths: '6 місяців',
  twelveMonths: '12 місяців',
  dateAndTime: 'Дата і час',
  startDate: 'Дата початку',
  endDate: 'Дата завершення',
  finishDate: 'Дата закінчення',
  addFinishDate: 'Додати дату закінчення',
  removeFinish: 'Видалити дату закінчення',
  startTime: 'Час початку',
  endTime: 'Час закінчення',
  nextDay: 'наступного дня',
  selectTime: 'Вибрати час',
  location: 'Розташування',
  locations: 'Розташування',
  locationFrom: 'З локації',
  locationTo: 'До локації',
  formType: 'Тип робочого процесу',
  title: 'Заголовок',
  noAvailableTemplates: 'Немає доступних шаблонів',
  editForm: 'Редагувати робочий процес',
  contact: 'Контакт',
  offloadingEquipment: 'Розвантажувальне обладнання',
  vehicles: 'Транспортні засоби',
  material: 'Матеріал',
  materialDeliveryLocation: 'Місце доставки матеріалу',
  materialsCategory: 'Категорія матеріалів',
  inspectionRequired: "Перевірка обов'язкова",
  supplierAndDriver: 'Постачальник & водій',
  anySpecialInstructions: 'Будь-які особливі вказівки?',
  deliverySitemap: 'Мапа доставки',
  gate: 'Ворота',
  area: 'Ділянка',
  fromToDelivery: 'Внутрішня Доставка',
  installationZone: 'Установочна зона',
  deliveryVehicleType: 'Тип транспортного засобу для доставки',
  numberOfVehicles: 'Кількість транспортних засобів',
  numberOfVehicleLicensePlate: 'Номерний знак транспортного засобу',
  equipment: 'Обладнання',
  materialResource: 'матеріальний ресурс',
  struxTags: 'StruxTags',
  registerStruxTag: 'Зареєструвати StruxTag',
  register: 'Зареєструвати',
  registering: 'Реєстрація...',
  infuseId: 'Infuse ID',
  infuseIdIsRequired: "Infuse ID є обов'язковим",
  infuseIdMustNotContainSpaces: 'Infuse ID не повинен містити пробілів',
  failedToRegisterStruxTag: 'Не вдалося зареєструвати StruxTag',
  sharedResources: 'Спільні Ресурси',
  units: 'Одиниці виміру',
  inspector: 'Контролер',
  vendorEmails: 'Електронна адреса продавця',
  driverMobilePhoneNumbers: 'Номер телефону водія',
  specialInstructions: 'Особливі вказівки',
  linkedActivity: "Пов'язана робота",
  customActionButton: 'Кнопка спеціальної дії',
  building: 'Будівля',
  route: 'Маршрут',
  staging: 'Тимчасове сховище',
  interiorPath: 'Внутрішній Шлях',
  interiorDoor: 'Внутрішні Двері',
  bookingDate: 'Дата бронювання',
  requesterCompany: 'Компанія запитувача',
  duration: 'Тривалість',
  onsiteContact: 'Місцевий контакт',
  onsiteContacts: 'Місцевий контакт',
  category: 'Категорія',
  materialNote: 'Примітка матеріалу',
  unnamedMaterial: 'Матеріал без назви',
  unnamedCompany: 'Компанія без назви',
  required: "Обов'язково",
  optional: "Необов'язково",
  multipleSelection: 'Множинний вибір',
  adding: 'Додавання',
  findOrAddNewEntry: 'Знайти або додати новий запис',
  findEntry: 'Знайти запис',
  yes: 'Так',
  no: 'Ні',
  unscheduledDelivery: 'Незапланована доставка',
  uploadASitemapToShowGatesAndAreas:
    'Завантажити мапу, щоб показати Ворота і Ділянки',
  uploadAViewToShowGatesAndAreas:
    'Завантажити вигляд, щоб показати Ворота і Ділянки',
  uploadASitemapToShowBuilding: 'Завантажити мапу, щоб показати Будівлю',
  buildingAccessMap: 'Мапа доступу до будівлі',
  uploadASitemapToShowGates: 'Завантажити мапу, щоб показати Ворота',
  gateAccessMap: 'Мапа доступу до Воріт',
  uploadASitemapToShowEquipment: 'Завантажити мапу, щоб показати Обладнання',
  equipmentAccessMap: 'Мапа доступу до Обладнання',
  zoneAccessMap: 'Мапа доступу до зон',
  uploadASitemapToShowAreas: 'Завантажити мапу, щоб показати Ділянки',
  routeAccessMap: 'Мапа доступу до маршруту',
  uploadASitemapToShowRoute: 'Завантажити мапу, щоб показати Маршрут',
  deliveryWorkflow: 'Робочий процес доставки',
  workflowMenu: 'Меню робочого процесу',
  addANoteToThisDelivery: 'Дадати нотатку до цієї доставки',
  send: 'Відправити',
  deliveryDescriptions: {
    confirmDelete:
      'Видалення цієї доставки призведе до видалення цієї доставки ' +
      "та усіх пов'язаних нотаток та даних з проєкта.",
    confirmDelete2: 'Робіть це тільки якщо доставка була помилково створена.',
    confirmCancel: `Скасовані поставки залишаться в календарі, але будуть позначені як "Скасовані".`,
    confirmCancel2: 'Скасувати цю доставку?',
    nonWorkTimeDateChange: (days: number) =>
      `Будь ласка змініть дату чи час цієї доставки. Запит має бути створений щонайменше за ${days} ` +
      `робоч${ending(days, 'ий', 'і', 'их')} ` +
      `д${ending(days, 'ень', 'ні', 'нів')} ` +
      `від дати та часу, яку Ви вказуєте та у робочий час.`,
    maximumDeliveriesAreScheduled:
      'Для цієї локації вже заплановано максимальну кількість доставок',
  },
  loginInfoPageDescriptions: {
    requestSentTitle: 'Запит надіслано',
    requestApprovedTitle: 'Запит схвалено',
    invalidInvitationTitle: 'Недійсне посилання на запрошення',
    somethingWentWrongTitle: 'Щось пішло не так',
    accountRequestPendingEmail:
      'Адміністратор розглядає ваш запит. Ви отримаєте лист із запрошенням, щойно його схвалять. Перевірте свою поштову скриньку.',
    accountRequestPendingSms:
      'Адміністратор розглядає ваш запит. Ви отримаєте посилання на запрошення в SMS, щойно його схвалять.',
    accessRequestPending:
      'Адміністратор розглядає ваш запит. Ви отримаєте запрошення, щойно його схвалять.',
    accountRequestApproved:
      'Лист із запрошенням надіслано на вашу поштову скриньку. Щоб увійти, дотримуйтеся інструкцій у ньому.',
    goToAvailableProject: 'Перейти до доступного Проєкту',
    invalidInvitationDescription:
      'Це посилання на запрошення неповне або пошкоджене. Відкрийте його ще раз із запрошення або попросіть адміністратора надіслати нове.',
    somethingWentWrongDescription:
      'Не вдалося виконати ваш запит. Спробуйте пізніше або зверніться до служби підтримки, якщо проблема не зникне.',
    expiredInvitationTitle: 'Термін дії запрошення минув',
    expiredInvitationDescription: (hours: number) =>
      `З міркувань безпеки посилання на запрошення мають обмежений термін дії. Надішліть запрошення повторно, щоб отримати нове посилання, дійсне ${hours} годин${ending(
        hours,
        'у',
        'и',
        '',
      )}.`,
    resendMyInvitationLink: 'Надіслати запрошення повторно',
    invitationResentTitle: 'Запрошення надіслано повторно',
    invitationResentDescription:
      'Перевірте електронну пошту та перейдіть за новим посиланням.',
  },

  ssoAdminConsentPage: {
    grantedTitle: 'Згоду адміністратора надано',
    grantedDescription: `Єдиний вхід Microsoft тепер увімкнено для вашої організації. Ваша команда може входити в ${brandName} за допомогою своїх облікових записів Microsoft.`,
    notCompletedTitle: 'Згоду не завершено',
    notCompletedDescription: `Згоду адміністратора не надано. Ви можете повторити спробу за посиланням для згоди або звернутися до служби підтримки ${brandName}.`,
    continueToApp: `Продовжити до ${brandName}`,
    backToApp: `Повернутися до ${brandName}`,
    directory: 'Каталог',
  },

  assignResponsibilitiesConfirmation: "Підтвердження призначення обов'язків",
  assignResponsibilities: "Призначити обов'язки",
  assignToActivities: 'Призначити на роботи',
  groupByTeam: 'Групувати за командами',
  noTeam: '[без команди]',
  hashActivities: '# Робіт',
  rfi: 'Запит інформації',
  flag: 'Прапор',
  reply: 'Відповісти',
  unspecifiedType: '(Невказаний тип)',
  contractsOrRevisions: 'Контракти/Ревізії',
  priorWorkNotComplete: 'Попередня робота не завершена',
  informationNotAvailable: 'Інформація не доступна',
  unclearTaskDescription: 'Опис завдання не зрозумілий',
  staffNotAvailable: 'Персонал не доступний',
  materialsNotAvailable: 'Матеріал не доступний',
  noInformationAvailableOnThisMonitoring:
    'Немає доступної інформації по даному моніторингу',
  conditionsOfSatisfactions: 'Умови виконання',
  taskSequenceChange: 'Зміна порядку завдань',
  incorrectTimeEstimate: 'Неправильна оцінка термінів',
  offProjectDemands: 'Позапроєктні вимоги',
  unforeseenConditions: 'Непередбачені умови',
  weather: 'Погода',
  weatherDescriptions: {
    'clear sky': 'Чисте небо',
    'few clouds': 'Невелика хмарність',
    'scattered clouds': 'Розсіяна хмарність',
    'overcast clouds': 'Затягнуті хмари',
    'broken clouds': 'Розірвані хмари',
    'light rain': 'Ливневий дощ',
    'heavy intensity rain': 'Сильний дощ',
    'light snow': 'Легка невелика',
    'heavy snow': 'Сильний сніг',
    'shower sleet': 'Дощ з снігом',
    'freezing rain': 'Лідяний дощ',
    'very heavy rain': 'Дуже сильний дощ',
    thunderstorm: 'Гроза',
    snow: 'Сніг',
    mist: 'Туман',
  },
  humidity: 'вологість',
  precipitation: 'опади',
  temperatureRange: {
    low: 'низька',
    high: 'висока',
    avg: 'серед.',
  },
  fiveDayForecast: 'Прогноз на 5 днів',
  elevatedWork: 'Робота на висоті',
  materialHandling: 'Обробка матеріалів',
  groundDistribution: 'Наземна доставка',
  craneOrHeavyLift: 'Кран / Підйомник',
  confinedSpace: 'Простір з обмеженим доступом',
  mobileEquipment: 'Мобільне спорядження',
  deliver: 'Доставити',
  loto: 'LOTO', // хз шо це
  hotWork: 'Гарячі роботи',
  hazardousMaterial: 'Небезпечні матеріали',
  highVoltage: 'Висока напруга',
  highDecibels: 'Висока гучність',
  lifting: 'Підйом',
  closeQuarters: 'Тісне приміщення (< 6 футів)',
  rfiOpened: 'Запит інформації відкрито',
  flagOpened: 'Прапор відкрито',
  scheduleCommentOpened: 'Коментар щодо розкладу відкрито',
  addANote: 'Додати нотатку',
  addToLog: 'Додати до журналу',
  emojiPicker: 'Вибір емодзі',
  addPhotoFromLibrary: 'Додати фото з галереї',
  addFile: 'Додати файл',
  atMentionAUser: 'Згадати користувача',
  atMentionUserDescription:
    'Щоб сповістити та додати користувача як додатковий контакт робочого процесу',
  start_noun: 'Початок',
  end_noun: 'Закінчення',
  forDate: (date: string) => `На ${date}`,
  addANoteToThisStatusUpdate: 'Додати нотатку до Оновлення статусу',
  chooseADeliveryTime: 'Обрати час доставки',
  allZones: 'Всі ділянки',
  allEquipment: 'Все обладнання',
  selectDate: 'Вибрати дату',
  selectMonth: 'Вибрати місяць',
  selectDateRange: 'Вибрати діапазон дат',
  selectField: 'Вибрати поле',
  selectDateAndTime: 'Вибрати дату & час',
  jumpToDate: 'Перейти до дати',
  jumpToMap: 'Перейти до мапи',
  newDelivery: 'Нова доставка',
  newForm: 'Новий робочий процес',
  pending: 'Очікується',
  confirmChange: 'Підтвердити зміну',
  okToProceed: 'Згодні продовжувати?',
  proceed: 'Продовжити',
  actualStart: 'Фактичний початок',
  updated: 'Оновлено',
  updating: 'Оновлення',
  submitting: 'Надсилання',
  approved: 'Підтверджено',
  pendingApproval: 'Очікує підтвердження',
  missing: 'Відсутнє',
  actualFinish: 'Фактичний кінець',
  xDaysUpdated: (x: number) =>
    `${x} д${ending(x, 'ень', 'ні', 'нів')} оновлено!`,
  copyToMissing: 'Скопіювати у відсутні',
  copy: 'Скопіювати',
  copiedToClipboard: 'Скопійовано в буфер обміну',
  traceId: 'ID трасування',
  copyTraceId: 'Скопіювати ID трасування',
  copyLinkToDelivery: 'Скопіювати посилання на доставку',
  copyLink: 'Скопіювати посилання',
  enabled: 'Увімкнено',
  disabled: 'Вимкнено',
  checkInUnscheduledDelivery: 'Зареєструвати позапланову доставку',
  paste: 'Вставити',
  estimateMissing: 'Оцінити відсутні',
  activityDetails: 'Деталі роботи',
  assigned: 'Призначено',
  followed: 'Відслідковується',
  following: 'Відслідковується',
  notFollowing: 'Не відслідковується',
  predecessors: 'Попередні роботи',
  successors: 'Наступні роботи',
  viewTeam: 'Переглянути команду',
  dates: 'Дати',
  plannedStart: 'Запланований початок',
  plannedEnd: 'Заплановане закінчення',
  actualEnd: 'Фактичний початок',
  activityLog: 'Журнал роботи',
  when: 'Коли',
  date: 'Дата',
  time: 'Час',
  where: 'Де',
  min_shortMinutes: 'хв',
  hr_shortHours: 'год',
  requester: 'Запитувач',
  responsible: 'Відповідальний',
  subscriber: 'Абонент',
  directAssignments: 'Прямі призначення',
  otherSubmissions: 'Інші подання',
  onsiteContactPerson: 'Контактна особа на місці',
  onsiteContactPersons: 'Контактна особа на місці',
  vehicle: 'Транспортний засіб',
  quantity: 'Кількість',
  inspection: 'Перевірка',
  thisDeliveryWasNotScheduledInAdvance:
    'Ця доставка не була запланована заздалегідь',
  thisDeliveryWasScheduledForNonWorkTime:
    'Ця доставка була запланована на не робочий день чи час',
  sitemap: 'Мапа',
  flagAnIssue: 'Позначити проблему',
  openAnRfi: 'Створити запит інформації',
  requestADelivery: 'Створити запит на доставку',
  reason: 'Причина',
  addReasonForFlag: 'Додати причину для прапора',
  addReasonForRfi: 'Додати причину для запиту інформації',
  addReasonForCategoryOfVariance: 'Додати причину для Категорії варіації',
  addReasonForSafetyHazard: 'Додати причину Загрози безпеки',
  addANoteToThisActivityLog: 'Додати нотатку до цього журналу роботи',
  uploadingImage: 'Завантажується зображення',
  ganttDescriptions: {
    assignXUsersToYActivities: (x: number, y: number) =>
      `Призначення наступн${ending(x, 'ого', 'их', 'их')} ${x} ` +
      `член${ending(x, 'а', 'ів', 'ів')} команди на ` +
      `${y} вибран${ending(y, 'у', 'і', 'их')} ` +
      `Роб${ending(y, 'оту', 'оти', 'іт')}`,
  },
  actualDatesDescriptions: {
    newStartDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Встановлення ',
      wrapper(newDate),
      ' фактичною датою початку відмінить ',
      wrapper(oldDate),
      ` в якості дати початку і проігнорує всі оновлення до ${newDate}.`,
    ],
    newStartDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Встановлення ',
      wrapper(newDate),
      ' фактичною датою початку відмінить ',
      wrapper(oldDate),
      " в якості дати початку без втрати всіх пов'язаних даних.",
    ],
    newEndDateAfterOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Встановлення ',
      wrapper(newDate),
      ' фактичною датою закінчення відмінить ',
      wrapper(oldDate),
      ' в якості дати закінчення і проігнорує значення % виконання.',
    ],
    newEndDateBeforeOld: (
      newDate: string,
      oldDate: string,
      wrapper: (x: string) => any,
    ) => [
      'Встановлення ',
      wrapper(newDate),
      ' фактичною датою закінчення відмінить ',
      wrapper(oldDate),
      ` в якості дати закінчення і проігнорує всі оновлення після ${newDate}.`,
    ],
  },

  offloadingEquipmentMaps: (x: number) =>
    `Мап${x === 1 ? 'а' : 'и'} розвантажувального обладнання`,

  filterOptions: 'Фільтрувати опції',
  selectOptions: 'Вибрати Опції',
  filterByLBS: 'Фільтрувати за LBS',
  auto: 'авто',

  level: 'Поверх',
  zone: 'Зона',
  siteLocation: 'Локація сайту',
  verticalObject: "Вертикальний об'єкт",
  manpower: 'Робоча сила',
  manPower: 'Робоча сила',
  description: 'Опис',
  code: 'Код',
  codeVerifying: 'Код перевіряється',
  codeResending: 'Код повторно надсилається',
  resendCode: 'Надіслати код повторно',
  contract: 'Контракт',
  plannedValue: 'Запланована вартість',
  bidStatus: 'Бід статус',

  street: 'Вулиця',
  satellite: 'Супутник',
  layers: 'Шари',
  terrain: 'Ландшафт',
  transit: 'Транзит',
  traffic: 'Трафік',
  more: 'Більше',
  mapType: 'Тип мапи',
  mapSettings: 'Налаштування мапи',
  mapDetails: 'Деталі мапи',
  overlays: 'Накладення',
  labelsDisplay: 'Відображення міток',

  allowTextAndIconToScale: 'Дозволити масштабування тексту та іконок',
  allowLabelsToOverlap: 'Дозволити накладання міток',
  showWorkflowPinLabels: 'Показувати мітки маркерів робочих процесів',
  workflowMarkups: 'Позначки робочих процесів',
  showForMe: 'Показувати для мене',
  showAssociationLabels: 'Показувати мітки прив’язки',
  showWorkflowMarkupsForEveryone:
    'Показувати всім у цьому проєкті за замовчуванням',
  showAdjacent: 'Показувати суміжні',
  markupsShortcutHint: 'вмикає/вимикає',
  clickAgainToEditPoints: 'Натисніть ще раз, щоб редагувати точки',
  longPressToRemovePoint: 'Утримуйте точку, щоб її видалити',
  finishShaping: 'Завершити фігуру',
  discardShape: 'Скасувати фігуру',
  finishLine: 'Завершити лінію',
  discardLine: 'Скасувати лінію',
  labels: 'Мітки',
  threeDBuildings: '3D Будівлі',
  resetViewport: 'Скинути вигляд',
  toggleProjection: 'Змінити проєкцію',
  moreOptions: 'Більше опцій',
  showPlan: 'Показувати план',
  showWorkflows: 'Показати робочі процеси',

  version: 'Версія',
  revision: 'Ревізія',
  build: 'Сбірка',

  viewName: "Ім'я виду",
  viewImage: 'Зображення виду',
  shareToProject: 'Поділитися в проєкті',
  create: 'Створити',
  private: 'Приватний',
  listSections: {
    shown: 'Показано',
    hidden: 'Прихований',
  },

  deliveryIdNotProvided: 'Ідентифікатор відправлення не надано',
  sendingDeliveryTicket: 'Відправляю ТТН водію',
  ticketSuccessfullySent: 'ТТН усіпшно відправлено',
  failedToSendTicket: 'Не вдаллося надіслати ТТН',
  invalidDeliveryTicket: 'ТТН недійсна',
  deliveryDetails: 'Деталі доставки',
  onSiteLogistics: 'Логістика місцевості',

  showAtEntryGate: 'Покажіть біля вхідних воріт',
  showAtX: (location: string) => `Покажіть біля ${location}`,
  deliveryTicket: 'ТТН',
  hasDelivery: 'Має доставку',
  duplicate_verb: 'Дублювати',
  createFromToDelivery:
    'Створіти доставку Від-До. Поточна доставка повинна бути позначена ≥ [На місці]',
  duplicateCurrentDelivery: 'Дублювати поточну доставку',
  unsubscribeToDelivery: 'Скасувати підписку на доставку',
  subscribeToDelivery: 'Підписатися на доставку',
  sharedDelivery: 'Поширена доставка',

  requiredFieldCannotBeEmpty: 'Обов’язкове поле не може бути порожнім',
  fieldShouldBeUnique: 'Поле має бути унікальним',
  pleaseCorrectErrors: 'Будь ласка виправте помилки',
  pleaseFillAllFields: 'Будь ласка, заповніть усі поля',
  companiesUpdated: 'Компанії оновлено',
  companyAdded: 'Компанію додано',

  forms: 'Форми',
  formTypes: 'Типи робочих процесів',

  legacy: 'Спадок',
  deliveryLocations: 'Локації доставки',
  cards: 'Картки',

  columns: 'Колонки',
  struxhubLabs: `${brandName} лабораторія`,

  mobileConfiguration: 'Конфігурація моб. версії',
  additionalDataUpload: 'Завантаження додаткових даних',
  onTimeThresholds: 'Своєчасні пороги',
  qrScannersSetup: 'Налаштування сканерів QR кодів',

  businessPhone: 'Номер телефону компанії',
  businessEmail: 'Електронна адреса компанії',
  responsibles: 'Відповідальні',
  responsibleContact: 'Відповідальний',
  dba: 'Здійснює діяльність під найменуванням',
  licenseNumber: 'Номер ліцензії',

  deliveryId: 'Ідентифікатор доставки',
  bookingTime: 'Час бронювання',
  enterVerificationCodeFromSMS: 'Введіть код підтвердження з SMS',
  getDirections: 'Отримати направлення',
  showDirectionsOnMap: 'Показати маршрут на мапі',
  copyGoogleMapsLink: 'Копіювати посилання Google Maps',
  copyLocationCoordinates: 'Копіювати координати локації',
  moreActions: 'Інші дії',
  copyFailed: 'Не вдалося скопіювати',

  userInviteMessages: {
    oneUserWasInvitedToProject: (userName: string) =>
      `${userName} запрошено до проекту`,
    manyUsersWereInvitedToProject: (count: number) =>
      `${xUsers(count)} були запрошені до проекту`,
  },

  userEmailLinking: {
    linkYourEmail: 'Зв’яжіть свою електронну адресу',
    linkYourEmailAndSetPassword:
      'Зв’яжіть свою електронну адресу та встановіть пароль',
    intro: `Ви також можете використовувати свою електронну адресу для входу в ${brandName}. Цей крок є необов'язковим.`,
    setEmailWithPassword: 'Будь ласка, створіть пароль.',
    weakPassword: 'Ненадійний пароль. Перевірте допустимий формат',
    emailHasBeenLinkedSuccessfully: `Електронну пошту успішно підв'язано до облікового запису`,
  },

  thereIsNoProject: `Для вашого облікового запису користувача немає проекту. Будь ласка, зв'яжіться з адміністратором`,
  noProjectsAvailableTitle: 'Немає доступних проектів',

  repeatOn: 'Повторювати в',

  operatorValues: {
    isNot: 'НЕ Є',
    is: 'Є',
    are: 'Є',
    and: 'І',
    or: 'АБО',
    for: 'ДЛЯ',
    assign: 'ПРИЗНАЧИТИ',
    if: 'ЯКЩО',
  },

  any: 'Будь-який',
  selectCompany: 'Оберіть компанію',
  selectPermittedCompanies: 'Виберіть Дозволені Компанії',
  selectLocation: 'Оберіть локацію',
  approval: 'Схвалення',
  firewatch: 'Пожежний нагляд', // у перекладі не впевнена
  noRules: 'Жодного правила',
  rule: 'Правило',

  xOfY: (x: number, y: number) => `${x} із ${y}`,
  options: 'Опції',
  selectActivityGroups: 'Вибрати групи робіт',
  selectActionType: 'Виберіть тип дії',
  numberOfBands: 'Кількість ланок',
  lbs: 'ІСЛ', // Ієрархічна структура локацій
  lbsTag: 'Тег ІСЛ',
  lbsTagTooltip:
    'ІСЛ (ієрархічна структура локацій) визначає, де розташований ресурс у проєкті. Виберіть пов’язаний ресурс для вигляду мапи.',
  workBreakdownStructureWbs: 'Ієрархічна структура робіт (ІСР)',
  locationBreakdownStructureLbs: 'Ієрархічна структура локацій (ІСЛ)',
  groupByOptions: 'Групувати за опціями',
  showEmptyBandsLevels: 'Показати порожні рівні ланок',
  customGroupCreation: 'Створення спеціальної групи',
  bandX: (x: number) => `Ланка ${x}`,
  custom: 'Інше',
  groupByX: (x: string) => `Групування - ${x}`,

  bookingId: 'ІН бронювання',
  responsibleCompany: 'Відповідальна компанія',
  bookingDateEnTimeOffset: (offset: string) =>
    `Дата і час бронювання (${offset})`,
  durHmm_durationShort: 'Трив. (г:хх)',
  locationStatus: 'Статус локації',
  closeLocations: 'Закрити локації',
  closeResources: 'Закрити ресурси',
  openLocations: 'Відкрити локації',
  openResources: 'Відкрити ресурси',
  selectFields: 'Вибрати поля',
  selectProperty: 'Вибрати властивість',
  addAction: 'Додати дію',
  closureStart: 'Початок закриття',
  closureFinish: 'Завершення закриття',
  permittedCompany: 'Дозволена компанія',
  closureWorkflow: 'Робочий процес закриття',
  updateForm: 'Оновити робочий процес',
  formStatus: 'Статус робочого процесу', // REVIEW: род. 'процесу'
  formStart: 'Початок робочого процесу',
  formFinish: 'Завершення робочого процесу',
  materialName: 'Назва матеріала',
  productCategory: 'Категорія продукта',
  deliveryMaterialNotes: 'Нотатки по матеріалу доставки',
  vendor: 'Продавець',
  quantityOnsite: 'Наявна кількість',
  storageArea: 'Місце зберігання',
  requiredOnsiteDate: `Обо'язкова дата доставки`,
  nextDelivery: 'Наступна доставка',
  deliveryBookingDate: 'Дата замовлення доставки',
  bookedQuantity: 'Заброньована кількість',
  recordLastUpdated: 'Запис востаннє оновлено',
  numObjects: `# Об'єктів`,
  showOn: 'Показати для',
  noShow: 'Не відображається',
  moved: 'Переміщено',
  otherReason: 'Інша причина',
  mostPertinentDefault: 'Найдоречніше (за замовчуванням)',
  zones: 'Зони',
  gates: 'Ворота',
  routes: 'Маршрути',
  areas: 'Ділянка',
  levels: 'Поверхи',
  totalX: (x: number) => `Загалом ${x}`,
  deliveriesEnEquipmentNeedsPerHour: 'Потреби доставок та обладнання за годину',
  resetFilters: 'Скинути фільтри',
  saved_activities: 'Збережені',
  since: 'Від',
  lastP6Update: 'Останнє оновлення з P6',
  lastWeek: 'Останній тиждень',
  lastMonth: 'Останній місяць',
  last6Week: 'Останні 6 тижнів',
  dateRange: 'Проміжок часу',
  dateRangeForRecentlyUpdatedActivities:
    'Проміжок часу для нещодавно оновлених робіт',
  myTasks: 'Мої завдання',
  recentlyUpdated_activities: 'Нещодавно оновлені',
  activitiesWithSpecificStatus: 'Роботи з конкретним статусом',
  actualizedFromSchedule_activities: 'Актуалізовані з розкладу',
  activitiesWithOpenScheduleComment:
    'Роботи з відкритим коментарем щодо розкладу',
  notCompleted_activities: 'Не завершені',
  criticalPathActivities: 'Роботи з критичної послідовності',
  flaggedActivities: 'Позначені роботи',
  lateActivities: 'Роботи із запізненням',
  activitiesWithOpenRFI: 'Роботи з відкритим запитом інформації',
  activitiesWithDeliveries: 'Роботи з доставками',
  numberOfActivities: 'Кількість робіт',
  seeXActivities: (x: number) =>
    `Переглянути ${x} роб${ending(x, 'оту', 'оти', 'іт')}`,
  type: 'Тип',
  types: 'Типи',
  assigners: 'Цеденти', // це гугл так сказав
  items: 'Предмети',
  projectMember: 'Член проєкта',
  disableFilter: 'Вимкнути фільтр',
  totalDeliveries: 'Усього доставок',
  totalSitemaps: 'Усього мап сайту',
  totalForms: 'Усього форм',
  totalAttachments: 'Усього вкладень',
  totalClosures: 'Усього закриттів',
  totalSensors: 'Усього сенсорів',
  seeXUsers: (x: number) =>
    `Переглянути ${x} користувач${ending(x, '', 'a', 'ів')}`,
  totalLogistics: 'Усього воркфлов',
  totalMaterials: 'Усього матеріалів',
  totalReports: 'Усього репортiв',
  seeXDeliveries: (x: number) =>
    `Переглянути ${x} достав${ending(x, 'ку', 'ки', 'ок')}`,
  seeXObjects: (x: number) =>
    `Переглянути ${x} об'єкт${ending(x, '', 'и', 'ів')}`,
  seeXSitemaps: (x: number) =>
    `Переглянути ${x} мап${ending(x, 'у', 'и', '')} сайту`,
  seeXForms: (x: number) => `Переглянути ${xForms(x)}`,
  seeXClosures: (x: number) =>
    `Переглянути ${x} закритт${ending(x, 'я', 'я', 'ів')}`,
  seeXSensors: (x: number) =>
    `Переглянути ${x} сенсор${ending(x, '', 'а', 'ів')}`,
  seeXLogistics: (x: number) =>
    `Переглянути ${x} воркфлов${ending(x, 'я', 'я', 'ів')}`,
  seeXMaterials: (x: number) =>
    `Переглянути ${x} матеріал${ending(x, '', 'и', 'ів')}`,
  seeXReports: (x: number) =>
    `Переглянути ${x} репорт${ending(x, '', 'а', 'ів')}`,
  seeXAttachments: (x: number) => `Переглянути ${x} вкладень`,
  currentLookahead: 'Поточний період',
  fullProject: 'Увесь проєкт',
  numberOfDeliveries: 'Кількість доставок',
  numberOfWorkflows: 'Кількість робочих процесів',
  savedFilters: 'Saved Filters',
  saved_deliveries: 'Saved',
  xDeliveries: (x: number) => `${x} ${deliveriesNominative(x)}`,
  xWorkflows: (x: number) => `${x} ${workflows(x)}`,
  xPlans: (x: number) => `${x} ${plans(x)}`,
  savedActivitySets: 'Збережені набори робіт',
  thereAreNoSavedActivityPresets: 'Немає жодного збереженого набору робіт',
  xForms,
  xFilters: (x: number) => `${x} фільтр${ending(x, '', 'и', 'ів')}`,
  thereAreNoSavedFilters: 'Немає жодного збереженого фільтру',
  makePublic: 'Зробити загальнодоступним',
  workPackageName: 'Назва робочого пакета',
  selectedFilters: 'Вибрані фільтри',
  share: 'Поділитися',
  shareWithLink: 'Поділитися посиланням',
  saveCurrentFilters: 'Зберегти поточні фільтри',
  saveANewCustomFilter: 'Зберегти новий фільтр користувача',
  editACustomFilter: 'Редагувати фільтр користувача',

  oneDay: 'Один день',
  oneDayDefaultToday: 'Один день за замовчуванням сьогодні',
  week: 'Тиждень',
  fixedWeek: 'Фіксований тиждень',
  fixedTwoWeeks: 'Фіксовані 2 тижні',
  fixedThreeWeeks: 'Фіксовані 3 тижні',
  threeWeeks: '3 тижні',
  fourWeeks: '4 тижні',
  fixedFourWeeks: 'Фіксовані 4 тижні',
  month: 'Місяць',
  fixedSixWeeks: 'Фіксовані 6 тижнів',
  sixWeeks: '6 тижнів',
  threeMonth: '3 місяці',
  year: 'Рік',
  other: 'Інше',

  xDateToYDate: (xDate: string, yDate: string) => `${xDate} по ${yDate}`,
  assignUsersOrCategories: 'Призначити користувачів або категорії',
  searchUserCompanyTeamOrRole: 'Шукати користувача, компанію, команду або роль',
  successfullyUpdatedExistingAssignments:
    'Успішно оновлено існуючі призначення',
  successfullyDeletedAssignments: 'Успішно видалено призначення',

  approve_verb: 'Підтвердити',
  readyToInspect: 'Готово до перевірки',
  toInspect: 'До перевірки',
  notInUse: 'Не використовується',
  everyXDaysInspection: (x: number) =>
    `Перевірка ${
      x === 1
        ? 'щодня'
        : `кож${ending(x, 'ен', 'ні', 'ні')} ` +
          `${x} д${ending(x, 'ень', 'ні', 'нів')}`
    }`,
  everyXWeeksInspection: (x: number) =>
    `Перевірка ${
      x === 1
        ? 'щотижня'
        : `кож${ending(x, 'ен', 'ні', 'ні')} ` +
          `${x} тиж${ending(x, 'день', 'ні', 'нів')}`
    }`,
  everyXMonthsInspection: (x: number) =>
    `Перевірка ${
      x === 1
        ? 'щомісяця'
        : `кож${ending(x, 'ен', 'ні', 'ні')} ` +
          `${x} місяц${ending(x, 'ь', 'і', 'ів')}`
    }`,
  addInspection: 'Додати інспекцію',
  finish_noun: 'Завершення',
  legend: 'Легенда',
  startFinishState: 'Початковий/Кінцевий стан',
  intermediateState: 'Проміжний стан',
  action: 'Дія',
  ballInCourtStep: 'Етап зони відповідальності',
  active: 'Активний',
  struxhubActivityX: (code: string) => `Робота ${code} у ${brandName}`,
  openActivity: 'Відкрити роботу',
  openDelivery: 'Відкрити доставку',
  loadingPresentationMode: 'Завантажується режим презентації',

  xSelected: (x: number) => `Вибрано: ${x}`,
  xItemsCount: (x: number) => `${x} Елемент${ending(x, '', 'и', 'ів')}`,
  xTypesCount: (x: number) => `${x} Тип${ending(x, '', 'и', 'ів')}`,
  xFormsCount: (x: number) => `${x} Форм${ending(x, 'а', 'и', '')}`,
  xApplicationsCount: (x: number) =>
    `${x} Додат${ending(x, 'ок', 'ки', 'ків')}`,
  xStepsCount: (x: number) => `${x} Етап${ending(x, '', 'и', 'ів')}`,
  xFieldsCount: (x: number) => `${x} Пол${ending(x, 'е', 'я', 'ів')}`,
  xOfYSelected: (x: number, y: number) => `Вибрано ${x} з ${y}`,
  xDeliveriesShowing: (x: number) =>
    `Показано ${x} достав${ending(x, 'ка', 'ки', 'ок')}`,
  xEquipment: (x: number) => `${x} Обладнання`,
  xSensorsShowing: (x: number) =>
    `Показано ${x} датчик${ending(x, '', 'ів', 'и')}`,
  xStruxTags: (x: number) => `${x} ${struxTagsCount(x)}`,
  xResourcesShowing: (x: number, countLabel: string) =>
    `Показано ${x} ${countLabel}`,
  assign_verb: 'Призначити',
  subscribe_verb: 'Підписатися',
  new: 'Нове',
  completedTotal: 'Завершено/Усього',
  searchByBookingIdOrSubscribers: 'Пошук за ID бронювання або підписниками',
  me: 'Мої',
  full_period: 'Повний',
  day: 'День',
  calendarView: 'У вигляді калердарю',
  ganttView: 'у вигляді Гантт',
  listView: 'У вигляді списка',
  mapView: 'У вигляді мапи',
  dur_duration: 'Трив',
  loc_locations: 'Лок',
  equip_equipment: 'Облад',
  mat_materials: 'Мат',
  vend_vendor: 'Прод',
  a_actualFlag: 'А',
  unspecified: 'Невказано',

  yesUnsubscribe: 'Так, відписатися',
  deliveriesListDescriptions: {
    subscribeConfirmMassage: (x: number, y: number) =>
      `Підписатися на [${x}] ` +
      `непідписан${ending(x, 'у', 'і', 'их')} ` +
      `достав${ending(x, 'ку', 'ки', 'ок')} ` +
      `з [${y}] вибран${ending(y, 'ої', 'их', 'их')} ` +
      `достав${ending(y, 'ки', 'ок', 'ок')}?`,

    unsubscribeConfirmMessage: (x: number) =>
      `Ви вже підписалися на ${x > 1 ? 'всі' : ''} [${x}] ` +
      `вибран${ending(x, 'у', 'і', 'их')} ` +
      `достав${ending(x, 'ку', 'ки', 'ок')}.\n\n` +
      'Можливо, Ви хотіли б відписатися?',
  },
  logisticsListDescriptions: {
    subscribeConfirmMassage: (x: number, y: number) =>
      `Підписатися на [${x}] ` +
      `непідписан${ending(x, 'у', 'і', 'их')} ` +
      `допусти${ending(x, 'ку', 'ки', 'ок')} ` +
      `з [${y}] вибран${ending(y, 'ої', 'их', 'их')} ` +
      `достав${ending(y, 'ки', 'ок', 'ок')}?`,
    unsubscribeConfirmMessage: (x: number) =>
      `Ви вже підписалися на ${x > 1 ? 'всі' : ''} [${x}] ` +
      `вибран${ending(x, 'у', 'і', 'их')} ` +
      `допусти${ending(x, 'ку', 'ки', 'ок')}.\n\n` +
      'Можливо, Ви хотіли б відписатися?',
    areYouWantToDelete:
      'Ви впевнені, що бажаєте видалити всі ці вибрані робочі процеси?',
    deletionConfirmMsg: (selectedCount: number) =>
      `Ви вибрали - ${selectedCount} ${workflows(selectedCount)}`,
  },
  sitemapsDataIsLoading: 'Завантажуються дані для мапів сайту',
  editingConfirmation: 'Підтвердження редагування',
  selectDataColumn: 'Оберіть колонку даних',
  addProperty: 'Додати властивість',
  addFieldForEditing: 'Додати поле для редагування',
  deliveryBulkEditorDescriptions: {
    warningMessagePart1:
      'Жодна з обраних доставок не може бути змінена ' +
      'згідно з Вашими дозволами облікового запису',
    warningMessagePart2: 'Оберіть інші або зверніться до адміністратора',
  },
  failedToEdit: 'Сталася помилка редагування',
  editedXFieldsOnYDeliveries: (x: number, y: number) =>
    `Редаговано ${x} пол${ending(x, 'е', 'я', 'ів')} ` +
    `у ${y} достав${ending(y, 'ці', 'ках', 'ках')}`,
  editXDeliveries_question: (x: number) =>
    `Редагувати ${x} достав${ending(x, 'ку', 'ки', 'ок')}?`,
  onlyXOfYSelectedDeliveriesCanBeChanged: (x: number, y: number) =>
    `Тільки [${x}] з [${y}] ` +
    `вибран${ending(y, 'ої', 'их', 'их')} ` +
    `достав${ending(y, 'ки', 'ок', 'ок')} ` +
    `можна змінити, продовжити?`,
  editXFieldsOnYDeliveries: (x: number, y: number) =>
    `Редагувати ${x} пол${ending(x, 'е', 'я', 'ів')} ` +
    `у ${y} достав${ending(y, 'ці', 'ках', 'ках')}`,
  updateXDeliveries: (x: number) =>
    `Оновити ${x} достав${ending(x, 'ку', 'ки', 'ок')}`,
  failedToUpdate: 'Сталася помилка оновлення',
  signedOff: 'Погоджено',
  signedOffFor: (companyName: string) => `Погоджено від імені ${companyName}`,
  statusUpdated: 'Статус оновлено',
  statusUpdatedOnXDeliveries: (x: number) =>
    `Статус оновлено для ${x} достав${ending(x, 'ки', 'ок', 'ок')}`,
  updateDeliveryStatus: 'Оновити статус доставки',
  loadingDeliveriesData: 'Завантажуються дані доставок',
  loadingFormsData: 'Завантажуються дані робочих процесів',
  form: 'Форма',
  log: 'Журнал',
  utilization: 'Використання',
  controls: 'Управління',
  resourceProperties: 'Властивості ресурсу',
  resourceControls: 'Управління ресурсом',
  mapProperties: 'Властивості карти',
  sessions: 'Сесії',
  properties: 'Властивості',
  map: 'Карта',
  deleteResourceQuestion: 'Ви впевнені, що хочете видалити цей ресурс?',

  weekOfEndDate: 'Тиждень кінцевої дати',
  companySummary: 'Резюме компанії',
  percentDone: '% Виконаного',
  percentOnTime: '% За розкладом',
  trucks: 'Вантажівки',
  avgTrucksPerDelivery: 'Сер. кіл-ть вантажівок / Доставка',
  deliveryGantt: 'Гантт графік доставок',
  projectSummary: 'Резюме проєкта',
  completionRatePercent: 'Коефіцієнт завершення (%)',
  completionRate: 'Коефіцієнт завершення',
  onTimeRatePercent: 'Коефіцієнт своєчасності (%)',
  onTimeRate: 'Коефіцієнт своєчасності',
  unplanned: 'Незаплановане',
  unplannedRate: 'Коефіцієнт незапланованого',
  onTimeStart: 'Початок за розкладом',
  lateOnTime: 'Із запізненням/За розкладом',
  allDay: 'Увесь день',
  weekly: 'Щотижня',
  monthly: 'Щомісяця',
  onTimeLate: 'За розкладом/Із запізненням',
  earnedDuration: 'Накопичена тривалість',
  show_verb: 'Показати',
  showXMore: (x: number) => `Показати ще ${x}`,
  showActive: 'Показати активне',
  loadingProjectData: 'Завантажуються дані проєкта',
  updateToday: 'Оновлено сьогодні',
  critical: 'Критичне',
  deltaDays: '∆ днів',
  sumSlip: '∑ просковзування',
  openIssues: 'Відкриті питання',
  plannedToday: 'Заплановано на сьогодні',
  actualToday: 'Актуально на сьогодні',
  deltaManpowerToday: '∆ Роб. сили на сьогодні',
  deltaManpowerTrend: 'Тренд ∆ Роб. сили',
  soon: 'Скоро',
  numberOfPicks: 'Кількість вибірок',
  repeatDelivery: 'Повторювати доставку',
  every_plural: 'Кожні',
  endsOn: 'Закінчується',
  thisDelivery: 'Цю доставку',
  thisAndFutureDeliveries: 'Цю та наступні доставки',
  allDeliveries: 'Усі доставки',
  everyXDays: (x: number) =>
    `${
      x === 1
        ? 'Щодня'
        : `Кож${ending(x, 'ен', 'ні', 'ні')} ` +
          `${x} д${ending(x, 'ень', 'ні', 'нів')}`
    }`,
  everyXWeeks: (x: number) =>
    `${
      x === 1
        ? 'Щотижня'
        : `Кож${ending(x, 'ен', 'ні', 'ні')} ` +
          `${x} тиж${ending(x, 'день', 'ні', 'нів')}`
    }`,
  everyXMonths: (x: number) =>
    `${
      x === 1
        ? 'Щомісяця'
        : `Кож${ending(x, 'ен', 'ні', 'ні')} ` +
          `${x} місяц${ending(x, 'ь', 'і', 'ів')}`
    }`,
  xDays: (x: number) => `Д${ending(x, 'ень', 'ні', 'нів')}`,
  xWeeks: (x: number) => `Тиж${ending(x, 'день', 'ні', 'нів')}`,
  xMonths: (x: number) => `Місяц${ending(x, 'ь', 'і', 'ів')}`,
  xMaterial: (x: number) => `Матеріал #${x}`,
  addMaterial: 'Додати матеріал',
  forecast: 'Прогноз',
  unknown: 'Невідомо',
  multipleEntries: {
    add_x: (x: string) => `Додати ${x}`,
    paste: 'You can paste multiple entries',
    typeOrPaste: 'You can type or paste multiple entries',
    repeatedValue: 'Duplicate entry',
  },
  fileType: 'Тип файлу',
  fileExtension: 'Розширення файлу',
  workflowType: 'Тип робочого процесу',
  workflowTypeLabel: 'Тип робочого процесу',
  workflowName: 'Назва робочого процесу',
  workflowCategory: 'Категорія робочого процесу',
  workflowId: 'ID робочого процесу',
  fileCategory: 'Категорія файлу',
  uploadedBy: 'Завантажено',
  uploader: 'Завантажувач',
  source: 'Джерело',
  sort: 'Сортувати',
  sortBy: 'Сортувати за',
  sortDirection: 'Напрям сортування',
  setDefaults: 'Встановити за замовчуванням',
  ascending: 'За зростанням',
  descending: 'За спаданням',
  failedToVerifyUploadedFile: 'Не вдалося перевірити завантажений файл',

  searchCompanyOrCompanyType: 'Шукати компанію або тип компанії',

  newActivity: 'Нова робота',
  monitoringName: 'Назва моніторінгу',
  deviceId: 'ID Пристрою',
  device: 'Пристрій',
  lastMaturity: 'Остання зрілість',
  lastReading: 'Останнє зчитування',
  elapsedTime: 'Пройшло часу (гг:мм)',
  maxElapsedTime: 'Максимальний Пройшло часу (мин)',
  lastTemperature: (temperatureUnit: MaturixTemperatureUnit) =>
    `Остання температура (${UkrainianTree.temperatureUnit(temperatureUnit)})`,
  lastStrength: (strengthUnit: MaturixStrengthUnit) =>
    `Остання сила (${UkrainianTree.strengthUnit(strengthUnit)})`,
  temperatureWithUnit: (temperatureUnit: MaturixTemperatureUnit) =>
    `Температура (${UkrainianTree.temperatureUnit(temperatureUnit)})`,
  strengthWithUnit: (strengthUnit: MaturixStrengthUnit) =>
    `Сила (${UkrainianTree.strengthUnit(strengthUnit)})`,
  temperatureUnit: (temperatureUnit: MaturixTemperatureUnit): string => {
    switch (temperatureUnit) {
      case MaturixTemperatureUnit.CELCIUS:
        return 'Цельсій'
      case MaturixTemperatureUnit.FAHRENHEIT:
        return 'Фаренгейт'
    }
  },
  strengthUnit: (strengthUnit: MaturixStrengthUnit): string => {
    switch (strengthUnit) {
      case MaturixStrengthUnit.KGPERCMSQ:
        return 'кг/см²'
      case MaturixStrengthUnit.MPA:
        return 'МПа'
      case MaturixStrengthUnit.NPERMMSQ:
        return 'Н/мм²'
      case MaturixStrengthUnit.PSI:
        return 'Ф/дюйм²'
    }
  },

  unconfirmed: 'Непідтверджено',
  confirmed: 'Підтверджено',
  delivering: 'Доставляється',
  paused: 'Призупинено',
  onHold: 'На утриманні',

  ticketed: 'Оформленно',
  recalled: 'Відкликано',
  enRoute: 'В дорозі',
  pouring: 'Розливання',
  delivered: 'Доставлено',

  orderSummary: 'Підсумок замовлення',
  payloadSummary: 'Підсумок корисного навантаження',

  orderStatus: 'Статус замовлення',
  totalVolume: 'Загальний обсяг',
  deliveryLocation: 'Місце доставки',
  mixType: 'Тип суміші',
  slump: 'Спад',
  spacingOrUnloadTime: 'Інтервал або час розвантаження',
  pouringMode: 'Розливний режим',
  elementsToPour: 'Елементи для заливки',
  extraCharge: 'Додатковий збір',
  orderedBy: 'Хто замовив',

  load: 'Навантаження',
  total: 'Всього',
  timeOnSite: 'Час на сайті',
  ticket: 'Квиток',
  volumeLoaded: 'Обсяг завантаженного',
  totalOrderVolume: 'Загальний обсяг замовлення',
  additive: 'Добавка',
  jobTimes: 'Час роботи',
  startLoad: 'Початок завантаження',
  beginPouring: 'Починаю заливати',
  washing: 'Промивання',
  totalTimeOnSite: 'Загальний час перебування на сайті',
  inXHoursAndXMin: (hours: number, minutes: number) =>
    `Через ${hours}г ${minutes}хв`,
  xHoursAndXMinLate: (hours: number, minutes: number) =>
    `${hours}г ${minutes}хв запізнення`,
  inXMin: (minutes: number) => `Через ${minutes} хв`,
  xMinLate: (minutes: number) => `${minutes} хв запізнення`,
  isAboutToArrive: ' Ось-ось прибуде',

  projected_time: 'Прогнозований',
  recurringDelivery: 'Регулярна',
  recurringWorkflow: 'Регулярна',
  endingXDate: (date: string) => `Окончание ${date}`,
  isQuestionMandatory: "Питання обов'язкове?",
  remainingDuration: 'Залишкова тривалість',
  baseUrl: 'Базова Url',
  siteId: 'ID Майданчика',
  isActive: 'Чи активно',
  syncStartDate: 'Дата початку синхронизації',
  notUniqueActivityCodeMessage: (code: string) =>
    `Активність з кодом  [${code}] вже існує. Спробуйте інше значення`,
  activityWasUpdated: 'Роботу оновлено',
  activityUpdated: 'Роботу оновлено',
  editXFieldsOnYActivities: (x: number, y: number) =>
    `Редагувати ${x} пол${ending(x, 'е', 'я', 'ів')} ` +
    `у ${y} робо${ending(y, 'ті', 'тах', 'тах')}`,
  editedXFieldsOnYActivities: (x: number, y: number) =>
    `Редаговано ${x} пол${ending(x, 'е', 'я', 'ів')} ` +
    `у ${y} робо${ending(y, 'ті', 'тах', 'тах')}`,
  editXActivities_question: (x: number) =>
    `Редагувати ${x} робо${ending(x, 'ті', 'тах', 'тах')}?`,
  onlyXOfYSelectedActivitiesCanBeChanged: (x: number, y: number) =>
    `Тільки [${x}] з [${y}] ` +
    `вибран${ending(y, 'ої', 'их', 'их')} ` +
    `роб${ending(y, 'оти', 'біт', 'біт')} ` +
    `можна змінити, продовжити?`,
  p6Viewer: 'Перегляд P6',
  scanner: 'Cканер',
  scannerApp: 'Додаток сканера',
  selectScanner: 'Виберіть сканер',
  createNewScanner: 'Створити новий сканер',
  activitySetName: 'Назва набору робіт',
  editActivitySet: 'Редагування набору робіт',
  includeXFile: (ext: string) => `Додайте файл .${ext}`,
  includeDetailedEachForm: 'Додайте детальний перегляд кожної форми',
  usersSubscribedTo: {
    deliveries: (uCount: number, iCount: number) =>
      `Підписано ${uCount} ${users(uCount)} на ${iCount} ${deliveriesAccusative(
        iCount,
      )}`,
    activities: (uCount: number, iCount: number) =>
      `Підписано ${uCount} ${users(uCount)} на ${iCount} ${activitiesAccusative(
        iCount,
      )}`,
    forms: (uCount: number, iCount: number) =>
      `Підписано ${uCount} ${users(uCount)} на ${iCount} ${workflowsAccusative(
        iCount,
      )}`,
  },
  usersUnSubscribedFrom: {
    deliveries: (uCount: number, iCount: number) =>
      `Скасовано підписку ${uCount} ${users(
        uCount,
      )} на ${iCount} ${deliveriesAccusative(iCount)}`,
    activities: (uCount: number, iCount: number) =>
      `Скасовано підписку ${uCount} ${users(
        uCount,
      )} на ${iCount} ${activitiesAccusative(iCount)}`,
    forms: (uCount: number, iCount: number) =>
      `Скасовано підписку ${uCount} ${users(
        uCount,
      )} на ${iCount} ${workflowsAccusative(iCount)}`,
  },
  scannerName: 'Назва сканера',
  scanMaster: 'Майстер сканування',
  scanStationReport: 'Звіт станції сканування',
  materialTrackingReport: 'Звіт про відстеження матеріалів',
  truckSize: 'Розмір вантажівки',
  cancelationReason: 'Причина скасування',
  responsibleCompanies: 'Відповідальні компанії',
  noDataAvailable: 'Немає даних',
  last7Days: 'Останні 7 днів',
  last30Days: 'Останні 30 днів',
  last120Days: 'Останні 120 днів',
  last365Days: 'Останні 365 днів',
  entireProject: 'Весь проєкт',
  frequencyAndPeriod: 'Частота та період',
  activityDataLinking: {
    disableLinkingQuestion: (field: string) =>
      `Зв'язність з '${field}' буде вимкнено. Бажаєте продовжити?`,
    enableLinkingQuestion: (fieldValue: string, externalFieldValue: string) =>
      `Бажаєте зв'язати '${fieldValue}' з зовнішнім '${externalFieldValue}'? Це може вплинути на інші роботи.`,
    linkingIsDisabled: `Зв'язність даних вимкнена`,
    linkingIsEnabled: `Зв'язність даних увімкнена`,
    linkingDisabled: `Зв'язність даних вимкнено`,
    linkingEnabled: `Зв'язність даних увімкнено`,
    editWarning: `Зміни в зв'язності даних можуть вплинути на інші дії`,
    bulkEditWarning: `Зв'язність даних буде вимкнено для редагованих полів`,
  },

  materialsUpload: 'Завантаження матеріалів',
  plannedQuantity: 'Планова кількість',

  materialsUploading: {
    uploadMaterials: 'Завантажте матеріали',
    goToTheMaterialsPage: 'Перейти на сторінку Матеріали',
    uploadYourMaterialsFile:
      'Завантажте файл матеріалів у форматі Excel (.xlsx, .xlsm).',
    thereAreNoSheetsError:
      'У наданому файлі Excel немає аркушів або під час считування файлу сталася помилка',
    materialCategoryLvl1: 'Категорія матеріалу (Рівень 1)',
    materialLvl2: 'Матеріал (Рівень 2)',
    materialDescription: 'Опис матеріалу',
    materialPlannedQuantity: 'Планова кількість матеріалу',
    startingTheUpload: 'Початок завантаження',
    readingMaterialsFile: 'Cчитування файлу матеріалів',
    extractingMaterialsData: 'Вилучення даних матеріалів',
    removingOldData: 'Видалення старих даних',
    savingMaterialsData: 'Збереження даних про матеріали',
    finishingUpload: 'Завершення завантаження',
    uploadOfMaterialsFailedAt: 'Не вдалося завантажити матеріали під час',
    uploadWasSuccessful: 'Завантаження пройшло успішно',
    pleaseSelectSheetToUpload: (isMultiple: boolean) =>
      `${
        isMultiple ? 'Поточний файл містить кілька аркушів. ' : ''
      }Виберіть аркуш із файлу для імпорту.`,
    makeSureLocationNamesUnique:
      'Переконайтеся, що назви локацій у StruxHub унікальні',
  },

  materialsUploadHistory: {
    currentMaterials: 'Поточні матеріали',
    projectUsesDefaultMaterials:
      'У проєкті використовуються стандартні матеріали',
    latestUpload: 'Останнє завантаження',
    materialsRestoringConfirmation: 'Підтвердження відновлення матеріалів',
    totalMaterialCategoriesAndMaterials: 'Всього категорій і матеріалів',
    restoreDefaultMaterials: 'Відновити стандартні матеріали',
    doYouConfirmRestoreToDefaults:
      'Ви підтверджуєте відновлення стандартних матеріалів?',
    restoredSuccessfully: 'Успішно відновлено',
    failedToRestore: 'Не вдалося відновити',
  },

  materialsUploadConfirm: {
    confirmDataMerge: 'Підтвердити злиття даних',
    confirmDescription1: `Завантаження цього файлу оновить усі змінені значення для існуючих матеріалів
      і створить нові записи для нових матеріалів.`,
    confirmDescription2:
      'Якщо ви хочете видалити всі існуючі дані матеріалів і замінити їх даними у файлі завантаження,',
    confirmDescription3: 'натисніть тут',
    replaceDescription:
      'Ви справді бажаєте видалити всі наявні дані матеріалів і замінити їх даними у файлі завантаження?',
  },

  collapseAll: 'Згорнути все',
  expandAll: 'Розгорнути все',
  expandMenu: 'Розгорнути меню',
  expandPanel: 'Розгорнути панель',
  collapseMenu: 'Згорнути меню',

  myUserProfile: 'Профіль мого користувача',
  addTeammates: 'Додати користувачів',
  addSummaryColumn: 'Додайте колонку підсумків',
  configureColumns: 'Налаштувати стовпці',
  summaryColumns: 'Підсумкові колонки',
  receipt: 'Розписка',
  summary: 'Резюме',

  remainingQuantity: 'Кількість, що залишилася',

  materialsInDeliveryFormWarning: {
    onlyXMaterialsForForm: (x: number) =>
      `Наразі для однієї форми доставки доступно лише ${x} матеріал${ending(
        x,
        '',
        'и',
        'ів',
      )}. Якщо це обмеження, зверніться до`,
    andWeCanTalk: 'і ми зможемо це обговорити',
  },

  deliverySuccessfullyCreated: 'Доставка успішно створена',

  xDates,

  materialDescription: 'Опис матеріалу',
  thisGroupAlreadyExists: 'Ця група вже існує',

  upload: 'Завантажити',
  totalBooked: 'Всього заброньовано',
  totalDelivered: 'Всього доставлено',
  doneDeliveries: 'Виконані поставки',

  xVendors: (x: number) => `${x} Продав${ending(x, 'ець', 'ці', 'ців')}`,
  thisWeek: 'Цей тиждень',
  thisMonth: 'Цей місяць',

  youCannotChangeDelivery:
    'Ви не можете змінювати доставку, коли вона має цей статус',
  plannedInstallLocation: 'Запланована локація інсталяції',
  plannedDeliveryLocation: 'Заплановане локація доставки',
  currentLocation: 'Поточна локація',
  procurementID: 'Ідентифікатор закупівлі',
  id_short: 'ID',
  readyToInstallQuantity: 'Кількість готова до встановлення',
  totalOnSiteQuantity: 'Загальна наявна кількість',
  toMoveQuantity: 'Кількість для переміщення',
  preciseLocation: 'Точна локація',
  editPreciseLocation: 'Редагувати точну локацію',
  preciseLocationPreviewTooltip:
    'Цей попередній перегляд використовується у PDF-звіті. Налаштуйте рівень масштабування, щоб контролювати вигляд карти.',
  zoomOverviewReportTooltip:
    'Цей рівень масштабування також використовується як Оглядове зображення у PDF-звітах для Локацій Робочих Процесів.',
  thisLocationIsNotGeolocated: 'Ця локація не геолокована',
  notGeolocated: 'Не геолоковано',
  inheritsFrom: 'Успадковує з',
  defaultsTo: 'Походить від',
  setPreciseLocation: 'Встановити точну локацію',
  movePinPosition: 'Перемістити позицію маркера',
  markupTools: 'Інструменти розмітки',
  bold: 'Жирний',
  italic: 'Курсив',
  underline: 'Підкреслений',
  fill: 'Заливка',
  border: 'Контур',
  thickness: 'Товщина',
  shortcuts: 'Комбінації клавіш',
  gestures: 'Жести',
  gesturePointsGroup: 'Точки (лінія / полігон)',
  gestureTextGroup: 'Текст',
  gestureMapGroup: 'Карта',
  gestureSelectMarkup: 'Вибрати розмітку',
  gestureEditPoints: 'Редагувати точки (лінія / полігон)',
  gestureDeselect: 'Зняти виділення',
  gestureMovePoint: 'Перемістити точку',
  gestureAddPoint: 'Додати точку',
  gestureRemovePoint: 'Видалити точку',
  gestureClosePolygon: 'Замкнути в полігон',
  gestureRectangleCircle: 'Прямокутник / коло',
  gestureLineArrow: 'Лінія / стрілка',
  gesturePolygonPolyline: 'Полігон / ламана',
  gesturePenHighlighterEraser: 'Перо / маркер / гумка',
  gestureAddText: 'Додати текст',
  gestureEditText: 'Редагувати текст',
  gestureMoveResizeRotate: 'Перемістити / змінити розмір / обернути',
  gestureRepositionPin: 'Перемістити маркер',
  gesturePan: 'Панорамувати',
  gestureZoom: 'Масштаб',
  gestureRotateTilt: 'Обертати й нахиляти',
  gestureTap: 'Торкнутися',
  gestureTapAgain: 'Торкнутися ще раз',
  gestureTapMap: 'Торкнутися карти',
  gestureDrag: 'Перетягнути',
  gestureTapMidpoint: 'Торкнутися середньої точки',
  gestureLongPress: 'Довге натискання',
  gestureTapFirstPoint: 'Торкнутися першої точки',
  gestureTapOrDrag: 'Торкнутися або перетягнути',
  gestureDragOrTapEnds: 'Перетягнути або торкнутися початку, потім кінця',
  gestureTapPointsClose: 'Торкайтеся точок; торкніться першої, щоб замкнути',
  gestureDoubleTap: 'Подвійне торкання',
  gestureDragBodyCornerPin: 'Перетягнути тіло / кут / маркер',
  gestureTapMoveThenDrag: 'Торкніться «Перемістити», потім перетягніть карту',
  gesturePinch: 'Зведення пальців',
  gestureTwoFingerDrag: 'Перетягування двома пальцями',
  customColor: 'Власний колір',
  strokeWidth: 'Товщина лінії',
  strokeWidthSlider: 'Повзунок товщини лінії',
  borderWidth: 'Товщина контуру',
  borderWidthSlider: 'Повзунок товщини контуру',
  backgroundColor: 'Колір фону',
  textColor: 'Колір тексту',
  textSize: 'Розмір тексту',
  fontSize: 'Розмір шрифту',
  moveToolbar: 'Перемістити панель інструментів',
  move: 'Перемістити',
  savePreciseLocationPosition: 'Зберегти позицію точної локації?',
  dontSave: 'Не зберігати',
  dragTheMapToChangeLocationPinPosition:
    'Перетягніть карту, щоб редагувати позицію мітки',
  addMarkups: 'Додати розмітку',
  markUpPhoto: 'Розмітити фото',
  photoMarkupLoadFailed: 'Не вдалося завантажити це фото',
  untitledWorkflow: 'Робочий процес без назви',
  fromDrawing: 'З малюнка',
  continueWorkflow: 'Продовжити робочий процес…',
  collapse: 'Згорнути',
  expand: 'Розгорнути',
  createWorkflow: 'Створити робочий процес',
  holdToCreateWorkflow: 'Створити робочий процес',
  holdToCreateWorkflowHint:
    'Натисніть і утримуйте будь-де на карті, щоб поставити позначку та розпочати робочий процес.',
  gotIt: 'Зрозуміло',
  enterWorkflowTitle: 'Введіть назву',
  discard: 'Відхилити',
  discardWorkflowDraft: 'Відхилити чернетку робочого процесу?',
  discardWorkflowDraftQuestion:
    'Ви втратите пін, введені у форму дані та всю намальовану розмітку.',
  workflowTitleSubmitted: (name: string) => `${name} подано`,
  userStamp: 'Штамп користувача',
  userStampTooltip: "Включає ім'я підписанта разом із підписом.",
  inventoryTracking: 'Поля відстеження',
  inventoryTrackingTooltip:
    'При увімкненні додає поля кількості та місцезнаходження для відстеження інвентаря матеріальних ресурсів.',
  moveToMyLocation: 'Перейти до моєї локації',
  noCoordinatesAvailable: 'Координати недоступні',
  geolocationPermissionDenied:
    'Дозвіл на геолокацію відхилено. Будь ласка, увімкніть доступ до місцезнаходження в налаштуваннях вашого браузера.',
  geolocationPositionUnavailable:
    'Інформація про місцезнаходження недоступна. Будь ласка, перевірте налаштування місцезнаходження вашого пристрою.',
  geolocationTimeout:
    'Запит на місцезнаходження закінчився. Будь ласка, спробуйте ще раз.',
  geolocationError:
    'Сталася помилка при отриманні вашого місцезнаходження. Будь ласка, спробуйте ще раз.',
  dismiss: 'Закрити',
  pictograms: {
    names: {
      building: 'Будівля',
      level: 'Рівень',
      zone: 'Зона',
      area: 'Ділянка',
      route: 'Маршрут',
      door: 'Двері',
      gate: 'Ворота',
      shaft: 'Шахта',
      'loading-zone': 'Зона завантаження',
      'restrooms-men': 'Чоловіча вбиральня',
      entrance: 'Вхід',
      exit: 'Вихід',
      'access-a': 'Доступ A',
      'access-b': 'Доступ B',
      'access-c': 'Доступ C',
      'access-d': 'Доступ D',
      'access-e': 'Доступ E',
      'access-f': 'Доступ F',
      'no-access-1': 'Вхід заборонено 1',
      'no-access-2': 'Вхід заборонено 2',
      'no-access-3': 'Вхід заборонено 3',
      'pedestrian-walkway': 'Пішохідний прохід',
      elevator: 'Ліфт',
      stairs: 'Сходи',
      parking: 'Парковка',
      'bike-parking': 'Велопарковка',
      'buggy-parking': 'Парковка для електрокарів',
      'hi-vis-apparel': 'Сигнальний одяг підвищеної видимості',
      'safety-helmet': 'Захисна каска',
      'safety-gloves': 'Захисні рукавиці',
      'safety-boots': 'Захисне взуття',
      'not-required-all': 'Не обовʼязково: усе',
      'not-required-apparel': 'Не обовʼязково: одяг',
      'not-required-helmet': 'Не обовʼязково: каска',
      'not-required-gloves': 'Не обовʼязково: рукавиці',
      'not-required-boots': 'Не обовʼязково: взуття',
      'visitor-check-in': 'Реєстрація відвідувачів',
      'admin-office': 'Адміністративний офіс',
      'electrical-room': 'Електрощитова',
      restrooms: 'Вбиральні',
      'restrooms-women': 'Жіноча вбиральня',
      'break-area': 'Зона відпочинку',
      'hydration-station': 'Питна вода',
      'smoking-area': 'Зона для куріння',
      canteen: 'Їдальня',
      break: 'Перерва',
      'wash-area': 'Зона миття',
      dumpster: 'Смітник',
      'brand-oes': 'OES Equipment',
      'brand-sunbelt': 'Sunbelt Rentals',
      'brand-united-rentals': 'United Rentals',
      'brand-equipmentshare': 'EquipmentShare',
      'brand-feco': 'First Equipment Company',
      deliveries: 'Доставки',
      'construction-vehicles': 'Будівельна техніка',
      trailer: 'Причіп',
      'security-checkpoint': 'Пункт охорони',
      'temporary-water': 'Тимчасове водопостачання',
      'equipment-storage': 'Зберігання обладнання',
      'material-boxes': 'Склад матеріалів: коробки',
      'material-wood': 'Склад матеріалів: деревина',
      'material-steel-1': 'Склад матеріалів: сталь 1',
      'material-plumbing': 'Склад матеріалів: сантехніка',
      'material-steel-2': 'Склад матеріалів: сталь 2',
      'construction-debris-1': 'Будівельне сміття 1',
      'construction-debris-2': 'Будівельне сміття 2',
      'information-area': 'Інформаційна зона',
      'site-map': 'План майданчика',
      'first-aid': 'Перша допомога',
      aed: 'Автоматичний зовнішній дефібрилятор',
      'covid-checkpoint': 'Перевірка здоров’я',
      'fire-extinguisher': 'Вогнегасник',
      'muster-point': 'Пункт збору',
      'exit-running': 'Вихід (фігура, що біжить)',
      cleanroom: 'Чисте приміщення / перевдягання',
      'icra-zone': 'Зона ICRA',
      'negative-pressure': 'Негативний тиск',
      'esd-zone': 'Зона ESD',
      'no-photography': 'Фотографувати заборонено',
      'no-drones': 'Дрони заборонено',
      'fall-hazard': 'Небезпека падіння / відкритий край',
      'silica-dust': 'Кремнезем / пил',
      noise: 'Шум',
      'overhead-load': 'Підвішений вантаж / зона крана',
      excavation: 'Котлован',
      'hot-work': 'Вогневі роботи',
      'confined-space': 'Замкнений простір',
      'arc-flash': 'Висока напруга / електрична дуга',
      stop: 'Стоп',
      yield: 'Дати дорогу',
      'speed-limit': 'Обмеження швидкості',
      'one-way': 'Односторонній рух',
      flagger: 'Регулювальник попереду',
      'truck-crossing': 'Перетин вантажівок',
      'fire-lane': 'Пожежний проїзд — не захаращувати',
      'concrete-washout': 'Мийка бетонозмішувачів',
      'storm-drain': 'Захист зливоприймачів',
      'erosion-control': 'Протиерозійний захист',
      'wheel-wash': 'Мийка коліс',
      'spill-kit': 'Набір для розливів',
      scaffold: 'Риштування',
      'swing-stage': 'Підвісна люлька',
      'mast-climber': 'Щоглова платформа',
      eyewash: 'Фонтанчик для очей / аварійний душ',
      standpipe: 'Пожежний стояк / рукав',
      'fire-hydrant': 'Пожежний гідрант',
      'alarm-pull-station': 'Ручний пожежний сповіщувач',
      'crane-zone': 'Зона крана / підйому',
      'brand-herc-rentals': 'Herc Rentals',
    },
  },
  iconPicker: {
    library: 'Бібліотека',
    companyLogos: 'Логотипи компаній',
    selectIcon: 'Вибрати значок',
    xIcons: (x: number) => `${x} Знач${ending(x, 'ок', 'ки', 'ків')}`,
    noResults: 'Значків не знайдено',
    libraryIconColor:
      'Колір застосовується до міток, тегів і календарів, але не до значка, бо значок має власні кольори',
  },

  formMaterialSubFields: {
    materialHasQuantityAndLocation:
      'Матеріал має поля Кількість і Локація, які ви також можете додати/редагувати нижче.',
    clickToEnableQuantity: 'Натисніть тут, якщо ви хочете ввімкнути Кількість.',
    clickToEnableLocation: 'Натисніть тут, якщо ви хочете ввімкнути Локацію.',
  },
  newFormType: 'Новий тип робочого процесу',
  materialTransferId: 'Ідентифікатор транспортування матеріалу',
  materialTransferDate: 'Дата транспортування матеріалу',
  newTransfer: 'Нове транспортування',
  doneTransfers: 'Виконані траспортування',
  totalTransferred: 'Всього транспортовано',
  xTransfers: (x: number) => `${x} Транспортуван${ending(x, 'ня', 'ня', 'ь')}`,

  youCanSelectMultipleOptions: 'Ви можете вибрати декілька варіантів',
  updateStatus: 'Оновити Статус',

  review_verb: 'Оглянути',
  reviewed: 'Переглянуто',
  activated: 'Активовано',
  submitted: 'Подано',
  workflowIsDoneOrDenied: (isDenied: boolean) =>
    `Робочий процес ${isDenied ? 'відхилено' : 'завершено'}`,
  workflowIsEnded: 'Робочий процес завершено',
  request_noun: 'Запит',
  review_noun: 'Огляд',
  recurringInspection: 'Періодична перевірка',
  assignAsBicWhenWorkflowMatches:
    'Призначити як Ball-in-Court, коли Робочий процес відповідає',
  thisWorkflowStepIsAssignedByRules:
    'Цей крок робочого циклу призначається за такими правилами',
  adminsCanAdvanceWorkflow:
    'Адміністратори можуть просувати Робочий процес незалежно від призначених користувачів.',
  completedBy: 'Виконує',
  anyAssignedUser: 'Будь-який призначений користувач',
  allAssignedUsers: 'Усі призначені користувачі',
  allAssignedCompanies: 'Усі призначені компанії',
  allAssignedUsersAndCompanies: 'Усі призначені користувачі/компанії',
  oneUserPerRule: 'Один користувач на правило',
  anyAssignedUserDescription:
    'Перший призначений користувач, який виконає цей крок, просуває робочий цикл далі.',
  allAssignedUsersDescription:
    'Кожен призначений користувач і одна особа з кожної призначеної компанії повинні виконати цей крок, перш ніж робочий цикл просунеться далі.',
  oneUserPerRuleDescription:
    'Один користувач з кожного правила повинен виконати цей крок, перш ніж робочий цикл просунеться далі.',
  assignees: 'Призначені',
  optionalInParentheses: "(необов'язково)",
  workflowCompany: 'Компанія робочого циклу',
  completeForWhichRule: 'Виконати для якого правила?',
  countsTowardOneRule:
    'Вас призначено через кілька правил на цьому кроці. Виконання зараховується одному з них.',
  ruleName: 'Назва правила',
  allUsersMustCompleteDisabledTooltip:
    'Додайте правило BIC користувача, щоб увімкнути цю опцію.',
  xOfYComplete: (x: number, y: number) => `${x} з ${y} завершено`,
  noFieldsSubmitted: 'Поля не надіслано',
  submittedAt: 'Надіслано о',
  xRules: (x: number) => `${x} правил${ending(x, 'о', 'а', '')}`,
  step: 'Крок',
  stepCompletion: 'Завершення кроку',
  bicUser: 'BIC користувач',
  formSection: 'Розділ робочого процесу',
  actions: 'Дії',
  frequency: 'Частота',
  deadline: 'Кінцевий термін',
  automaticStart: 'Автоматичний запуск',
  addStep: 'Додати крок',

  workflowConfDescr: {
    mustContainApproval:
      'Робочий процес із кроком Запиту повинен містити крок Схвалення',
    cannotBeTheLastStep: (stepName: string) =>
      `${stepName} не може бути останнім кроком, додайте ще один або видаліть цей.`,
    workflowStartsOnDate:
      'Робочий процес запускається автоматично в дату початку.',
    automaticallyStart: 'Автоматично запускати робочий процес у дату початку',
    replaceWithStep: (stepType: string): string =>
      `Замінити кроком ${stepType}`,
    replaceWithStart: 'Замінити кроком Початок',
    deleteWorkflowStep: 'Видалити крок робочого процесу',
    removeStepDialogMsg: 'Ви бажаєте видалити цей крок робочого процесу?',
    toastUnsavedMsg: 'Внесені вами зміни було скинуто',
    recurringHasConstr: 'Крок періодичної інспекції має обмеження:',
    toggleDialogMsg: (isManualStart: boolean): string =>
      `Змінити його на крок ${
        isManualStart ? 'Ручний Запуск' : 'Автоматичний Запуск'
      }?`,
    selectStepMsg: 'Виберіть крок робочого процесу, щоб додати/змінити поля',
  },
  workflowConfTooltips: {
    bicBlockFirstLine: `Назначте одного або кількох користувачів робочого процесу як Ball-in-Court для цього кроку. Користувачі Ball-in-Court відповідальні за виконання кроку перед тим, як робочий процес може продовжитися.`,
    bicBlockSecondLine: `Правила можна використовувати для призначення користувачів динамічно на основі полів StruxHub, таких як Локація, Оборудование або Компанія`,
  },
  xWorkflowsShowing: (x: number) => `${x} ${workflows(x)} показано`,
  workflowTableText: {
    seeDescr: 'Перегляньте опис кроків',
    stepCategory: 'Категорія кроку робочого процесу',
    workflowStep: 'Крок робочого процесу',
    constraints: 'Обмеження',
    bicText: `Чи можна вказати ball-in-court користувача?`,
    multipleText: 'Чи можна мати кілька екземплярів у робочому процесі?',
    submissionDescr:
      'Початок кроку робочого процесу для надсилання форми, яка не потребує подальшого затвердження.',
    submissionConstr: 'Може лише ініціювати робочий процес.',
    requestDescr: 'Початок кроку робочого процесу для запиту схвалення.',
    requestConstr:
      'Може лише ініціювати робочий процес. Наступний крок має бути Затвердження.',
    reviewDescr:
      'Загальний крок просування робочого циклу, який можна використовувати для попередніх схвалень.',
    approvalDescr:
      'Схвалення запиту на робочий процес, який можна призначити певному(им) користувачу(ам).',
    approvalConstr: 'Попередній крок має бути Запит.',
    startDescr:
      'Запуск робочого процесу, ініційований натисканням кнопки «Розпочати».',
    autoStartDescr:
      'Початок робочого циклу, який автоматично запускається в дату початку робочого циклу.',
    onSiteDescr:
      'Використовується для позначення прибуття транспортного засобу доставки на місце.',
    readyToInspectDescr:
      'Проміжний крок робочого процесу, який використовується для сповіщення інспекторів.',
    inspectionDescr: 'Загальний крок перевірки.',
    recurringDescr: 'Періодичні перевірки з визначеною періодичністю.',
    recurringConstr:
      'Повинен безпосередньо передувати крок запуску або автоматичного запуску.',
    finishDescr:
      'Завершення робочого процесу позначається натисканням кнопки «Готово».',
  },
  workflowSteps: 'Кроки робочого процесу',
  workflowDiagram: 'Схема робочого процесу',
  workflowStepConfigurator: 'Конфігуратор кроків робочого процесу',
  configurator: 'Конфігуратор',
  restoreToLastSavedState: 'Відновити останній збережений стан',
  endOfTheDay: 'Кінець дня',
  everyX_withoutNum: (x: number) => `Кож${ending(x, 'ен', 'ні', 'ні')}`,
  submission: 'Подання',
  initiation: 'Ініціація',

  deleteWorkflow: 'Видалити робочий процес',
  workflowDeletionDialogDescr: {
    confirmDelete:
      'Видалення цього робочого процесу видалить сам робочий процес, а також всі асоційовані нотатки та дані з проекту.',
    confirmDelete2:
      'Зробіть це лише в тому випадку, якщо запит робочого процесу був помилкою.',
  },
  draftWorkflowDeletionDialogDescr: {
    confirmDelete: 'Це видалить робочий процес в чернетці з проекту назавжди.',
  },
  emailPdfReport: 'Звіт у форматі PDF електронною поштою',
  unsubscribe: 'Відписатися',
  followFormMenuDescr: {
    toReceive: 'Щоб отримувати сповіщення про робочий процес',
    toStop: 'Щоб припинити отримання сповіщень про робочий процес',
  },
  workflowDeleteMenuDescr: {
    isDone: 'Робочий процес завершено, ви не можете його видалити.',
    onlyFor: `Доступно лише для адміністраторів,
      майстрів форм, запитувачів, призначених`,
  },
  configureTableColumns: 'Налаштувати стовпці таблиці',
  searchProperties: 'Пошук властивостей',
  shownInTable: 'Показано в таблиці',
  hiddenInTable: 'Сховано в таблиці',
  visibleLayers: 'Видимі шари',
  hiddenLayers: 'Приховані шари',
  finishingUpload: 'Завершення завантаження',
  processingTheUpload: 'Обробка завантаження',
  uploadSucceeded: 'Завантаження успішне',
  uploadFailed: 'Помилка завантаження',
  replaced: 'Замінено',
  pleaseWaitThisMayTakeAWhile: 'Зачекайте, це може тривати деякий час',
  aNewerVersionOfThisFileWasUploaded: 'Завантажено новішу версію цього файлу.',
  somethingWentWrongPleaseTryAgain: 'Щось пішло не так — спробуйте ще раз.',
  freezeColumns: 'Закріпити стовпці вище',
  restoreDefaults: 'Відновити стандартні налаштування',
  rememberChoice: 'Запам’ятати мій вибір',
  successfullyDeletedForms: (x: number) =>
    `Успішно видалено ${x} ${workflowsAccusative(x)}`,
  failedToDelete: 'Не вдалося видалити',
  failedToArchive: 'Не вдалося архівувати',
  failedToUnarchive: 'Не вдалося розархівувати',
  failedToLoadUsageData:
    'Не вдалося завантажити дані використання. Спробуйте ще раз.',
  retry: 'Повторити',
  cannotDeleteDoneForms: 'Неможливо видалити завершені форми',
  cannotDeleteSystemFormsTooltip: 'Видалення форм за замовчуванням заборонено',
  getMobileNativeApp: 'Отримати додаток',
  getMobileNativeApp_long: 'Отримати мобільний додаток StruxHub',
  getAppOnGoogle: 'Get it on Google Play',
  getAppOnApple: 'Download on the App Store',
  oneTimePopup: {
    title: 'Завантажте наш новий мобільний додаток!',
    body: 'Отримуйте оновлення в реальному часі та виконуйте більше роботи в полі з StruxHub.',
    dismiss: 'Добре, зрозуміло!',
  },

  enterX: (x: string) => `Введіть ${x}`,
  selectX: (x: string) => `Обрати ${x}`,
  saveMapView: 'Зберегти вигляд',
  saveAsNewMapView: 'Зберегти як новий вигляд',
  restoreMapView: 'Відновити вигляд',
  moreSaveOptions: 'Інші варіанти збереження',
  aboutX: (x: string) => `Про ${x}`,
  selectNestedItems: 'Обрати вкладені елементи',
  deselectNestedItems: 'Скасувати вибір вкладених елементів',
  createNewX: (x: string) => `Створити ${x}`,
  editX: (x: string) => `Редагувати ${x}`,
  hoursUnit: 'години',
  enterProjectCode: 'Введіть код проекту',
  somethingWentWrongTryAgainLater: 'Something went wrong, try again later!',
  unsubscribedSuccessfully: 'Підписку скасовано!',
  exitScanner: 'Вийти зі сканера',
  deleteScanner: 'Видалити сканер',
  editScannerText: 'Редагувати сканер',
  shouldDeleteScanner: 'Потрібно видалити сканер',
  startScanner: 'Запустити сканер',
  queue: 'Черга',
  startQueue: 'Розпочати Чергу',
  addedToQueue: 'Додано До Черги',
  startSession: 'Розпочати Сесію',
  allowedUsers: 'Дозволені користувачі',
  badgeDescription: 'Опис Бейджа',
  makeItTimed:
    'Зробіть це сканером із часовим діапазоном для користувачів. ' +
    'Scan-Start : Scan-Stop = Total Time',
  limitAccess:
    "Обмежте доступ для певних користувачів, команд або компаній із 'Бейджем'",
  endRideConfirm: 'Ви впевнені, що хочете завершити поїздку?',
  changeModeConfirm: 'Ви впевнені, що хочете змінити режим поїздки?',
  startSessionConfirm: 'Ви впевнені, що хочете розпочати сесію?',
  isScannerTimedByDay:
    'Таймер автоматично ЗАКІНЧУЄТЬСЯ після закінчення робочого дня',
  endRide: 'Закінчити поїздку',
  scanTo: 'Сканувати до',
  activeUsers: 'Активних користувачів:',
  scannerActive: 'Cканер активний',
  scannerInactive: 'Сканер неактивний',
  pauseScanner: 'Призупинити сканер',
  resumeScanner: 'Відновити сканер',
  endSessionForAll: 'Завершити сесію для всіх',
  formName: 'Тип робочого процесу',
  undo: 'Скасувати',
  redo: 'Повторити',
  icon: 'Значок',
  color: 'Колір',
  parent: 'Батьківський елемент',
  lbsParent: 'Батьківський елемент LBS',
  lbsParentDescription:
    'LBS (структура розподілу розташувань) визначає, де Ресурс розташований у межах Проєкту. Виберіть Батьківське Розташування для цього Ресурсу.',
  newLocationNeedsParent:
    'Нове розташування потребує батьківського елемента LBS.',
  locationTypeNotUsableInWorkflows:
    'Цей тип розташування поки не можна використовувати в робочому процесі.',
  uncreatableLocationTypesSkipped:
    'Пропущено розташування типу, який цей проєкт не може створювати.',
  locationTypesStillLoading:
    'Типи розташувань ще завантажуються. Спробуйте ще раз за мить.',
  locationTypesFailedToLoad:
    'Не вдалося завантажити типи розташувань. Перезавантажте сторінку, щоб спробувати ще раз.',
  uncreatableLocationType:
    'Цей проєкт не може створювати розташування цього типу.',
  showInApp: 'Показати в Додатку',
  allowCreation: 'Дозволити створення',
  activeNotActive: 'Активний / Не активний',
  notActive: 'Не активний',
  allowCreationInfo:
    'Вмикає або вимикає створення цього типу робочого процесу.',
  creationAccess: 'Доступ до створення',
  creationAccessInfo:
    'Визначає, хто може створювати цей тип робочого процесу.\nУсі: будь-який учасник проєкту.\nЛише адміністратори: зарезервовано для адміністраторів.',
  adminsOnly: 'Лише адміністратори',
  everyone: 'Усі',
  blockRequestsForNonWorkTimes: 'Блокувати запити на неробочий час',
  general: 'Загальне',
  steps: 'Кроки',
  views: 'Види',
  confirmation: 'Підтвердження',
  deletionOfDefaultForms: 'Видалення форм за замовчуванням заборонено',
  theFollowingEntitiesWereUpdated: 'Наступні сутності були оновлені',
  globeViews: 'Зображення Шарів',
  whiteboards: 'Дошки',
  whiteboard: 'Дошка',
  georeferenced: 'Геопозиційовано',
  nonGeoreferenced: 'Не геопозиційовано',
  areYouSureDeleteMapView: 'Ви впевнені, що хочете видалити цю мапу?',
  areYouSureDeleteTileset: 'Ви впевнені, що хочете видалити цей набір тайлів?',
  areYouSureDeleteRemoteTileset:
    'Ви впевнені, що хочете видалити цей віддалений набір тайлів?',
  addRemoteTileset: 'Додати віддалений набір тайлів',
  fileProperties: 'Властивості файлу',
  shapeProperties: 'Властивості фігури',
  createLocationWithThisShape: 'Створити локацію з цією фігурою',
  latitude: 'Широта',
  longitude: 'Довгота',
  zoom: 'Масштаб',
  zoomIn: 'Збільшити',
  zoomOut: 'Зменшити',
  parts: 'Частини',
  geometryType: 'Тип геометрії',
  noMapViewsConfigured: 'Для цього проєкту ще не налаштовано жодної мапи.',
  noMapViewsMatchingX: (x: string) => `Жодна мапа не відповідає запиту «${x}».`,
  mapViewsManager: 'Менеджер мап',
  selectMapView: 'Виберіть мапу',
  xMapViews: (x: number) => `${x} мап`,
  xOfYMapViews: (visible: number, total: number) => `${visible} з ${total} мап`,
  createdAt: 'Створено',
  byLBS: 'За LBS',
  byLocation: 'За місцем',
  byType: 'За типом',
  collapseOneLevel: 'Згорнути один рівень',
  expandOneLevel: 'Розгорнути один рівень',
  previousMapView: 'Попередня мапа',
  nextMapView: 'Наступна мапа',
  closeMapViews: 'Закрити мапи',
  mapViewsBrowseAndSelect: 'Перегляньте та виберіть мапу для проєкту.',
  selectAssociatedResource: 'Виберіть пов’язаний ресурс',
  selectPermittedResources: 'Виберіть дозволені ресурси',
  selectResources: 'Виберіть ресурси',
  openActionsForX: (x: string) => `Відкрити дії для ${x}`,
  openX: (x: string) => `Відкрити ${x}`,
  openAMap: 'Відкрити мапу',
  backToX: (x: string) => `Назад до ${x}`,
  selectMapViewX: (x: string) => `Виберіть мапу ${x}`,
  available: 'Доступний',
  useMapLabels: 'Показувати назви місць',
  notifiedUsers: 'Сповіщені користувачі',
  selectNotificationUsers:
    'Виберіть користувачів, які отримають сповіщення після завершення цього етапу',
  wouldYouEnableNotificationsForSteps:
    'Бажаєте увімкнути сповіщення для цього поля на всіх етапах робочого процесу?',
  allowNotificationsFor: (fieldName: string) =>
    `Дозволити сповіщення для ${fieldName}`,
  notifyFor: 'Сповіщувати для',
  selectWorkflowSteps: 'Вибрати етапи робочого процесу',
  allSteps: 'Всі етапи',
  workflowBookingDeadline: 'Кінцевий термін бронювання робочих процесів',
  workflowDeadlineDescr: `Робочі процеси повинні бути запитані до терміну.
  Встановіть, скільки днів заздалегідь нижче.
  Перемикач можна налаштувати для блокування пізніх запитів на робочі процеси.
  Користувач отримає попередження і повинен буде вибрати день, який знаходиться далі вперед`,
  workflowsMustBeRequested: 'Робочі процеси повинні бути запитані заздалегідь',
  workflowWasntRequestedInAdvance: (interval: string) =>
    `Цей робочий процес не був запитаний${
      interval ? ` ${interval}` : ''
    } заздалегідь`,
  workflowDateTimeBlocked: 'Дата та час робочого процесу заблоковані',
  pleaseMoveThisWorkflow: (days: number) =>
    `Будь ласка змініть дату чи час цього Робочого Процесу. Запит має бути створений щонайменше за ${days} ` +
    `робоч${ending(days, 'ий', 'і', 'их')} ` +
    `д${ending(days, 'ень', 'ні', 'нів')} ` +
    `від дати та часу, яку Ви вказуєте та у робочий час.`,
  requestsMustBeInAdvance: (days: number) =>
    `Запит має бути створений щонайменше за ${days} ` +
    `робоч${ending(days, 'ий', 'і', 'их')} ` +
    `д${ending(days, 'ень', 'ні', 'нів')} ` +
    `від дати та часу, яку Ви вказуєте`,
  hide: 'Сховати',
  warnUsers: 'Попередити Користувачів',
  blockUsers: 'Заблокувати Користувачів',
  duplicateCurrentWorkflow: 'Дублювати поточний робочий процес',
  mapNotifications: 'Сповіщення мапи',
  olderWorkflowCantBeDuplicate:
    'Старішу версію робочого процесу не можна дублювати.',
  invalidNumberFormat: 'Невірний формат числа',

  restrooms: 'Вбиральні',
  canteen: 'Їдальня',
  toilet: 'Туалет',
  breakText: 'Перерва',
  dumpster: 'Смітник',
  elevator: 'Ліфт',
  electricalRoom: 'Електрощитова',
  access: 'Доступ',
  washArea: 'Зона миття',
  medicalArea: 'Медична зона',
  musterPoint: 'Пункт збору',
  parking: 'Парковка',
  smokingArea: 'Зона для куріння',
  stairway: 'Сходи',
  covidCheckpoint: 'Пункт перевірки Covid',
  breakArea: 'Зона відпочинку',
  walkway: 'Пішохідний прохід',
  trailer: 'Вагончик',
  informationPoints: 'Інформаційні пункти',
  glyphNames: {
    truck: 'Вантажівка',
    'concrete-mixer-truck': 'Бетонозмішувач',
    'equipment-storage': 'Склад обладнання',
    'material-storage-boxes': 'Склад: коробки',
    'material-storage-wood': 'Склад: деревина',
    'material-storage-steel': 'Склад: сталь',
    'material-storage-pipes': 'Склад: труби',
    'material-lifting-steel': 'Підйом сталі',
    scaffolding: 'Риштування',
    'swing-stage': 'Підвісна люлька',
    'personnel-lift': 'Підйомник для людей',
    info: 'Інфо',
    'site-map': 'План майданчика',
    'log-out': 'Вихід',
    'no-entry': 'Вхід заборонено',
    stop: 'Стоп',
    'no-pedestrians': 'Пішоходам заборонено',
    'no-access': 'Доступ заборонено',
    'security-checkpoint-window': 'Пункт охорони',
    'visitor-check': 'Реєстрація відвідувачів',
    'high-visibility-vest': 'Сигнальний жилет',
    'safety-helmet': 'Захисна каска',
    'safety-gloves': 'Захисні рукавиці',
    'safety-toe-boots': 'Захисне взуття',
    'ppe-not-required': 'ЗІЗ не потрібні',
    'no-vest': 'Без жилета',
    'no-helmet': 'Без каски',
    'no-gloves': 'Без рукавиць',
    'no-boots': 'Без взуття',
    'cleanroom-gowning': 'Чисте приміщення / перевдягання',
    'icra-zone': 'Зона ICRA',
    'negative-pressure': 'Негативний тиск',
    'esd-zone': 'Зона ESD',
    'no-photography': 'Фотографувати заборонено',
    'no-drones': 'Дрони заборонено',
    'fall-hazard': 'Небезпека падіння',
    'silica-dust': 'Кремнезем / пил',
    noise: 'Шум',
    'confined-space': 'Замкнений простір',
    'energized-equipment': 'Обладнання під напругою',
    'bike-parking': 'Велопарковка',
    'utility-cart': 'Візок',
    'loading-zone': 'Зона завантаження',
    'fire-lane': 'Пожежний проїзд',
    'flagger-ahead': 'Регулювальник попереду',
    'one-way': 'Односторонній рух',
    'speed-limit': 'Обмеження швидкості',
    trash: 'Сміття',
    'admin-office': 'Адміністративний офіс',
    'hand-wash-station': 'Пункт миття рук',
    'spill-kit': 'Набір для розливів',
    'erosion-control': 'Протиерозійний захист',
    'storm-drain-inlet-protection': 'Захист зливоприймачів',
    'concrete-washout': 'Мийка бетонозмішувачів',
    'wheel-wash': 'Мийка коліс',
    'restroom-men': 'Чоловіча вбиральня',
    'restroom-women': 'Жіноча вбиральня',
    sofa: 'Кімната відпочинку',
    hydration: 'Пиття',
    'heart-pulse': 'АЗД',
    eyewash: 'Фонтанчик для очей',
    'fire-extinguisher': 'Вогнегасник',
    'fire-hydrant': 'Пожежний гідрант',
    'fire-alarm-button': 'Кнопка пожежної тривоги',
    'standpipe-hose': 'Пожежний стояк / рукав',
    flame: 'Вогневі роботи',
    excavator: 'Екскаватор',
    'overhead-load': 'Підвішений вантаж',
    'brand-oes': 'OES Equipment',
    'brand-sunbelt': 'Sunbelt Rentals',
    'brand-united-rentals': 'United Rentals',
    'brand-equipmentshare': 'EquipmentShare',
    'brand-feco': 'First Equipment Company',
    'brand-herc-rentals': 'Herc Rentals',
  },
  informationPointTypes: {
    general_information_point: 'Загальна інформаційна точка',
    access: 'Доступ',
    exit: 'Вихід',
    restricted_area: 'Зона обмеженого доступу',
    security_checkpoint: 'Пункт охорони',
    ppe_zone: 'Зона ЗІЗ',
    controlled_environment: 'Контрольоване середовище',
    site_rules: 'Правила майданчика',
    hazard_zone: 'Небезпечна зона',
    confined_space: 'Замкнений простір',
    energized_equipment: 'Обладнання під напругою',
    walkway: 'Пішохідний прохід',
    parking: 'Парковка',
    loading: 'Завантаження',
    traffic_control: 'Регулювання руху',
    waste: 'Відходи',
    stormwater_erosion_control: 'Зливові води та ерозія',
    elevated_access: 'Доступ на висоту',
    site_office: 'Офіс майданчика',
    trailer: 'Вагончик',
    electrical_room: 'Електрощитова',
    utilities: 'Інженерні мережі',
    restrooms: 'Вбиральні',
    break_area: 'Зона відпочинку',
    canteen: 'Їдальня',
    hydration_station: 'Пункт питної води',
    handwash_station: 'Пункт миття рук',
    smoking_area: 'Зона для куріння',
    medical_area: 'Медична зона',
    fire_protection: 'Пожежний захист',
    muster_point: 'Пункт збору',
  },
  informationPointGroups: {
    general: 'Загальне',
    entry_restrictions: 'Доступ і обмеження',
    hazards: 'Небезпеки',
    site_logistics: 'Логістика майданчика',
    offices_utilities: 'Офіси та мережі',
    restrooms_breaks: 'Побутові приміщення',
    emergency: 'Екстрена допомога',
  },
  informationPointPicker: {
    title: 'Розмістити точку',
    byType: 'За типом',
    byIcon: 'За значком',
    xTypes: (x: number) => `${x} Тип${ending(x, '', 'и', 'ів')}`,
  },

  shaft: 'Вал',
  crane: 'Кран',
  hoist: 'Лебідка',
  aerialLift: 'Підйомник',
  forkLift: 'Вилковий навантажувач',
  gradall: 'Gradall',
  allFormsSharingCalendarEnabled: 'Поширення всіх форм в календар застосовано',
  allDeliveriesSharingCalendarEnabled:
    'Поширення всіх доставок в календар застосовано',
  allFormsSharingNotificationEnabled:
    'Поширення всіх форм до сповіщень застосовано',
  allDeliveriesSharingNotificationEnabled:
    'Поширення всіх доставок до сповіщень застосовано',

  workflowTitle: 'Заголовок робочого процесу',
  workflowStatus: 'Статус робочого процесу',

  openWorkflow: 'Відкрити робочий процес',
  attachmentPhotoAdded: 'Прикріплення/Фото було додано',
  attachment: 'Додаток',
  sharingAllFormsToCalendarAndNotificationsEnabled:
    'Увімкнено спільний доступ до всіх форм у календарі та сповіщеннях',
  sharingAllDeliveriesToCalendarAndNotificationsEnabled:
    'Увімкнено спільний доступ до всіх доставок до календаря та сповіщень',
  fromRules: 'З правил',
  calendarLinkInputText:
    'Ви можете надати це посилання стороннім програмам-календарям, як-от Календар Google, TeamUp, Outlook тощо',
  enableIcal: 'Увімкнути iCalendar',
  generate: 'Згенерувати',

  ended: 'Завершено',
  externalWorkflowSharing: 'Спільний доступ до зовнішнього робочого процесу',
  addOtherStxCalendarsToProject: 'Додати інші календарі StruxHub до проекту',
  shareBasedOnSettingsIn: 'Поділитися на основі налаштувань у',
  ifSelected: 'Якщо вибрано',
  onlyWorkflowsSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, спільний доступ буде надано лише до Форм, що відповідають правилам модуля «${module}».`,
  onlyDeliveriesSatisfyingModuleRulesWillBeShared: (module: string) =>
    `, спільний доступ буде надано лише до Доставок, що відповідають правилам модуля «${module}».`,

  autoFinishWorkflowOnFinishDate:
    'Автоматично завершити робочий процес на дату завершення',
  executeAutomaticFinish: 'Виконати автоматичне завершення',
  atAnyStep: 'На будь-якому етапі',
  onlyAt: 'Тільки на',
  onlyAtOrAfter: 'Тільки на або після',
  youHaveUnsavedChanges: 'У вас є незбережені зміни.',
  unsavedChangesDiscarded: 'Усі незбережені зміни будуть втрачені.',
  stay: 'Залишитися',
  continue: 'Продовжити',
  workflowMessageAdded: 'Додано повідомлення до робочого процесу',
  createdBy: 'Створено',
  categoryCreatedByStruxHub: 'Категорія створена StruxHub',
  from: 'Від',
  to: 'До',
  _to: 'на',
  shareAllForms: 'Поділитися всіма формами',
  shareAllDeliveries: 'Поділитися всіма доставками',
  formSharing: 'Спільний доступ до форм',
  deliverySharing: 'Спільний доступ до доставок',
  chooseDate: 'Виберіть дату',
  linkForProjectCopiedToClipboard: (projectCode: string) =>
    `Посилання на проєкт ${projectCode} скопійовано в буфер обміну!`,
  invitedSuccessfully: 'Запрошення успішно надіслано!',
  unsubscriberSuccessfully: 'Відписка успішно виконана!',
  provideLink: 'Надайте посилання!',
  youCannotSubscribeToYourOwnProject:
    'Ви не можете підписатися на власний проєкт!',
  fetchingEventsOnBackground: 'Отримання подій у фоновому режимі...',
  importedSuccessfully: 'Успішно імпортовано!',
  thisProjectIsAlreadyInvited: 'Цей проєкт вже запрошено!',
  confirmDeleteSubscriber: (subscriberCode: string) =>
    `Ви впевнені, що хочете видалити проєкт ${subscriberCode}?`,
  sharedFromWithName: companyName => `Поширено від ${companyName}`,
  sharedFrom: `Поширено від`,
  enableRestrictions: 'Увімкнути обмеження за кодами запрошень',
  notProvided: 'Не вказано',
  shared: 'Спільний доступ',
  generateAndCopyLinkForStxProjects:
    'Згенерувати та скопіювати посилання для проєктів StruxHub:',
  inviteStruxProjectsByItsCode: 'Запросити інші проєкти StruxHub за їх кодом',
  invitedProjectCodes: 'Коди запрошених проєктів',
  youDontHaveAnyInviteesYet: 'У вас ще немає запрошених.',
  subscribeToOtherStruxHubProjects: 'Підписатися на інші проєкти StruxHub',
  subscribeToExternalProjects: 'Підписатися на зовнішні проєкти',
  pastUrlFromOtherProject: 'Вставте URL іншого проєкту',
  yourSubscriptions: 'Ваші підписки',
  youDoNotHaveAccessToProject: 'У вас немає доступу до проєкту',
  youDontHaveAnySubscriptionsYet: 'У вас ще немає підписок.',
  youSubscribedToNExternalProjects: (n: number) =>
    `Ви підписалися на ${n} зовнішній проєкт${ending(n, '', 'а', 'ів')}`,
  visibility: 'Видимість',
  inviteLink: 'Посилання на запрошення',
  sharedProjectName: 'Назва поширеного проєкту',
  dontShare: 'Не поширювати',
  disableAccess: 'Заборонити доступ',
  shareAllDeliveriesWithinSpecificDate: 'Поширити всі доставки в певний період',
  shareAllFormsWithinSpecificDate: 'Поширити всі форми в певний період',
  areYouSureYouWantToDisableAccess: 'Ви впевнені, що хочете заборонити доступ?',
  deleteSubscription: 'Видалити підписку',
  allExternalProjectsWillLosePermission:
    'Всі зовнішні проєкти, які використовують цей посилання як підписку на ваш проєкт, втратять дозвіл на перегляд ваших робочих процесів.',
  areYouSureYouWantToDeleteSubscription:
    'Ви впевнені, що хочете видалити підписку на проєкт?',
  inThisCaseYouWillNotBeAbleToSeeAnyWorkflowsFromThisProject:
    'У цьому випадку ви не зможете побачити жодного робочого процесу з цього проєкту',
  sharingSettings: 'Налаштування поширення',
  shareIcalCalendarLink: 'Поширити iCal календар',
  shareToExternalProject: 'Поширено до зовнішнього проєкту',
  generateShareableLink: `Створіть посилання, яке можна поширити, щоб зовнішні власники проєктів StruxHub могли переглядати ваші робочі процеси на основі ваших налаштувань поширення.`,
  generateShareableCalendarLink: `Створіть посилання, яке можна поширити, щоб зовнішні програми-календарі
    (Apple Calendar, Google Calendar, тощо) могли переглядати ваші робочі процеси на основі ваших налаштувань поширення.`,
  subscribeShareableLink: `Вставте посилання, щоб отримати дані з зовнішнього проєкту StruxHub.`,
  ifTheProjectIsEnabled:
    'Якщо перемикач увімкнено, зовнішній проєкт зможе переглядати ваші поширені робочі процеси на основі налаштувань поширення, які ви налаштували.',
  ifTheProjectIsDisabled:
    'Якщо перемикач вимкнено, зовнішній проєкт не зможе переглядати жодного поширеного робочого процесу.',
  import: 'Імпортувати',
  preparingUpload: 'Підготовка завантаження...',
  uploadEquipmentFile:
    'Завантажте ваш файл обладнання у форматі Excel (.xlsx, .xlsm) або CSV (.csv).',
  importComplete: 'Імпорт завершено',
  created: 'Створено:',
  skippedValues: 'Деякі Властивості залишились порожніми',
  skippedValuesDescription:
    'Усі рядки були створені, але Властивості нижче не вдалося зіставити з існуючими записами, і вони були збережені порожніми.',
  xPropertiesSkipped: (n: number) => `${xProperties(n)} пропущено`,
  mapColumnsToProperties: 'Зіставити колонки з властивостями',
  id: 'ID',
  selectIdColumn: 'Оберіть колонку ID',
  selectTitleColumn: 'Оберіть колонку назви',
  selectColumn: 'Оберіть колонку',
  template: 'Шаблон',
  selectTemplate: 'Оберіть шаблон',
  loadingProperties: 'Завантаження властивостей...',
  equipmentProperties: (n: number) => `Властивості обладнання (${n})`,
  materialProperties: (n: number) => `Властивості матеріалів (${n})`,
  locationProperties: (n: number) => `Властивості розташування (${n})`,
  inviteCodeCantBeEmpty: 'Код запрошення не має бути пустим.',
  goToTheProject: 'Перейти до проекту',
  locationsFrom: 'Місця відправлення',
  locationsTo: 'Місця призначення',
  goToLocation: (locationName: string) => `Перейти до ${locationName}`,
  callTo: (name: string) => `Зателефонувати ${name}`,
  mailTo: (name: string) => `Надіслати листа ${name}`,
  goToTheBuilding: 'Перейти до будівлі',
  selectWorkflowType: 'Виберіть Тип Робочого Процесу',
  createNew: 'Створити новий',
  defaultDuration: 'Тривалість за замовчуванням',
  maximumDuration: 'Максимальна тривалість',
  maxDurationError: (maxDuration: string) =>
    `Тривалість між «початком» та «кінцем» перевищує максимально дозволений ліміт у ${maxDuration}`,
  nonWorkDayOrDays: 'Неробочий день/дні',
  nonWorkHours: 'Неробочі години',
  failedToSave: 'Не вдалося зберегти',
  failedToSaveChangesTryAgain:
    'Не вдалося зберегти зміни. Будь ласка, спробуйте ще раз.',
  failedToDeleteFormsTryAgain:
    'Не вдалося видалити форми. Будь ласка, спробуйте ще раз.',

  filterType: 'Фільтрувати тип',
  workWeek: 'Робочий тиждень',
  maximumDurationTime: 'Максимальний час тривалості',
  defaultDurationTime: 'Час тривалості за замовчуванням',
  allowRepeatingWorkflow: 'Дозволити повторювати робочий процес',
  maximumRepeatingDuration: 'Максимальна тривалість повторення',
  iCalSharingSettings: 'Налаштування спільного доступу до iCal',
  nothing: 'нічого',
  if: 'Якщо',
  thenShow: 'тоді показати',
  showAllSettings: 'Показати всі налаштування',
  hideAllSettings: 'Сховати всі налаштування',
  addDependentField: 'Додати залежне поле',
  addField: 'Додати поле',
  alreadyAdded: 'Вже додано',
  item: 'Елемент',
  addAssociatedField: 'Додати поле елемента',
  associatedFields: 'Поля елемента',
  xAssociatedFields: (x: number) =>
    `${x} Пол${ending(x, 'е', 'я', 'ів')} елемента додано`,
  addTableColumn: 'Додати стовпець таблиці',
  tableColumns: 'Стовпці таблиці',
  xTableColumns: (x: number) =>
    `${x} Стовп${ending(x, 'ець таблиці', 'ці таблиці', 'ців таблиці')}`,
  newRule: 'Нове правило',
  inactive: 'Неактивне',
  repeatWorkflow: 'Повторити робочий процес',
  thisWorkflow: 'Цей робочий процес',
  showOnSourceMap: 'Показати на початковій карті',
  thisWorkflowIsPinnedOnADifferentMapView:
    'Цей робочий процес закріплено на іншому вигляді карти',
  thisWorkflowIsHiddenOnTheMap: 'Цей робочий процес прихований на карті',
  switchedToMapViewX: (name: string) => `Перемкнено на вигляд карти ${name}`,
  thisWorkflowIsNotShownOnThisMapView:
    'Цей робочий процес не відображається на цьому вигляді карти',
  thisWorkflowHasNoPinOnAnyMapView:
    'Цей робочий процес не має маркера на жодному вигляді карти',
  thisAndFutureWorkflows: 'Цей та майбутні робочі процеси',
  usedInMaterialsTableCalculations:
    'Використовується в розрахунках таблиці матеріалів',
  usedToSpecifySourceLocationInMaterialsTable:
    'Використовується для вказування джерела розташування в таблиці матеріалів',

  note: 'Примітка',
  dependentFieldsTooltip: {
    title: 'Залежні поля',
    description: `Створюйте правила для динамічного показу інших полів
    на основі значення, вибраного в цьому полі.`,
  },
  dynamicFiltersTooltip: {
    title: 'Динамічні фільтри',
    description:
      'Створюйте фільтри для динамічного показу опцій на основі значень полів та умов.',
  },
  selection: 'Вибір',
  selectionSettings: 'Параметри вибору',
  conditionalFilters: 'Умовні фільтри',
  xConditionalFilters: (x: number) =>
    `${x} Умовн${ending(x, 'ий фільтр', 'і фільтри', 'их фільтрів')}`,
  addConditionalFilter: 'Додати умовний фільтр',
  conditionalFields: 'Умовні поля',
  xConditionalFields: (x: number) =>
    `${x} Умовн${ending(x, 'е поле', 'і поля', 'их полів')}`,
  addConditionalField: 'Додати умовне поле',
  selectionTooltip:
    'Визначте, як користувачі додають елементи \u2014 створювати, вибирати або обидва варіанти. Встановіть сортування, групування та фільтри за замовчуванням.',
  itemFieldsTooltip:
    'Зберігайте додаткові деталі для кожного елемента. Кожен елемент стає рядком; ці поля є його стовпцями.',
  conditionalFiltersTooltip:
    'Фільтруйте список доступних елементів на основі значень інших полів у цій формі.',
  conditionalFieldsTooltip:
    'Показувати додаткові поля лише коли вибрано конкретний елемент.',
  cardView: 'Перегляд картки',
  lastUpdatedBy: 'Востаннє оновлено',
  deleteFormsConfirm: (x: number) => `Видалити ${x} форм(и)?`,
  removeSelected: 'Видалити обрані',
  addOption: 'Додати опцію',
  optionName: 'Назва опції',
  removed: 'видалено',
  editProperty: 'Редагувати властивість',
  createNewProperty: 'Створити нову властивість',
  deleteProperty: 'Видалити властивість',
  archiveProperty: 'Архівувати властивість',
  unarchiveProperty: 'Розархівувати властивість',
  typeToConfirm: 'Введіть для підтвердження',
  propertyUsedByXResources: (x: number) =>
    `Ця Властивість використовується ${x} Ресурс${ending(
      x,
      'ом',
      'ами',
      'ами',
    )}.`,
  deletePropertyConfirmation: (count: number) =>
    count > 0
      ? `Видалення назавжди видалить цю Властивість та всі її дані з цих Ресурсів. Введіть кількість пов'язаних Ресурсів або "Delete" для підтвердження.`
      : 'Ця Властивість не використовується жодним Ресурсом. Вона буде видалена назавжди.',
  archivePropertyConfirmation: (count: number) =>
    count > 0
      ? `Архівування видалить цю Властивість з активного використання на цих Ресурсах. Це можна скасувати. Введіть кількість пов'язаних Ресурсів або "Archive" для підтвердження.`
      : 'Ця Властивість не використовується жодним Ресурсом. Вона буде архівована.',
  propertyDetails: 'Деталі властивості',
  propertyPreview: 'Попередній перегляд',
  propertyPreviewUnavailable: 'Попередній перегляд недоступний',
  configurePropertyBeforePreview:
    'Налаштуйте деталі властивості перед попереднім переглядом.',
  managedRelationPreviewHelp:
    'Цим зв’язком можна керувати після створення ресурсу.',
  calculatedPropertyPreviewHelp:
    'Це значення обчислюється автоматично, і його не можна редагувати.',
  unsupportedPropertyPreviewHelp:
    'Попередній перегляд цього типу властивості недоступний.',
  backToDetails: 'Назад до деталей',
  textSettings: 'Налаштування тексту',
  enableMultilineInput: 'Увімкнути багаторядковий ввід',
  internalName: 'Внутрішня назва',
  internalNameCannotBeChanged: 'Внутрішню назву не можна змінити',
  dropdownOptionsSettings: 'Налаштування випадаючих опцій',
  newOption: 'Нова опція',
  xOptionsAdded: (x: number) =>
    `${x} ${ending(x, 'опція додана', 'опції додані', 'опцій додано')}`,
  allowUsersToCreateNewOptions: 'Дозволити користувачу створювати нові опції',
  allowUsersToSelectExistingWorkflows:
    'Allow Users to select existing workflows',
  allowUsersToCreateNewWorkflow: 'Allow Users to create new workflow',
  selectExisting: 'Вибрати існуючий',
  noResultsFound: 'Результатів не знайдено',
  allowUsersToSelectExisting: (label: string) =>
    `Дозволити користувачам вибирати існуючі ${label}`,
  allowUsersToCreateNew: (label: string) =>
    `Дозволити користувачам створювати нові ${label}`,
  allowToSelect: 'Дозволити вибір',
  activityFieldRestrictedTooltip:
    'Поле Активність обмежене лише типами робочих процесів Активність.',
  cantSeeResource: (resource: string) => `Не бачите ${resource}?`,
  openNonWorkflowResources: (resource: string) =>
    `Відкрити ${resource}, що не є робочими ресурсами`,
  alsoManageFromControls: 'Крім того, ви можете внести ці зміни на',
  resourceControlsPage: (resource: string) =>
    `сторінці «Елементи керування: ${resource}»`,
  enablingResourcesHint: (resource: string) =>
    `Ці ${resource} ще не є Робочими ресурсами. Збереження вмикає їхній перемикач «WR» і дозволяє їх для цього поля, а також для будь-якого поля зі значенням «Будь-який ${resource}».`,
  anyWorkflowTypes: 'Будь-які типи робочих процесів',
  allowNoneSelection: "Дозволити вибір 'Немає'",
  saveChanges: 'Зберегти зміни',
  noIconsFoundByYourRequest: 'Не знайдено значків за вашим запитом',
  generalWorkflow: 'Загальний робочий процес',
  ganttChart: 'Діаграма Ганта',
  activity: 'Діяльність',
  automation: 'Автоматизація',
  craneBooking: 'Бронювання крана',
  deliveryBooking: 'Delivery Booking',
  environmental: 'Екологія',
  airMitigation: 'Мінімізація впливу',
  eco: 'ЕКО',
  authorizedVehicleAccess: 'Дозволений доступ до транспортних засобів',
  fireExtinguisherPermit: 'Дозвіл на використання вогнегасника',
  heavyEquipmentUsageLog: 'Журнал використання важких машин',
  ladderPermit: 'Дозвіл на використання лестні',
  surfacePenetrationPermit: 'Дозвіл на проникнення поверхні',
  interiorConcretePenetrationPermit: 'Дозвіл на проникнення всередині/бетон',
  aerialLiftInspection: 'Інспекція підйомника',
  forkliftRoomWorkPermit: 'Дозвіл на використання візка',
  materialTransfer: 'Передача матеріалів',
  procurementTracking: 'Слідкування за закупівлями',
  quality: 'Якість',
  qualityControlInspection: 'Контроль якості',
  report: 'Звіт',
  timesheet: 'Робочий час',
  safety: 'Безпека',
  energizedRoomWorkPermit: 'Дозвіл на роботу в енергозабезпеченій кімнаті',
  hotWorkPermit: 'Дозвіл на роботу в горячій кімнаті',
  jobHazardAnalysis: 'Аналіз ризиків роботи',
  siteCommunication: 'Комунікація сайту',
  closureRequest: 'Запит на закриття',
  siteAnnouncement: 'Анонс сайту',
  comingSoon: 'Скоро буде доступно',
  incorrectDateOrTime: 'Неправильна дата або час',
  invalidSettings: 'Неправильні налаштування',
  imageDimensionsCanNotBeGreaterThan: (
    maxWidth?: number,
    maxHeight?: number,
  ) => {
    if (maxWidth && maxHeight) {
      return `Розміри зображення не можуть бути більше ніж ${maxWidth}x${maxHeight}`
    } else if (maxWidth) {
      return `Ширина зображення не може бути більше ніж ${maxWidth}px`
    } else if (maxHeight) {
      return `Висота зображення не може бути більше ніж ${maxHeight}px`
    }
    return 'Недійсні розміри зображення'
  },
  fileSizeCanNotBeGreaterThan: (maxFileSize: string) =>
    `Розмір файлу не може бути більше ніж ${maxFileSize}`,
  controlledAccessZone: 'Зона обмеженого доступу',
  guardrailRemovalPermit: 'Дозвіл на демонтаж огородження',
  roofAccessPermit: 'Дозвіл на доступ на дах',
  smartFormImport: 'Інтелектуальний імпорт робочого процесу',
  smartFormImportDescription:
    'Завантажте файл PDF або зображення, і штучний інтелект визначить і відобразить автоматично поля.',
  dragAndDropFile: 'Перетягніть файл',
  or: 'або',
  chooseFile: 'Виберіть файл',
  fileSupportText: 'Підтримує файли PDF, JPG, PNG',
  importFile: 'Імпорт файлу',
  workingOnIt: '🛠️ Ми працюємо над цим.',
  detectingHeaders: '🔎 Визначення заголовків…',
  findingFields: '📑 Знаходження полів…',
  scanningCheckboxes: '☑️ Сканування чекбоксів…',
  organizingWorkflowSteps: '🗂️ Організація кроків робочого процесу…',
  aligningWithStruxHubSchema: '🧩 Вирівнювання зі схемою StruxHub…',
  doubleCheckingFieldTypes: '🛡️ Двократне перевірка типів полів…',
  tidyingUpYourForm:
    '🧹 Прибираємо ваш формуляр (краще, ніж візка на будівельному майданчику).',
  sharpeningDigitalPencils: '✏️ Актуалізуємо цифрові олівці…',
  sortingThingsIntoTheRightBins: '🏗️ Сортуємо речі в правильні контейнери…',
  moveFieldsIntoDifferentWorkflowStepsAfterImport:
    '💡 Ви можете переміщати поля в різні кроки робочого процесу після імпорту.',
  addWorkflowStepsForApprovalsInspectionsAndStartTimes:
    '💡 Ви можете додавати кроки робочого процесу для погоджень, перевірок та початкових часів.',
  routeWorkflowStepsToBallInCourtUsersWithConditionalLogic:
    '💡 Ви можете маршрутизувати кроки робочого процесу до користувачів-полегшених з умовним логікою.',
  notifyWorkflowMembersAfterSpecificSteps:
    '💡 Ви можете сповіщати членів робочого процесу після конкретних кроків.',
  triggerResourceClosureAfterSpecificWorkflowStep:
    '💡 Ви можете активувати закриття ресурсу після конкретного кроку робочого процесу.',
  linkWorkflowToAnotherWorkflow:
    "💡 Ви можете пов'язати робочий процес з іншим робочим процесом через поле «Пов'язаний робочий процес».",
  createTemplatesFromImportedFormsToReuseAcrossProjects:
    '💡 Ви можете створювати шаблони з імпортованих форм для повторного використання на проектах.',
  assignResponsibilityForWorkflowStepsToSpecificCompanies:
    '💡 Ви можете призначати відповідальність за кроки робочого процесу для конкретних компаній.',
  rollUpFormDataIntoExportableReports:
    '💡 Ви можете збирати дані з форм в експортовані звіти.',
  attachDocumentsAndPhotosToAnyWorkflow:
    '💡 Ви можете прикріплювати документи та фотографії до будь-якого робочого процесу.',
  safetyFirstNoHardHatRequiredWhileWeProcessYourForm:
    '🦺 Безпека в першу чергу: не потрібен шолом під час обробки вашого формуляра.',
  ourScannersLiftFormsFasterThanALifterLiftsSteel:
    '🚜 Наші скануючі пристрої піднімають форми швидше, ніж крани піднімають сталь.',
  everSeenPaperworkMoveThisFastOnAJobsiteNeitherHaveWe:
    '🏃 Ви коли-небудь бачили, як папір рухається так швидко на будівельному майданчику? Ні наші.',
  importingFormsNoRFIsRequired: '📨 Імпорт форм — не потрібні RFIs.',
  dontWorryThisWontTakeAsLongAsAConcretePour:
    '🏗️ Не хвилюйтеся, це не займе так багато часу, як це стало б з бетонним залізом.',
  processingFormsFasterThanFindingTheRightWrenchInYourToolbox:
    '🔧 Обробка форм швидше, ніж знаходження правильного ключа в вашому інструменті.',
  proTipThisIsTheOnlyTimePaperworkWillDoTheWorkForYou:
    '🤓 Професійна порада: це єдина епоха, коли папір зможе зробити роботу за вас.',
  scanningWithoutThePaperCuts: '✋ Сканування… без скрапок паперу.',
  ourImportToolDoesntNeedAForemanYellingAtIt:
    '📣 Наш інструмент імпорту не потребує, щоб керівник майданчику кричав на нього.',
  almostThereUnlikeThatElevatorInstallation:
    '🚧 Майже там — на відміну від установки ліфта.',
  wrappingThingsUp: '🎁 Завершуємо роботу…',
  puttingFinishingTouchesOnYourForm: '✅ Завершуємо ваш формуляр…',
  justOneMoreCheck: '📦 Ще одна перевірка…',
  workflowWasSuccessfullyImported: (workflowName: string) =>
    `🎉 ${workflowName} було успішно імпортовано!`,
  somethingWentWrongWhileImportingFile:
    'Щось пішло не так під час імпортування вашого файлу',
  importInProgress: 'Імпорт вже виконується',
  viewWorkflow: 'Переглянути робочий процес',
  enableMaximumDuration: 'Увімкнути максимальну тривалість',
  enableDefaultDuration: 'Увімкнути тривалість за замовчуванням',
  minute_plural: (x: number) => `Хвилина${ending(x, 'у', 'и', '')}`,
  hour_plural: (x: number) => `Година${ending(x, 'у', 'и', '')}`,
  day_plural: (x: number) => `День${ending(x, 'у', 'и', '')}`,
  week_plural: (x: number) => `Тиждень${ending(x, 'у', 'и', '')}`,
  month_plural: (x: number) => `Місяць${ending(x, 'у', 'и', '')}`,
  durationCannotBeGreaterThan: `Тривалість не може бути більше ніж`,
  saveAsDraft: 'Зберегти як чернетку',
  draftSavedSuccessfully:
    'Чернетку успішно збережено. Всі зміни були збережені.',
  createdXofYWorkflows: (created: number, total: number) =>
    `Створено ${created} з ${total} робочих процесів`,
  skippedWorkflows: 'Пропущено',
  unknownLocation: 'Невідома локація',
  blockedByClosure: 'заблоковано закриттям у',
  blockedByProjectClosed: 'заблоковано інтервалом закриття проекту',
  blockedByNonWorkingDay: 'у неробочі дні',
  blockedByOperatingHours: 'поза робочими годинами для',
  reservedForOtherCompanies: 'Зарезервовано для інших компаній',
  discardedUnsavedDrawing:
    'Ваш незбережений малюнок скасовано, оскільки карту було закрито',
  failedToCreateWorkflow:
    'Не вдалося створити робочий процес. Спробуйте ще раз.',
  failedToCreateRecurringWorkflows:
    'Не вдалося створити повторювані робочі процеси. Спробуйте ще раз або зверніться до служби підтримки',
  unavailableDraftWorkflowsSelected:
    'Недоступно: Один або кілька вибраних робочих процесів знаходяться в статусі чернетки',
  draft_noun: 'Чернетка',
  linkedWorkflows: `Пов’язані робочі процеси`,
  addLinkedWorkflow: `Додати пов’язаний робочий процес`,
  dateTime: 'Дата і Час',
  file: 'Файл',
  allWorkflow_s: 'Всі робочі процеси',
  appliedTo: (count: number) => `Застосовано до (${count})`,
  closuresConflictTitle: 'Конфлікти Закриттів',
  closuresConflictMessage:
    'Вибрані локації мають закриття, що конфліктують з датами Workflow. Конфліктні локації були автоматично видалені. Перегляньте форму та за потреби відкоригуйте.',
  closuresConflictRecurringMessage:
    'Деякі повторювані появи припадають на закриті дати. Ви можете продовжити створення повторюваного Workflow або переглянути та скоригувати вибір.',
  selectWorkflowTypes: 'Виберіть Типи Робочих Процесів',

  resourcePropertyText: 'Текст',
  resourcePropertyNumber: 'Число',
  resourcePropertySelect: 'Випадаючий список',
  resourcePropertyHierarchy: 'Ієрархія',
  resourcePropertyCheckbox: 'Прапорець',
  resourcePropertyDatetime: 'Дата/Час',
  resourcePropertyRelation: "Зв'язок",
  resourcePropertyLocation: 'Локація',
  resourcePropertyRollup: 'Поле підсумку',
  resourcePropertyComputed: 'Обчислюване поле',
  resourcePropertyUser: 'Користувач',
  resourcePropertyCompany: 'Компанія',
  resourcePropertyFile: 'Файл',
  uploadFiles: 'Завантажити файли',
  uploadFile: 'Завантажити файл',
  attachFile: 'Прикріпити файл',
  attachFiles: 'Прикріпити файли',
  camera: 'Камера',
  maxFilesReached: 'Досягнуто максимальну кількість файлів',
  fileTooLarge: (maxMB: number) => `Файл занадто великий (макс. ${maxMB} МБ)`,
  sitemapsTooLargeForMobile:
    'Деякі мапи сайту завеликі для відображення в застосунку. Відкрийте StruxHub у браузері, щоб переглянути їх.',
  fileTypeNotAllowed: 'Тип файлу не дозволено',
  pdfPageImagesCouldNotBeRendered:
    'Деякі зображення на цій сторінці PDF не вдалося відобразити, тому їх немає на імпортованій мапі.',
  associatedProperties: 'Пов’язані властивості',
  appliedToColumn: 'Застосовано до',
  xResourceObjects: (count: number, resourceTypeId: string) => {
    const noun =
      (
        {
          offloading_equipment: 'обладнання',
          material: 'матеріалу',
          sensor: 'сенсору',
        } as Record<string, string>
      )[resourceTypeId] ?? ''
    return `${count} Об'єкт${ending(count, '', 'и', 'ів')} ${noun}`
  },
  deleteSelected: 'Видалити вибрані',
  selectASingleWorkflowToDuplicate:
    'Виберіть один робочий процес для дублювання',
  subscribeSelected: 'Підписати вибрані',
  assignSelected: 'Назначити вибрані',
  doYouWantToDeleteSelectedWorkflows:
    'Ви впевнені, що хочете видалити всі вибрані робочі процеси?',
  allDataRelatedToSelectedWorkflowsWillBeDeleted:
    "Всі дані, пов'язані з вибраними робочими процесами, будуть постійно видалені і не можуть бути відновлені.",
  statuses: 'статуси',
  updateSelectedWorkflowsStatusTo:
    'Оновити статус вибраних Робочих Процесів на:',
  youAreGoingToUpdate: 'Ви збираєтеся оновити',
  _for: 'для',
  updateXWorkflows: (x: number) =>
    `Оновити ${x} Робочий процес${ending(x, 'у', 'и', '')}`,
  subscribeUsers: 'Підписати користувачів',
  subscribed: 'Підписаний',
  noUsersHaveSubscribedYet: 'Ніхто ще не підписався',
  thisUserHasSubscribedToOnlySomeOfTheWorkflowsYouSelected:
    'Цей користувач підписався лише на деякі з вибраних вами робочих процесів.',
  subscribedTo: 'Підписаний до:',
  notSubscribedTo: 'Не підписаний до:',
  signature: 'Підпис',
  signaturePreview: 'Попередній перегляд підпису',
  draw: 'Малювати',
  typeHere: 'Введіть тут',
  addTextHere: 'Додайте текст тут',
  signHere: 'Підпишіть тут',
  editStyle: 'Редагувати стиль',
  clickToAddSignature: 'Натисніть, щоб додати підпис',
  newUserSignupWorkflow: 'Процес реєстрації нових користувачів',
  whiteListDomains: 'Домени білого списку',
  whiteListDomainsTooltip:
    'Коли домен додано до білого списку, він дозволяє всім користувачам з підтвердженими електронними адресами цих доменів приєднатися до вашого проекту StruxHub без спеціального запрошення.',
  whiteListDomainsTooltipNote:
    'ПРИМІТКА: Не додавайте до білого списку загальні домени електронної пошти, такі як @gmail.com, @outlook.com, @iCloud.com. Це дозволить неперевіреним користувачам отримати доступ до вашого проекту.',
  usersCanCreateAccountByLink:
    'Користувачі можуть створити обліковий запис за цим посиланням:',
  orSnappingThisQRCode: 'Або відсканувавши цей QR-код:',
  print: 'Друк',
  noPermissionToCreateWorkflowType:
    'Вибачте! У вас немає необхідних дозволів для створення цього типу робочого процесу',
  approvalConfiguration: 'Конфігурація затвердження',
  sendBicApprovalTo: 'Надіслати затвердження BIC до',
  bicApprovalTooltip:
    'Ці користувачі отримують повідомлення про затвердження ball-in-court, коли застосовується це правило.',
  allProjectCoordinators: 'Усі координатори проєкту',
  selectApprovers: 'Вибрати затверджувачів',
  addFilter: 'Додати фільтр',
  onlyWhen: 'Тільки коли:',
  selectCompanyType: 'Вибрати тип компанії',
  deleteRule: 'Видалити правило',
  deleteView: 'Видалити перегляд',
  deleteResource: 'Видалити ресурс',
  deleteTag: 'Видалити тег',
  deleteItem: 'Видалити елемент',
  deleteField: 'Видалити поле',
  deleteFieldQuestion: 'Ви впевнені, що хочете видалити поле?',
  deleteViewQuestion: (name: string) =>
    `Ви впевнені, що хочете видалити перегляд ${name}?`,
  removeUser: 'Видалити користувача',
  xAllowedFormsHaveBeenDeleted: (x: number) =>
    `${x} дозволен${ending(x, 'у', 'і', 'их')} форм${ending(
      x,
      'у',
      'и',
      '',
    )} ${ending(x, 'видалено', 'видалено', 'видалено')}`,
  confirmResourceControlChangesTitle: 'Оновити елементи керування ресурсом?',
  resourceControlChangeAffectsWorkflows: (count: number) =>
    `Цей ресурс досі використовується в типах робочих процесів у кількості: ${count}. Це вилучить його з кожного:`,
  cascadeConditionalFieldsRemoved: (count: number) =>
    `Видалено умовних полів: ${count}`,
  cascadeConditionalFiltersRemoved: (count: number) =>
    `Видалено умовних фільтрів: ${count}`,
  cascadeNoLongerSelectableInFields: (count: number) =>
    `Більше не можна вибрати в полях у кількості: ${count}`,
  cascadeNoLongerSelectableInConditionalFilters: (count: number) =>
    `Більше не можна вибрати в умовних фільтрах у кількості: ${count}`,
  cascadeNoLongerSelectableInConditionalFields: (count: number) =>
    `Більше не можна вибрати в умовних полях у кількості: ${count}`,
  cascadeRemovedFromExclusion: (count: number) =>
    `Вилучено зі списків виключень у кількості: ${count}`,
  cascadeOtherReferencesRemoved: 'Інші посилання видалено',
  couldNotCheckAffectedWorkflows:
    'Не вдалося перевірити, які робочі процеси буде порушено. Спробуйте ще раз.',
  someResourcesCouldNotBeDisabled: (count: number) =>
    `Не вдалося вимкнути ресурси: ${count}. Спробуйте ще раз.`,
  someResourcesCouldNotBeUpdated: (count: number) =>
    `Не вдалося оновити ресурси: ${count}. Спробуйте ще раз.`,
  projectLocationDeletionProhibited: 'Видалення Project Location заборонено',
  projectLocationControlEditProhibited: (controlName: string) =>
    `Редагування «${controlName}» для Project Location заборонено`,
  projectLocationBulkEditProhibited:
    'Масове редагування Project Location заборонено',
  selectionHasResourcesYouCannotChange:
    'Ресурси можуть редагувати або видаляти лише адміністратори та їхні власники (відповідальний користувач або учасники відповідальної компанії)',
  outsideHours: 'Поза графіком',
  closedStatus: 'Закрито',
  tooLong: 'Надто довго',
  fullyBooked: 'Немає місць',
  reserved: 'Зарезервовано',
  unavailable: 'Недоступно',
  closedOnWeekdays: (weekday: string) => `Вихідний: ${weekday}`,
  closedOnX: (day: string) => `Вихідний: ${day}`,
  onlyOpenXToY: (opens: string, closes: string) =>
    `Працює лише ${opens} – ${closes}`,
  opensAtX: (time: string) => `Працює з ${time}`,
  closesAtX: (time: string) => `Працює до ${time}`,
  closesAtXReopensAtY: (closes: string, reopens: string) =>
    `Працює до ${closes}, знову з ${reopens}`,
  hasBreakDuringThisBooking: 'Має перерву під час цього бронювання',
  xToYEveryDay: (start: string, end: string) => `${start} – ${end} щодня`,
  allDayX: (day: string) => `Увесь день ${day}`,
  xMinutesDuration: (x: number) => `${x} хвилин${ending(x, 'а', 'и', '')}`,
  xHoursDuration: (x: number) => `${x} годин${ending(x, 'а', 'и', '')}`,
  xDaysDuration: (x: number) => `${x} ${ending(x, 'день', 'дні', 'днів')}`,
  xWeeksDuration: (x: number) =>
    `${x} ${ending(x, 'тиждень', 'тижні', 'тижнів')}`,
  xMonthsDuration: (x: number) =>
    `${x} ${ending(x, 'місяць', 'місяці', 'місяців')}`,
  limitIsX: (limit: string) => `Ліміт: ${limit}`,
  limitIsOneWorkingDay: 'Ліміт: один робочий день',
  limitIsOneWorkWeek: 'Ліміт: один робочий тиждень',
  thisBookingIsX: (length: string) => `Тривалість бронювання: ${length}.`,
  listWithXMore: (items: string[], moreCount: number) =>
    moreCount ? `${items.join(', ')} і ще ${moreCount}` : items.join(' і '),
  aBooking: 'бронювання',
  takenByX: (bookings: string) => `Зайнято: ${bookings}`,
  takenByXAtY: (booking: string, time: string) =>
    `Зайнято: ${booking}, ${time}`,
  bookingWithTime: (booking: string, time: string) => `${booking}: ${time}`,
  noRoomAtThisTime: 'Цей час уже зайнятий',
  takesXBookingsAtATime: (x: number) =>
    `Приймає ${x} бронюван${ending(x, 'ня', 'ня', 'ь')} одночасно.`,
  fullyBookedOnAllXRepeats: (total: number) =>
    `Повністю заброньовано в усіх ${total} повтореннях.`,
  fullyBookedOnXOfYRepeats: (failing: number, total: number, first: string) =>
    `Повністю заброньовано в ${failing} з ${total} повторень, перше — ${first}.`,
  forX: (companies: string) => `Для ${companies}`,
  forOtherCompanies: 'Для інших компаній',
  cantBeBooked: 'Не можна забронювати',
  affectsAllXRepeats: (total: number) => `Стосується всіх ${total} повторень.`,
  affectsXOfYRepeats: (failing: number, total: number, first: string) =>
    `Стосується ${failing} з ${total} повторень, перше — ${first}.`,
  projectHoursHeading: 'Робочі години проєкту:',
  usesProjectHoursHeading: 'Використовує робочі години проєкту:',
  hoursHeading: 'Години роботи:',
  noneSet: 'не задано',
  notRequestedXAhead: (deadline: string) =>
    `Не запитано за ${deadline} наперед`,
  notRequestedInAdvance: 'Не запитано заздалегідь',
  outsideWorkingHours: 'Поза робочими годинами',
  youCanStillApprove:
    'Ви все одно можете підтвердити. Переконайтеся, що майданчик до цього готовий.',
  youCanStillSubmit:
    'Ви все одно можете надіслати. Затверджувач побачить ці примітки.',
  approveAnyway: 'Підтвердити все одно',
  submitAnyway: 'Надіслати все одно',
  xMore: (x: number) => `Ще ${x}`,
}

export default UkrainianTree
